import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Rows3, Rows4, ChevronDown, ChevronRight, RefreshCw, Filter, Download, Lock, Edit3, TrendingUp, Square, CheckSquare, MinusSquare, Layers, Plus, Minus, Maximize2, Minimize2, FileSpreadsheet, Sliders, Sparkles, Trash2, Check, X, Pin, MoreHorizontal, Grid3x3 } from 'lucide-react';
import '../../aiIdentity.css';
import { toolbarLayoutFor, segmentLabels } from './pnlToolbarLayout';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { useForecastV3, useMonthlyVersions, useExpandedColumns } from '../../context/ForecastV3Context';
import { DriverBadge } from '../shared/DriverBadge';
import { formatCurrency } from '../../utils/formatters';
// @ts-ignore - JavaScript module without types
import { currencySymbol } from '../../../../utils/currency';
import type { ForecastAccount, MonthVersionInfo, MonthStatus, MonthlyExpandedValues } from '../../types';
import { MONTH_LABELS, MONTHS } from '../../types';
import { CellContextMenu } from './CellContextMenu';
import { DriverSubRows, buildDriverSubRows, isDriverExpandable } from './DriverOverrideRows';
import type { DriverInputOverrideItem } from '../../types';
import { DailyEntryModal } from './DailyEntryModal';
import { DepartmentSpreadModal } from './DepartmentSpreadModal';
import { readSavedSpreadBasis, resolveSpreadBasis, spreadAllocations } from '../../utils/spreadBasis';
import { AccountSearchBar } from './AccountSearchBar';
import { RibbonPortal, useHasRibbonSlot } from '../layout/RibbonSlot';
import { flashRow } from '../../utils/flashRow';
import { clearDailyValues, saveOverrideV3, saveBulkOverridesV3, getLinkedStatAccount, getStatAccountExport, getExportFormats } from '../../api';
import type { ExportFormatsResponse } from '../../api';
import { buildForecastRawExport, rawExportToCsv } from '../../utils/forecastRawExport';
import { downloadBudgetPackXlsx } from '../../utils/budgetPackExport';
import type { BuildBudgetPackInput } from '../../utils/budgetPackExport';
import { buildFormattedExportRows, resolveExportFormat } from '../../utils/exportFormats';
import type { ExportFormatTarget } from '../../utils/exportFormats';
import { ExportFormatsModal } from './ExportFormatsModal';
import { buildStatProjected } from '../../utils/otherStatsGrid';
import { useStatAccountPlan, StatAccountBreakdownRows } from './OccupiedRoomsBreakdown';
import { OtherStatsSection } from './OtherStatsSection';
import type { StatAccountMapping } from '../../api';
import { useGridSelection, scrollCellIntoView } from '../../hooks/useGridSelection';
import { useGridDensity } from '../../hooks/useGridDensity';
import type { CellRange } from '../../hooks/useGridSelection';
import { useExpandedColumnConfig } from '../../hooks/useExpandedColumnConfig';
import { ExpandedCellsRenderer, expandedCellText } from './ExpandedCellsRenderer';
import { GridDisplayContext, useGridDisplay } from './gridDisplayContext';
import { ExpandedColumnSettings } from './ExpandedColumnSettings';
import { getNetIncomeValue, getNetIncomeColorMode, buildTotalContext, sumExpandedYear, TOTAL_COLUMN_INDEX } from './columnRegistry';
import type { ExpandedColumnDef, ColumnRenderContext } from './columnRegistry';
// @ts-ignore - JavaScript module without types
import { getChartOfAccountDepartments, getChartOfAccounts, getCoaRevenueBasisMappings } from '../../../../utils/api';
import { classifyDepartmentSections, buildGopLayout, sumStatementLine, type GridLayout, type DepartmentMetaRow, type RevenueBasisLinkRow } from '../../utils/usaliLayout';
// @ts-ignore - JavaScript module without types
import { useTheme } from '../../../../context/ThemeContext';

// Sweep animation for toolbar recalc indicator + origin-cell outline
const recalcStyles = `
@keyframes recalc-sweep {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(300%); }
}
@keyframes marching-ants {
  to { stroke-dashoffset: -8; }
}
.grid-selecting * { cursor: crosshair !important; user-select: none !important; }
.grid-copy-indicator {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 10;
}
.grid-copy-indicator svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.grid-copy-indicator rect {
  fill: none;
  stroke: var(--lodge-orange-500);
  stroke-width: 2;
  stroke-dasharray: 4 4;
  animation: marching-ants 0.4s linear infinite;
}
`;

// Inject recalc styles once
if (typeof document !== 'undefined') {
  const styleId = 'recalc-cascade-styles';
  if (!document.getElementById(styleId)) {
    const styleEl = document.createElement('style');
    styleEl.id = styleId;
    styleEl.textContent = recalcStyles;
    document.head.appendChild(styleEl);
  }
}

// New look drops the currency symbol from grid cells (the header carries it
// once), which buys every month column its width back. Set by PnLGrid on
// each render; exports and pills keep formatCurrency.
let hideCellSymbol = false;
function formatCell(value: number, showParens: boolean, currency?: string | null): string {
  const text = formatCurrency(value, showParens, currency);
  if (!hideCellSymbol) return text;
  const symbol = String(currencySymbol(currency) || '');
  return symbol ? text.replace(symbol, '') : text;
}

// Value wrapper that participates in the CSS recalc cascade
function RecalculatingValue({ value, isRecalculating: _, className = '' }: { value: string; isRecalculating?: boolean; className?: string }) {
  return (
    <span className={`recalc-value ${className}`}>
      {value}
    </span>
  );
}

/** Marching ants SVG overlay — renders dashed animated border on edges of the copied cell range */
function CopyIndicator({ edges }: { edges: { top: boolean; bottom: boolean; left: boolean; right: boolean } }) {
  return (
    <div className="grid-copy-indicator">
      <svg preserveAspectRatio="none">
        {edges.top && <line x1="0" y1="1" x2="100%" y2="1" stroke="var(--lodge-orange-500)" strokeWidth="2" strokeDasharray="4 4" style={{ animation: 'marching-ants 0.4s linear infinite' }} />}
        {edges.bottom && <line x1="0" y1="100%" x2="100%" y2="100%" stroke="var(--lodge-orange-500)" strokeWidth="2" strokeDasharray="4 4" style={{ animation: 'marching-ants 0.4s linear infinite' }} />}
        {edges.left && <line x1="1" y1="0" x2="1" y2="100%" stroke="var(--lodge-orange-500)" strokeWidth="2" strokeDasharray="4 4" style={{ animation: 'marching-ants 0.4s linear infinite' }} />}
        {edges.right && <line x1="100%" y1="0" x2="100%" y2="100%" stroke="var(--lodge-orange-500)" strokeWidth="2" strokeDasharray="4 4" style={{ animation: 'marching-ants 0.4s linear infinite' }} />}
      </svg>
    </div>
  );
}

interface PnLGridProps {
  onSelectAccount: (account: ForecastAccount | null) => void;
  selectedAccount: ForecastAccount | null;
  revenueAccounts: ForecastAccount[];
  expenseAccounts: ForecastAccount[];
  totals: {
    revenue: Record<string, number>;
    expense: Record<string, number>;
    netIncome: Record<string, number>;
  };
  roomStats?: {
    availableRooms: Record<string, number>;
    occupiedRooms: Record<string, number>;
    occupancyRate: Record<string, number>;
    expanded?: Record<string, {
      forecast: { availableRooms: number; occupiedRooms: number; occupancyRate: number };
      actual: { availableRooms: number; occupiedRooms: number; occupancyRate: number };
      budget: { availableRooms: number; occupiedRooms: number; occupancyRate: number };
      lyBudget?: { availableRooms: number; occupiedRooms: number; occupancyRate: number };
      projected?: { availableRooms: number; occupiedRooms: number; occupiedRoomsSource?: string };
      overrides?: {
        availableRooms: number | null;
        occupiedRooms: number | null;
        hasAvailableOverride: boolean;
        hasOccupiedOverride: boolean;
      };
    }>;
  };
  actualRollInEnabled?: boolean;
  onActualRollInChange?: (enabled: boolean) => void;
  // Budget versions show pure budget values — hides the Actuals + Forecast toggle
  isBudget?: boolean;
  closedMonthKeys?: string[];
  roomStatSource?: 'pms' | 'budget' | 'actuals';
  onRoomStatSourceChange?: (source: 'pms' | 'budget' | 'actuals') => void;
  onRoomStatOverride?: (month: number, roomStatType: 'availableRooms' | 'occupiedRooms', value: number | null) => Promise<void>;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  refreshSuccess?: boolean;
  onCellSave?: (chartOfAccountId: number, month: number, value: number) => Promise<void>;
  onBulkPaste?: (entries: { chartOfAccountId: number; month: number; value: number }[]) => Promise<void>;
  isReadOnly?: boolean;
  monthVersions?: MonthVersionInfo[];
  onMonthClick?: (month: number) => void;
  // Bulk selection props
  isBulkMode?: boolean;
  selectedAccountIds?: number[];
  onToggleAccountSelection?: (chartOfAccountId: number) => void;
  onSelectAccountsByType?: (type: 'Revenue' | 'Expense') => void;
  onSelectAccountsByDepartment?: (accountIds: number[]) => void;
  onToggleBulkMode?: () => void;
  // Build with AI - opens the AI build review takeover (only rendered when provided)
  onAiBuild?: () => void;
  // Analyze with AI - runs the forecast accuracy-review workflow in the assistant
  onAiAnalyze?: () => void;
  // Build/repair every driver through the assistant's deterministic builder
  onAiBuildDrivers?: () => void;
  // Check the rooms forecast against on-the-books and the RMS projection
  onAiCompareOtb?: () => void;
  // Read-only audit of a completed budget version (budget_review.v1)
  onAiReviewBudget?: () => void;
  // Which AI workflow stage the toolbar shows: 'build' on a fresh forecast,
  // 'analyze' once drivers exist, 'none' while loading
  aiWorkflowMode?: 'build' | 'analyze' | 'none';
  // Recalculation state (for showing shimmer on selected account)
  isRecalculating?: boolean;
  // Daily entry props
  versionId?: number;
  locationId?: number;
  year?: number;
  currency?: string | null;
  locationName?: string;
  versionName?: string;
  /** After room nights are applied from the ORN drill-down — refetch the grid. */
  onStatInputsApplied?: () => void | Promise<void>;
  /** "unlinked" in the ORN/ADR drill-down — open the stat-account mapping. */
  onOpenStatMapping?: (dept?: { department: string; departmentId?: number }) => void;
  // Department driver props - supports both ID-based (new) and name-based (legacy) lookups
  departmentDrivers?: Record<string, string>;  // { departmentName: driverType } OR { departmentId: driverType }
  onEditDepartment?: (department: { departmentId?: number; departmentName: string }, parentSection: 'Revenue' | 'Expense') => void;
  // External daily entry control - allows opening the modal from outside PnLGrid
  dailyEntryRequest?: { account: ForecastAccount; month: number } | null;
  onDailyEntryRequestHandled?: () => void;
  // GL detail drill-down
  onGLDetailClick?: (chartOfAccountId: number, accountName: string, accountNumber: string, month: number, columnType: 'Actual' | 'LY') => void;
  // Revenue forecast setup - opens modal for a revenue department
  onRevenueForecastSetup?: (dept: { departmentName: string; departmentId?: number }) => void;
  // Scroll-to: set to an account ID to expand its dept and snap the grid to that row
  scrollToAccountId?: number | null;
  onScrollToAccountHandled?: () => void;
  // Driver-level monthly overrides (rate / layer value / position wage; driver stays live)
  /** Opens the Labor Config position-driver modal for an account (from driver sub-rows) */
  onOpenLaborConfig?: (account: ForecastAccount) => void;
  onDriverInputOverrideSave?: (items: DriverInputOverrideItem[]) => Promise<{ applied: number; skippedLocked: { chartOfAccountId: number; year: number; month: number }[] }>;
  onClearDriverInputOverrides?: (chartOfAccountId?: number) => Promise<{ cleared: number }>;
}

// Context menu cell data
interface ContextMenuCellData {
  account: ForecastAccount;
  monthIndex: number; // 0-based
  hasOverride: boolean;
  driverOverrideCount?: number; // driver-level (rate) overrides on this month
  hasDailyValues: boolean;
  dailyMode?: 'blended' | 'legacy' | null; // blended = pinned days + live driver
  pinnedCount?: number;
  isRevenueAccount?: boolean;
}

// Month status indicator for column headers
function MonthStatusIndicator({ status, small = false }: { status: MonthStatus; small?: boolean }) {
  const size = small ? 10 : 12;
  switch (status) {
    case 'locked':
      return <Lock size={size} className="text-slate-400" />;
    case 'draft':
      return <Edit3 size={size} style={{ color: 'var(--lodge-orange-400)' }} />;
    case 'projected':
      return <TrendingUp size={size} className="text-blue-400" />;
    default:
      return null;
  }
}

// Get status label for tooltip
function getStatusLabel(status: MonthStatus): string {
  switch (status) {
    case 'locked':
      return 'Locked - Read only';
    case 'draft':
      return 'Draft - Editable';
    case 'projected':
      return 'Projected - Click to create version';
    case 'none':
      return 'No version - Click to create';
    default:
      return '';
  }
}

// ============ Editable Cell Component ============

interface EditableCellProps {
  /** Inside an expanded month group: takes the group's banded ground. */
  banded?: boolean;
  value: number;
  chartOfAccountId: number;
  monthIndex: number;
  onSave: (chartOfAccountId: number, month: number, value: number) => Promise<void>;
  isNegativeRed?: boolean;
  onNavigate?: (direction: 'up' | 'down' | 'left' | 'right') => void;
  cellRef?: React.RefObject<HTMLTableCellElement | null>;
  hasOverride?: boolean;
  hasDailyValues?: boolean;
  dailyMode?: 'blended' | 'legacy' | null; // blended = pinned days + live driver
  pinnedCount?: number;
  isRecalculating?: boolean;
  onContextMenu?: (e: React.MouseEvent) => void;
  // Grid selection props
  selectionStyle?: React.CSSProperties;
  onGridMouseDown?: (e: React.MouseEvent) => void;
  onGridMouseEnter?: () => void;
  onGridKeyDown?: (e: React.KeyboardEvent) => void;
  copyIndicatorEdges?: { top: boolean; bottom: boolean; left: boolean; right: boolean } | null;
  currency?: string | null;
}

function EditableCell({ value, chartOfAccountId, monthIndex, onSave, isNegativeRed = true, onNavigate, cellRef, hasOverride = false, hasDailyValues = false, dailyMode = null, pinnedCount = 0, isRecalculating = false, onContextMenu, selectionStyle, onGridMouseDown, onGridMouseEnter, onGridKeyDown, copyIndicatorEdges, currency, banded = false }: EditableCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  // Local optimistic value — shows the entered number instantly while the save runs in background
  const [optimisticValue, setOptimisticValue] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const internalCellRef = useRef<HTMLTableCellElement>(null);
  const tdRef = cellRef || internalCellRef;
  // 'key' = typed digit (cursor at end), 'enter' = Enter/F2 (select all)
  const editStartModeRef = useRef<'key' | 'enter'>('enter');
  // Prevents blur from double-committing when keyboard nav already committed
  const hasCommittedRef = useRef(false);

  // Once the real value prop catches up to the optimistic one, clear our local copy
  useEffect(() => {
    if (optimisticValue !== null && value === optimisticValue) {
      setOptimisticValue(null);
    }
  }, [value, optimisticValue]);

  // Focus input when editing starts; reset the commit guard
  useEffect(() => {
    if (isEditing) {
      hasCommittedRef.current = false;
      if (inputRef.current) {
        inputRef.current.focus();
        if (editStartModeRef.current === 'key') {
          const len = inputRef.current.value.length;
          inputRef.current.setSelectionRange(len, len);
        } else {
          inputRef.current.select();
        }
      }
    }
  }, [isEditing]);

  const parseValue = (str: string): number | null => {
    const cleaned = str.replace(/[$,\s]/g, '');
    if (cleaned === '' || cleaned === '-') return null;
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  };

  // Exit edit mode and navigate IMMEDIATELY, then save in the background
  const commitEdit = (direction?: 'up' | 'down' | 'left' | 'right') => {
    if (hasCommittedRef.current) return;
    hasCommittedRef.current = true;

    const newValue = parseValue(editValue);
    setIsEditing(false);

    // Move focus right away — don't wait for the network
    if (direction) {
      onNavigate?.(direction);
    } else {
      tdRef.current?.focus();
    }

    if (newValue === null || newValue === value) return;

    // Show the entered number immediately so it never "disappears"
    setOptimisticValue(newValue);

    // Fire-and-forget; the shimmer on the cell signals background work
    onSave(chartOfAccountId, monthIndex + 1, newValue)
      .catch(() => {
        toast.error('Failed to save — please try again');
        setOptimisticValue(null); // Revert display on error
      });
  };

  // Handle cell focus for keyboard navigation
  const handleCellKeyDown = (e: React.KeyboardEvent) => {
    if (isEditing) return;

    // Delegate to grid selection handler for Ctrl/Shift combos and arrow keys
    if (onGridKeyDown) {
      onGridKeyDown(e);
      if (e.defaultPrevented) return;
    }

    if (e.key === 'Enter' || e.key === 'F2') {
      e.preventDefault();
      editStartModeRef.current = 'enter';
      setEditValue(value.toString());
      setIsEditing(true);
    } else if (/^[\d.\-]$/.test(e.key) && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      editStartModeRef.current = 'key';
      setEditValue(e.key);
      setIsEditing(true);
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      editStartModeRef.current = 'key';
      setEditValue('');
      setIsEditing(true);
    } else if (onNavigate) {
      switch (e.key) {
        case 'ArrowUp':   e.preventDefault(); onNavigate('up');   break;
        case 'ArrowDown': e.preventDefault(); onNavigate('down'); break;
        case 'ArrowLeft': e.preventDefault(); onNavigate('left'); break;
        case 'ArrowRight':
        case 'Tab':
          if (!e.shiftKey) { e.preventDefault(); onNavigate('right'); }
          break;
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':     e.preventDefault(); commitEdit('down'); break;
      case 'ArrowDown': e.preventDefault(); commitEdit('down'); break;
      case 'ArrowUp':   e.preventDefault(); commitEdit('up');   break;
      case 'Tab':       e.preventDefault(); commitEdit(e.shiftKey ? 'left' : 'right'); break;
      case 'Escape':
        hasCommittedRef.current = true; // Prevent blur from saving
        setIsEditing(false);
        tdRef.current?.focus();
        break;
    }
  };

  const handleBlur = () => commitEdit(); // No direction = stay on current cell

  // Display: local optimistic entry > server value (never blank between the two)
  const displayValue = optimisticValue ?? value;
  const showOverrideStyling = hasOverride || optimisticValue !== null;

  if (isEditing) {
    return (
      <td className={`text-right py-1 px-1 ${banded ? 'fv3-xcell' : ''}`} onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className="w-full px-2 py-1 text-right text-sm rounded focus:outline-none tabular-nums"
          style={{ border: '1px solid var(--lodge-orange-400)', outline: 'none' }}
        />
      </td>
    );
  }

  const hasSelection = selectionStyle && Object.keys(selectionStyle).length > 0;
  const hasCopyIndicator = !!copyIndicatorEdges;
  // Blended months keep the live-driver look (no fill) — only a pin glyph marks
  // them; legacy static months keep the blue fill.
  const isBlended = hasDailyValues && dailyMode === 'blended';
  const showLegacyDailyFill = hasDailyValues && !isBlended;

  return (
    <td
      ref={tdRef}
      tabIndex={0}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={handleCellKeyDown}
      onMouseDown={onGridMouseDown}
      onContextMenu={onContextMenu}
      className={`${banded ? 'fv3-xcell ' : ''}text-right py-2.5 px-3 tabular-nums cursor-default transition-colors focus:outline-none relative ${
        isNegativeRed && displayValue < 0 ? 'text-red-600' : 'text-slate-700'
      } ${showOverrideStyling && !hasSelection ? 'bg-amber-50' : ''} ${showLegacyDailyFill && !showOverrideStyling && !hasSelection ? 'bg-blue-50' : ''}`}
      style={hasSelection ? selectionStyle : undefined}
      onMouseEnter={e => { onGridMouseEnter?.(); if (!hasSelection && !showOverrideStyling && !showLegacyDailyFill) (e.currentTarget as HTMLTableCellElement).style.background = 'var(--lodge-orange-50)'; }}
      onMouseLeave={e => { if (!hasSelection) (e.currentTarget as HTMLTableCellElement).style.background = ''; }}
      onFocus={e => { if (!hasSelection && !hasCopyIndicator) (e.currentTarget as HTMLTableCellElement).style.boxShadow = 'inset 0 0 0 2px var(--lodge-orange-400)'; }}
      onBlur={e => { if (!hasSelection && !hasCopyIndicator) (e.currentTarget as HTMLTableCellElement).style.boxShadow = ''; }}
      title={hasOverride
        ? 'Has override — press Enter to edit'
        : isBlended
          ? `${pinnedCount} pinned day${pinnedCount === 1 ? '' : 's'} + live driver — right-click for options`
          : hasDailyValues
            ? 'Daily values — right-click for options'
            : undefined}
    >
      {copyIndicatorEdges && <CopyIndicator edges={copyIndicatorEdges} />}
      {showOverrideStyling && (
        <div className="fv3-mark fv3-mark-override absolute top-0 right-0 w-0 h-0 border-t-[8px] border-t-amber-400 border-l-[8px] border-l-transparent" />
      )}
      {isBlended && !showOverrideStyling && (
        <Pin size={9} className="fv3-mark-pin absolute top-0.5 right-0.5 text-amber-500 fill-amber-400" />
      )}
      {showLegacyDailyFill && !showOverrideStyling && (
        <div className="fv3-mark fv3-mark-daily absolute top-0 right-0 w-0 h-0 border-t-[8px] border-t-blue-400 border-l-[8px] border-l-transparent" />
      )}
      <RecalculatingValue
        value={formatCell(displayValue, true, currency)}
        isRecalculating={isRecalculating}
        className="inline-block px-1 rounded"
      />
    </td>
  );
}

// Helper to check if account has any non-zero values
function hasNonZeroValues(account: ForecastAccount): boolean {
  // Check monthlyValues (forecast)
  for (const month of MONTHS) {
    if (Math.abs(account.monthlyValues[month] || 0) > 0.01) return true;
  }
  // Check monthlyExpanded for budget and actual
  if (account.monthlyExpanded) {
    for (const month of MONTHS) {
      const expanded = account.monthlyExpanded[month];
      if (expanded) {
        if (Math.abs(expanded.budget || 0) > 0.01) return true;
        if (Math.abs(expanded.actual || 0) > 0.01) return true;
      }
    }
  }
  return false;
}

// Group accounts by department
interface ExpandedTotals {
  forecast: number;
  budget: number;
  actual: number;
  lyActual: number;
  lyBudget?: number;
  lyBudgetOccupiedRooms?: number;
}

interface DepartmentGroup {
  department: string;
  departmentId?: number;  // FK to chartOfAccountDepartments
  accounts: ForecastAccount[];
  monthlyTotals: Record<string, number>;
  monthlyExpandedTotals: Record<string, ExpandedTotals>;
  total: number;
}

function accountNumberSortValue(accountNumber?: string | null): number {
  const normalized = String(accountNumber || '').replace(/[^0-9.]/g, '');
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

function compareAccountNumbers(a?: string | null, b?: string | null): number {
  const numericDiff = accountNumberSortValue(a) - accountNumberSortValue(b);
  if (numericDiff !== 0) return numericDiff;
  return String(a || '').localeCompare(String(b || ''), undefined, { numeric: true });
}

function normalizeActualForDisplay(actual: number, isExpense: boolean): number {
  return isExpense ? actual : actual * -1;
}

function getActualVsForecastVariance(expanded: ExpandedTotals | MonthlyExpandedValues | undefined, isExpense: boolean): number {
  if (!expanded) return 0;
  const actual = normalizeActualForDisplay(expanded.actual || 0, isExpense);
  const forecast = expanded.forecast || 0;
  return isExpense ? forecast - actual : actual - forecast;
}

function formatInlineVariance(variance: number, currency?: string | null): string {
  const abs = Math.abs(variance);
  const formatted = formatCell(abs, true, currency);
  return `${variance >= 0 ? '+' : '-'}${formatted} vs fcst`;
}

function getEffectiveDriver(account: ForecastAccount): string | null | undefined {
  return account.inheritsFromDepartment && account.departmentDriverType
    ? account.departmentDriverType
    : account.driverType;
}

function shouldShowOverrideHighlight(
  account: ForecastAccount,
  monthKey: string,
  actualRollInEnabled: boolean
): boolean {
  if (account.monthlyOverrides?.[monthKey] !== true) return false;
  if (account.monthlyExpanded?.[monthKey]?.isActualRolledIn) return false;

  // In Forecast only, show the original forecast override markers exactly as
  // forecast editing created them. In Actuals + Forecast, manual/static rows
  // still display the entered forecast as the base value instead of an override.
  if (!actualRollInEnabled) return true;

  const effectiveDriver = getEffectiveDriver(account);
  return effectiveDriver !== 'Manual' && effectiveDriver !== 'Static Entry';
}

const normalizeName = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();

const GRID_LAYOUT_KEY = 'fv3:gridLayout';
const GRID_LINES_KEY = 'fv3:gridLines';

/** Drag-set department presentation order (P&L Mapping tab), indexed by
 *  normalized name and by chartOfAccountDepartments id. Ordered departments
 *  lead; unordered ones keep the account-number fallback after them. */
export interface DeptOrderIndex {
  byName: Map<string, number>;
  byId: Map<number, number>;
}

function groupByDepartment(accounts: ForecastAccount[], deptOrder?: DeptOrderIndex): DepartmentGroup[] {
  // Group by normalized department NAME so two different departmentIds that
  // share the same name (within the same accountType — the caller splits
  // Revenue vs Expense) roll up into a single row. Normalization is
  // case-insensitive and whitespace-tolerant because data ingestion
  // sometimes yields "Administrative and General" vs "Administrative And
  // General " that visibly read the same but wouldn't match on ===.
  // Fall back to the id only when the name is missing entirely.
  const groups = new Map<string, ForecastAccount[]>();
  const groupKeyToName = new Map<string, string>();

  for (const account of accounts) {
    const rawName = (account.department ?? '').trim().replace(/\s+/g, ' ');
    const groupKey = rawName
      ? `name:${normalizeName(rawName)}`
      : `id:${account.departmentId ?? 'other'}`;

    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
      groupKeyToName.set(groupKey, rawName || 'Other');
    }
    groups.get(groupKey)!.push(account);
  }

  // Convert to array with totals
  const result: DepartmentGroup[] = [];
  groups.forEach((deptAccounts, groupKey) => {
    const monthlyTotals: Record<string, number> = {};
    const monthlyExpandedTotals: Record<string, ExpandedTotals> = {};
    let total = 0;

    // Initialize expanded totals
    for (const month of MONTHS) {
      monthlyExpandedTotals[month] = { forecast: 0, budget: 0, actual: 0, lyActual: 0, lyBudget: 0, lyBudgetOccupiedRooms: 0 };
    }

    // Calculate department totals
    for (const account of deptAccounts) {
      for (const month of MONTHS) {
        const value = account.monthlyValues[month] || 0;
        monthlyTotals[month] = (monthlyTotals[month] || 0) + value;

        // Add expanded totals if available
        if (account.monthlyExpanded?.[month]) {
          const expanded = account.monthlyExpanded[month];
          monthlyExpandedTotals[month].forecast += expanded.forecast || 0;
          monthlyExpandedTotals[month].budget += expanded.budget || 0;
          monthlyExpandedTotals[month].actual += expanded.actual || 0;
          monthlyExpandedTotals[month].lyActual += expanded.lyActual || 0;
          monthlyExpandedTotals[month].lyBudget! += expanded.lyBudget || 0;
          monthlyExpandedTotals[month].lyBudgetOccupiedRooms = expanded.lyBudgetOccupiedRooms || monthlyExpandedTotals[month].lyBudgetOccupiedRooms;
        } else {
          // Fall back to monthlyValues for forecast
          monthlyExpandedTotals[month].forecast += value;
        }
      }
      total += account.total || 0;
    }

    // Use the first account's departmentId as the representative id for the
    // group. Merged same-name groups may span multiple underlying ids; the
    // driver/edit callbacks still fall back to the department name when the
    // id lookup misses (see line 1707, driverType resolution).
    const firstAccount = deptAccounts[0];
    const departmentId = firstAccount?.departmentId;

    result.push({
      department: groupKeyToName.get(groupKey) || 'Other',
      departmentId,
      accounts: [...deptAccounts].sort((a, b) => compareAccountNumbers(a.accountNumber, b.accountNumber)),
      monthlyTotals,
      monthlyExpandedTotals,
      total,
    });
  });

  // Drag-set departments (P&L Mapping tab) lead in their saved order. The
  // rest sort by the first account number in each group. This keeps the
  // P&L in COA order, so Room Revenue (1030000) stays above Other Rooms
  // Revenue (1032500) even though alphabetical order would invert them.
  const orderOf = (g: DepartmentGroup): number | null => {
    if (!deptOrder) return null;
    const byName = deptOrder.byName.get(normalizeName(g.department));
    if (byName !== undefined) return byName;
    if (g.departmentId != null) {
      const byId = deptOrder.byId.get(g.departmentId);
      if (byId !== undefined) return byId;
    }
    return null;
  };
  result.sort((a, b) => {
    const oa = orderOf(a);
    const ob = orderOf(b);
    if ((oa !== null) !== (ob !== null)) return oa !== null ? -1 : 1;
    if (oa !== null && ob !== null && oa !== ob) return oa - ob;
    const accountDiff = compareAccountNumbers(a.accounts[0]?.accountNumber, b.accounts[0]?.accountNumber);
    if (accountDiff !== 0) return accountDiff;
    return a.department.localeCompare(b.department);
  });

  return result;
}

export function PnLGrid({
  onSelectAccount,
  selectedAccount,
  revenueAccounts,
  expenseAccounts,
  totals,
  roomStats,
  actualRollInEnabled = true,
  onActualRollInChange,
  isBudget = false,
  closedMonthKeys = [],
  roomStatSource = 'pms',
  onRoomStatSourceChange,
  onRoomStatOverride,
  onRefresh,
  onDriverInputOverrideSave,
  onOpenLaborConfig,
  onClearDriverInputOverrides,
  isRefreshing = false,
  refreshSuccess = false,
  onCellSave,
  onBulkPaste,
  isReadOnly = false,
  monthVersions: propMonthVersions,
  onMonthClick,
  isBulkMode = false,
  selectedAccountIds = [],
  onToggleAccountSelection,
  onSelectAccountsByType,
  onSelectAccountsByDepartment,
  onToggleBulkMode,
  onAiBuild,
  onAiAnalyze,
  aiWorkflowMode = 'none',
  onAiBuildDrivers,
  onAiCompareOtb,
  onAiReviewBudget,
  isRecalculating = false,
  versionId,
  locationId,
  year,
  currency = 'USD',
  locationName,
  versionName,
  onStatInputsApplied,
  onOpenStatMapping,
  departmentDrivers = {},
  onEditDepartment,
  dailyEntryRequest,
  onDailyEntryRequestHandled,
  onGLDetailClick,
  onRevenueForecastSetup,
  scrollToAccountId,
  onScrollToAccountHandled,
}: PnLGridProps) {
  const { insetActive } = useTheme() as { insetActive: boolean };
  const [gridDensity, setGridDensity] = useGridDensity();
  hideCellSymbol = insetActive;
  const { state, toggleSection, setFullscreen } = useForecastV3();
  const { expandedSections, isFullscreen, orgId } = state;
  const { monthVersions: contextMonthVersions, activeMonth } = useMonthlyVersions();
  const { isColumnExpanded, toggleColumnExpansion, expandAllColumns, collapseAllColumns } = useExpandedColumns();
  const { activeColumns: pickedColumns, allColumns, visibleKeys, toggleColumn, resetToDefaults, updateKeys } = useExpandedColumnConfig(isBudget);
  // An expanded month keeps ITS OWN cell first — the editable plan value the
  // collapsed column shows — and the picked comparison columns follow it as a
  // banded group. The plan column is therefore implicit: never in the picker,
  // always leading the group.
  const planColumn = useMemo(() => allColumns.find(c => c.isEditable) ?? null, [allColumns]);
  const activeColumns = useMemo(
    () => (planColumn ? [planColumn, ...pickedColumns.filter(c => !c.isEditable)] : pickedColumns),
    [planColumn, pickedColumns]
  );
  const activeColumnCount = activeColumns.length;
  const pickerColumns = useMemo(() => allColumns.filter(c => !c.isEditable), [allColumns]);
  const pickerVisibleKeys = useMemo(() => visibleKeys.filter(k => k !== planColumn?.key), [visibleKeys, planColumn]);

  // Grid lines: on (borders between rows and cells) or off (the design's
  // open sheet). A viewer preference, remembered per browser.
  const [gridLines, setGridLines] = useState<boolean>(() => {
    try { return localStorage.getItem(GRID_LINES_KEY) !== 'off'; } catch { return true; }
  });
  const toggleGridLines = useCallback(() => {
    setGridLines(prev => {
      const next = !prev;
      try { localStorage.setItem(GRID_LINES_KEY, next ? 'on' : 'off'); } catch { /* private mode */ }
      return next;
    });
  }, []);
  // The Total column expands into the same sub-columns as a month, with year sums.
  const isTotalExpanded = isColumnExpanded(TOTAL_COLUMN_INDEX);
  const totalColSpan = isTotalExpanded ? Math.max(1, activeColumnCount) : 1;
  const closedMonthSet = useMemo(() => new Set(closedMonthKeys), [closedMonthKeys]);

  // First-run AI band dismissal — persisted per version so it doesn't nag
  const aiBandKey = `lodgeic_ai_band_dismissed_v${versionId ?? 'none'}`;
  const [aiBandDismissed, setAiBandDismissedState] = useState<boolean>(() => {
    try { return localStorage.getItem(aiBandKey) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { setAiBandDismissedState(localStorage.getItem(aiBandKey) === '1'); } catch { /* noop */ }
  }, [aiBandKey]);
  const setAiBandDismissed = useCallback((dismissed: boolean) => {
    setAiBandDismissedState(dismissed);
    try { localStorage.setItem(aiBandKey, dismissed ? '1' : '0'); } catch { /* noop */ }
  }, [aiBandKey]);

  // Pre-compute occupied rooms per month for POR columns
  const occupiedRoomsPerMonth = useMemo(() => {
    const result: Record<string, { actual: number; forecast: number }> = {};
    MONTHS.forEach(month => {
      const expanded = roomStats?.expanded?.[month];
      result[month] = {
        actual: expanded?.actual?.occupiedRooms ?? roomStats?.occupiedRooms?.[month] ?? 0,
        forecast: expanded?.projected?.occupiedRooms ?? expanded?.forecast?.occupiedRooms ?? roomStats?.occupiedRooms?.[month] ?? 0,
      };
    });
    return result;
  }, [roomStats]);

  // Scroll container ref — used by AccountSearchBar to scroll to a matched row
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Ensures a section is expanded without toggling it off if already open
  const ensureSectionExpanded = useCallback((sectionId: string) => {
    if (!expandedSections[sectionId]) {
      toggleSection(sectionId);
    }
  }, [expandedSections, toggleSection]);

  // When a scrollToAccountId is provided (e.g. from Notes eye-icon navigation),
  // expand the parent section + department and snap the grid to that row.
  useEffect(() => {
    if (!scrollToAccountId) return;

    const isRevenue = revenueAccounts.some(a => a.chartOfAccountId === scrollToAccountId);
    const account = isRevenue
      ? revenueAccounts.find(a => a.chartOfAccountId === scrollToAccountId)
      : expenseAccounts.find(a => a.chartOfAccountId === scrollToAccountId);

    if (!account) {
      onScrollToAccountHandled?.();
      return;
    }

    const section = isRevenue ? 'revenue' : 'expense';
    const deptKey = `dept:${section}:${account.department}`;

    // Force-expand section and department (if not already open). In the GOP
    // layout the department sits inside a statement band instead — open any
    // band that was folded away.
    if (expandedSections[section] === false) toggleSection(section);
    if (!expandedSections[deptKey]) toggleSection(deptKey);
    Object.keys(expandedSections).forEach((k) => {
      if (k.startsWith('gop:') && expandedSections[k] === false) toggleSection(k);
    });

    // Capture page scroll now before anything external moves it
    const savedPageScroll = window.scrollY;

    // The row may not exist yet (rows render after the expand, and on a fresh
    // mount after the data lands), and the detail panel opening reflows the
    // grid — so wait for the row, centre it, then re-centre once it settles.
    let tries = 0;
    let timer: number | undefined;
    const centre = (behavior: ScrollBehavior): boolean => {
      const container = scrollContainerRef.current;
      const rowEl = container?.querySelector(`[data-account-id="${scrollToAccountId}"]`) as HTMLElement | null;
      if (!container || !rowEl) return false;
      const containerRect = container.getBoundingClientRect();
      const rowRect = rowEl.getBoundingClientRect();
      const scrollTarget = container.scrollTop + rowRect.top - containerRect.top - container.clientHeight / 2 + rowEl.clientHeight / 2;
      container.scrollTo({ top: scrollTarget, behavior });
      return true;
    };
    const attempt = () => {
      // Restore page scroll position first (panel mount may have shifted it)
      window.scrollTo({ top: savedPageScroll, behavior: 'instant' as ScrollBehavior });
      if (centre('instant' as ScrollBehavior)) {
        timer = window.setTimeout(() => {
          centre('smooth');
          flashRow(scrollContainerRef.current?.querySelector(`[data-account-id="${scrollToAccountId}"]`));
          onScrollToAccountHandled?.();
        }, 450);
      } else if (++tries < 25) {
        timer = window.setTimeout(attempt, 100);
      } else {
        onScrollToAccountHandled?.();
      }
    };
    timer = window.setTimeout(attempt, 80);
    return () => window.clearTimeout(timer);
  }, [scrollToAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Statement layout: the two-section P&L, or the USALI GOP statement. A
  // viewer preference, remembered per browser like the grid density.
  const [gridLayout, setGridLayout] = useState<GridLayout>(() => {
    try { return localStorage.getItem(GRID_LAYOUT_KEY) === 'gop' ? 'gop' : 'pnl'; } catch { return 'pnl'; }
  });
  const changeGridLayout = useCallback((next: GridLayout) => {
    setGridLayout(next);
    try { localStorage.setItem(GRID_LAYOUT_KEY, next); } catch { /* private mode */ }
  }, []);

  // Filter state
  const [hideZeroAccounts, setHideZeroAccounts] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const hasRibbonSlot = useHasRibbonSlot();
  const [showBudgetPackDialog, setShowBudgetPackDialog] = useState(false);
  const [showAiMenu, setShowAiMenu] = useState(false);
  const [toolbarWidth, setToolbarWidth] = useState(1400);
  const toolbarRef = useRef<HTMLDivElement>(null);
  // Header height feeds the sticky offset of an open department's row, so
  // the department name stays in view under the month header while its
  // accounts scroll — and hands over to the next department's row.
  // Measured through a callback ref: the table mounts after the loading
  // state, so an effect on first render would find no header and leave the
  // offset at its default — a gap the accounts scrolled through.
  const [theadHeight, setTheadHeight] = useState(48);
  const theadObserver = useRef<ResizeObserver | null>(null);
  const theadRef = useCallback((node: HTMLTableSectionElement | null) => {
    theadObserver.current?.disconnect();
    theadObserver.current = null;
    if (!node) return;
    const measure = () => setTheadHeight(Math.round(node.getBoundingClientRect().height));
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      theadObserver.current = new ResizeObserver(measure);
      theadObserver.current.observe(node);
    }
  }, []);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const aiMenuRef = useRef<HTMLDivElement>(null);

  // Close filter dropdown on outside click
  useEffect(() => {
    if (!showFilterDropdown) return;
    const handler = (e: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node)) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFilterDropdown]);

  // Toolbar width drives which labels survive — see pnlToolbarLayout.ts.
  useEffect(() => {
    const node = toolbarRef.current;
    if (!node) return;
    setToolbarWidth(node.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect?.width;
      if (width) setToolbarWidth(width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const toolbar = toolbarLayoutFor(toolbarWidth);
  const segments = segmentLabels(toolbar);

  // Close the AI menu on outside click
  useEffect(() => {
    if (!showAiMenu) return;
    const handler = (e: MouseEvent) => {
      if (aiMenuRef.current && !aiMenuRef.current.contains(e.target as Node)) {
        setShowAiMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAiMenu]);

  // Close export dropdown on outside click
  useEffect(() => {
    if (!showMoreMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMoreMenu]);

  // Context menu state
  // Accounts expanded to their driver sub-rows (driver-level monthly overrides)
  const [expandedDriverAccounts, setExpandedDriverAccounts] = useState<Set<number>>(new Set());
  const toggleDriverExpand = useCallback((chartOfAccountId: number) => {
    setExpandedDriverAccounts(prev => {
      const next = new Set(prev);
      if (next.has(chartOfAccountId)) next.delete(chartOfAccountId); else next.add(chartOfAccountId);
      return next;
    });
  }, []);
  const driverOverrideTotal = useMemo(
    () => [...revenueAccounts, ...expenseAccounts].reduce((n, a) => n + (a.driverInputOverrideCount || 0), 0),
    [revenueAccounts, expenseAccounts]
  );
  const handleDriverOverrideSave = useCallback(async (items: DriverInputOverrideItem[]) => {
    if (!onDriverInputOverrideSave) return;
    try {
      const res = await onDriverInputOverrideSave(items);
      if (res?.skippedLocked?.length) {
        toast(`${res.skippedLocked.length} locked month${res.skippedLocked.length === 1 ? '' : 's'} skipped`, { icon: '🔒' });
      }
    } catch (err) {
      console.error('Failed to save driver override:', err);
      toast.error('Failed to save driver override');
    }
  }, [onDriverInputOverrideSave]);
  const handleClearAllDriverOverrides = useCallback(async () => {
    if (!onClearDriverInputOverrides) return;
    try {
      const res = await onClearDriverInputOverrides();
      toast.success(`${res?.cleared ?? 0} driver override${res?.cleared === 1 ? '' : 's'} cleared`);
    } catch (err) {
      console.error('Failed to clear driver overrides:', err);
      toast.error('Failed to clear driver overrides');
    }
  }, [onClearDriverInputOverrides]);

  const [contextMenuState, setContextMenuState] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
    cellData: ContextMenuCellData | null;
  }>({ isOpen: false, position: { x: 0, y: 0 }, cellData: null });

  // Daily entry modal state
  const [dailyEntryState, setDailyEntryState] = useState<{
    isOpen: boolean;
    account: ForecastAccount | null;
    month: number; // 1-12
    linkedStatAccount: StatAccountMapping | null;
    initialMonthlyRN: number | null;
    initialMonthlyADR: number | null;
  }>({ isOpen: false, account: null, month: 1, linkedStatAccount: null, initialMonthlyRN: null, initialMonthlyADR: null });

  // Track the specific cell that triggered a recalculation — only that cell gets the origin outline
  const [recalculatingCell, setRecalculatingCell] = useState<{ chartOfAccountId: number; monthIndex: number } | null>(null);

  // Clear origin outline once the recalc resolves
  useEffect(() => {
    if (!isRecalculating) setRecalculatingCell(null);
  }, [isRecalculating]);

  // Wrap onCellSave to record which cell triggered the recalc before calling through
  const handleCellSaveTracked = useCallback(async (chartOfAccountId: number, month: number, value: number) => {
    setRecalculatingCell({ chartOfAccountId, monthIndex: month - 1 });
    await onCellSave?.(chartOfAccountId, month, value);
  }, [onCellSave]);

  // Spread a department total across sub-accounts using the saved basis preference
  const handleDeptCellSave = useCallback(
    (dept: DepartmentGroup, isRevenue: boolean) =>
      async (monthIndex: number, totalAmount: number) => {
        if (!versionId || !year) return;

        // If no saved basis preference, open the modal so the user can pick one
        const savedBasis = readSavedSpreadBasis(versionId);
        if (!savedBasis) {
          setDeptSpreadRequest({ dept, monthIndex, isRevenue, initialAmount: totalAmount });
          return;
        }

        const month = monthIndex + 1;

        // Same basis resolution as the modal: the saved basis when it has data
        // for this month, else the first basis (in mode preference order) that does.
        const resolved = resolveSpreadBasis(dept.accounts, month, savedBasis, isBudget);
        if (!resolved) {
          // Nothing to spread on — let the user see the empty preview and decide
          setDeptSpreadRequest({ dept, monthIndex, isRevenue, initialAmount: totalAmount });
          return;
        }

        const allocations = spreadAllocations(dept.accounts, resolved.basis, month, totalAmount)
          .map(r => ({ chartOfAccountId: r.account.chartOfAccountId, amount: r.allocated }));

        if (onBulkPaste) {
          // Use onBulkPaste for optimistic updates — values appear immediately
          await onBulkPaste(allocations.map(({ chartOfAccountId, amount }) => ({
            chartOfAccountId, month, value: amount
          })));
        } else {
          // Fallback: direct API call without optimistic updates
          await saveBulkOverridesV3(versionId, allocations.map(({ chartOfAccountId, amount }) => ({
            chartOfAccountId, year, month, overrideAmount: amount
          })));
          onRefresh?.();
        }
      },
    [versionId, year, isBudget, onRefresh, onBulkPaste]
  );

  // Department monthly spread state
  const [deptSpreadRequest, setDeptSpreadRequest] = useState<{
    dept: DepartmentGroup;
    monthIndex: number;
    isRevenue: boolean;
    initialAmount?: number;
  } | null>(null);

  // Department clear overrides state
  const [deptClearRequest, setDeptClearRequest] = useState<{
    dept: DepartmentGroup;
    monthIndex: number;
  } | null>(null);
  const [isClearingOverrides, setIsClearingOverrides] = useState(false);

  const handleClearDeptOverrides = async () => {
    if (!deptClearRequest || !versionId || !year) return;
    const { dept, monthIndex } = deptClearRequest;
    const month = monthIndex + 1;
    const accountsWithOverrides = dept.accounts.filter(
      a => a.monthlyOverrides?.[MONTHS[monthIndex]] === true
    );
    if (accountsWithOverrides.length === 0) {
      toast('No overrides to clear for this month.');
      setDeptClearRequest(null);
      return;
    }
    setIsClearingOverrides(true);
    try {
      await saveBulkOverridesV3(versionId, accountsWithOverrides.map(a => ({
        chartOfAccountId: a.chartOfAccountId,
        year,
        month,
        overrideAmount: null,
      })));
      toast.success(`Cleared overrides for ${accountsWithOverrides.length} account${accountsWithOverrides.length !== 1 ? 's' : ''}`);
      setDeptClearRequest(null);
      onRefresh?.();
    } catch (err) {
      console.error('Error clearing department overrides:', err);
      toast.error('Failed to clear overrides. Please try again.');
    } finally {
      setIsClearingOverrides(false);
    }
  };

  // Handle external daily entry request (e.g., from AccountDetailPanel)
  React.useEffect(() => {
    if (dailyEntryRequest && dailyEntryRequest.account && dailyEntryRequest.month) {
      setDailyEntryState({
        isOpen: true,
        account: dailyEntryRequest.account,
        month: dailyEntryRequest.month,
        linkedStatAccount: null,
        initialMonthlyRN: null,
        initialMonthlyADR: null,
      });
      // Notify parent that request was handled
      onDailyEntryRequestHandled?.();
    }
  }, [dailyEntryRequest, onDailyEntryRequestHandled]);

  // Use prop monthVersions if provided, otherwise from context
  const monthVersions = propMonthVersions || contextMonthVersions;

  // Get status for a specific month (1-indexed)
  const getMonthStatus = (monthIndex: number): MonthStatus => {
    const monthNum = monthIndex + 1; // Convert 0-based to 1-based
    return monthVersions.find(mv => mv.month === monthNum)?.status || 'none';
  };

  // Check if a month is editable (draft and projected months are editable)
  const isMonthEditable = (monthIndex: number): boolean => {
    const status = getMonthStatus(monthIndex);
    const isRolledActual = actualRollInEnabled && closedMonthSet.has(MONTHS[monthIndex]);
    return (status === 'draft' || status === 'projected') && !isReadOnly && !isRolledActual;
  };

  // Cell refs for keyboard navigation
  const cellRefs = useRef<Map<string, HTMLTableCellElement>>(new Map());

  // Filter accounts with zero values if enabled
  const filteredRevenueAccounts = useMemo(() => {
    if (!hideZeroAccounts) return revenueAccounts;
    return revenueAccounts.filter(hasNonZeroValues);
  }, [revenueAccounts, hideZeroAccounts]);

  const filteredExpenseAccounts = useMemo(() => {
    if (!hideZeroAccounts) return expenseAccounts;
    return expenseAccounts.filter(hasNonZeroValues);
  }, [expenseAccounts, hideZeroAccounts]);

  // Group accounts by department
  // Drag-set department presentation order (P&L Mapping tab) — org-wide.
  // Departments without an order keep the account-number fallback after the
  // ordered ones; no orders at all = today's behavior unchanged.
  const [deptOrder, setDeptOrder] = useState<DeptOrderIndex | undefined>(undefined);
  // Explicit USALI sections from the same fetch — the budget pack's GOP line
  // classifies departments with the financial report's precedence.
  const [deptUsaliSections, setDeptUsaliSections] = useState<Array<{ id: number; name?: string | null; usaliSection?: string | null }>>([]);
  // The full department rows (sections + statement captions) and the % Rev /
  // GOP pairing links feed the GOP layout's classifier — the same inputs the
  // Financial report's GOP view reads.
  const [deptMeta, setDeptMeta] = useState<DepartmentMetaRow[]>([]);
  const [revenueBasisLinks, setRevenueBasisLinks] = useState<RevenueBasisLinkRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (!orgId) { setRevenueBasisLinks([]); return undefined; }
    getCoaRevenueBasisMappings(orgId)
      .then((links: RevenueBasisLinkRow[]) => { if (!cancelled) setRevenueBasisLinks(links || []); })
      .catch(() => { if (!cancelled) setRevenueBasisLinks([]); });
    return () => { cancelled = true; };
  }, [orgId]);
  useEffect(() => {
    let cancelled = false;
    if (!orgId) { setDeptOrder(undefined); setDeptUsaliSections([]); setDeptMeta([]); return undefined; }
    getChartOfAccountDepartments(orgId, locationId || undefined)
      .then((rows: Array<{ id: number; name?: string | null; usaliSection?: string | null; presentationName?: string | null; displayOrder?: number | null; locationDisplayOrder?: number | null }>) => {
        if (cancelled) return;
        setDeptMeta(rows || []);
        setDeptUsaliSections((rows || []).map(d => ({ id: d.id, name: d.name, usaliSection: d.usaliSection })));
        // A location with ANY override rows uses ITS OWN order; otherwise
        // the org-wide ("All Locations") order applies.
        const hasLocationOrder = (rows || []).some((d) => d.locationDisplayOrder != null);
        const byName = new Map<string, number>();
        const byId = new Map<number, number>();
        (rows || []).forEach((d) => {
          const raw = hasLocationOrder ? d.locationDisplayOrder : d.displayOrder;
          if (raw === null || raw === undefined) return;
          const order = Number(raw);
          if (!Number.isFinite(order)) return;
          byId.set(d.id, order);
          const nameKey = normalizeName(String(d.name || ''));
          if (nameKey && !byName.has(nameKey)) byName.set(nameKey, order);
        });
        setDeptOrder(byName.size || byId.size ? { byName, byId } : undefined);
      })
      .catch(() => { if (!cancelled) setDeptOrder(undefined); });
    return () => { cancelled = true; };
  }, [orgId, locationId]);

  const revenueDepartments = useMemo(() => groupByDepartment(filteredRevenueAccounts, deptOrder), [filteredRevenueAccounts, deptOrder]);
  const expenseDepartments = useMemo(() => groupByDepartment(filteredExpenseAccounts, deptOrder), [filteredExpenseAccounts, deptOrder]);

  // GOP layout: the same department groups dealt into USALI statement bands.
  // Classification runs over the UNFILTERED account list so hiding $0
  // accounts never changes which band a department belongs to.
  const sectionByDept = useMemo(
    () => classifyDepartmentSections({
      accounts: [...revenueAccounts, ...expenseAccounts],
      departments: deptMeta,
      revenueBasisLinks,
      locationId: locationId ?? 0,
    }),
    [revenueAccounts, expenseAccounts, deptMeta, revenueBasisLinks, locationId]
  );
  const gopLayout = useMemo(
    () => buildGopLayout(revenueDepartments, expenseDepartments, sectionByDept),
    [revenueDepartments, expenseDepartments, sectionByDept]
  );

  // All accounts in display order for navigation (flattened)
  const allAccounts = useMemo(() => {
    const accounts: ForecastAccount[] = [];
    if (expandedSections.revenue !== false) {
      for (const dept of revenueDepartments) {
        // Departments default to collapsed
        if (expandedSections[`dept:revenue:${dept.department}`] === true) {
          accounts.push(...dept.accounts);
        }
      }
    }
    if (expandedSections.expense !== false) {
      for (const dept of expenseDepartments) {
        // Departments default to collapsed
        if (expandedSections[`dept:expense:${dept.department}`] === true) {
          accounts.push(...dept.accounts);
        }
      }
    }
    return accounts;
  }, [revenueDepartments, expenseDepartments, expandedSections]);

  // Grid selection (copy/paste, range select, Ctrl+Arrow)
  const gridSelection = useGridSelection({
    totalRows: allAccounts.length,
    totalCols: 12,
    isMonthEditable,
    onCellSave: isReadOnly || isBulkMode ? undefined : handleCellSaveTracked,
    onBulkPaste: isReadOnly || isBulkMode ? undefined : onBulkPaste,
    allAccounts,
    cellRefs,
    isReadOnly: !!isReadOnly || !!isBulkMode,
    scrollContainerRef,
  });

  // Handle cell navigation
  const handleCellNavigate = useCallback((rowIndex: number, colIndex: number, direction: 'up' | 'down' | 'left' | 'right') => {
    let newRowIndex = rowIndex;
    let newColIndex = colIndex;

    switch (direction) {
      case 'up':
        newRowIndex = Math.max(0, rowIndex - 1);
        break;
      case 'down':
        newRowIndex = Math.min(allAccounts.length - 1, rowIndex + 1);
        break;
      case 'left':
        if (colIndex > 0) {
          newColIndex = colIndex - 1;
        } else if (rowIndex > 0) {
          newRowIndex = rowIndex - 1;
          newColIndex = 11; // Last month
        }
        break;
      case 'right':
        if (colIndex < 11) {
          newColIndex = colIndex + 1;
        } else if (rowIndex < allAccounts.length - 1) {
          newRowIndex = rowIndex + 1;
          newColIndex = 0; // First month
        }
        break;
    }

    const key = `${newRowIndex}-${newColIndex}`;
    const cell = cellRefs.current.get(key);
    if (cell) {
      cell.focus({ preventScroll: true });
      scrollCellIntoView(cell, scrollContainerRef.current);
    }
  }, [allAccounts.length]);

  // Handle context menu open
  const handleContextMenu = useCallback((
    e: React.MouseEvent,
    account: ForecastAccount,
    monthIndex: number
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const monthKey = MONTH_LABELS[monthIndex].toLowerCase();
    const hasOverride = shouldShowOverrideHighlight(account, monthKey, actualRollInEnabled);
    const hasDailyValues = account.monthlyDailyValues?.[monthKey] === true;
    const dailyMode = hasDailyValues ? (account.monthlyDailyMode?.[monthKey] ?? 'legacy') : null;
    const pinnedCount = account.monthlyPinnedCounts?.[monthKey] ?? 0;
    const driverOverrideCount = account.monthlyDriverInputOverrides?.[monthKey]?.length ?? 0;

    const isRevenueAccount = account.accountType === 'Revenue';

    setContextMenuState({
      isOpen: true,
      position: { x: e.clientX, y: e.clientY },
      cellData: {
        account,
        monthIndex,
        hasOverride,
        hasDailyValues,
        dailyMode,
        pinnedCount,
        isRevenueAccount,
        driverOverrideCount,
      },
    });
  }, [actualRollInEnabled]);

  // Handle context menu close
  const handleContextMenuClose = useCallback(() => {
    setContextMenuState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Handle open daily entry modal
  const handleOpenDailyEntry = useCallback(() => {
    if (!contextMenuState.cellData) return;

    const account = contextMenuState.cellData.account;
    const month = contextMenuState.cellData.monthIndex + 1;

    // Open optimistically; linked stat account lookup runs in background
    setDailyEntryState({
      isOpen: true,
      account,
      month,
      linkedStatAccount: null,
      initialMonthlyRN: null,
      initialMonthlyADR: null,
    });
    handleContextMenuClose();

    // Background lookup: check if this revenue account has a linked stat account
    if (locationId && orgId && account.accountType === 'Revenue') {
      getLinkedStatAccount({ locationId, orgId, revenueChartOfAccountId: account.chartOfAccountId, versionId: versionId ?? undefined, year: year ?? undefined, month })
        .then(({ statAccount, monthlyRN, monthlyADR }) => {
          if (statAccount) {
            setDailyEntryState(prev =>
              prev.isOpen && prev.account?.chartOfAccountId === account.chartOfAccountId
                ? { ...prev, linkedStatAccount: statAccount, initialMonthlyRN: monthlyRN, initialMonthlyADR: monthlyADR }
                : prev
            );
          }
        })
        .catch(() => { /* non-critical — modal works without stat mode */ });
    }
  }, [contextMenuState.cellData, handleContextMenuClose, locationId, orgId, versionId, year]);

  // Handle close daily entry modal
  const handleCloseDailyEntry = useCallback(() => {
    setDailyEntryState({ isOpen: false, account: null, month: 1, linkedStatAccount: null, initialMonthlyRN: null, initialMonthlyADR: null });
  }, []);

  // Handle daily entry save - refresh data
  const handleDailyEntrySave = useCallback(() => {
    onRefresh?.();
  }, [onRefresh]);

  // Handle clear override from context menu
  const handleClearOverride = useCallback(async () => {
    if (!contextMenuState.cellData || !versionId) return;

    const { account, monthIndex } = contextMenuState.cellData;
    handleContextMenuClose();
    try {
      await saveOverrideV3({
        versionId,
        chartOfAccountId: account.chartOfAccountId,
        year: year || new Date().getFullYear(),
        month: monthIndex + 1,
        overrideAmount: null, // null removes the override → back to the driver
      });
      toast.success('Override cleared');
      onRefresh?.();
    } catch (error) {
      console.error('Failed to clear override:', error);
      toast.error('Failed to clear override');
    }
  }, [contextMenuState.cellData, versionId, year, handleContextMenuClose, onRefresh]);

  // Revert every driver-level (rate) override on this account-month
  const handleClearDriverOverridesForMonth = useCallback(async () => {
    if (!contextMenuState.cellData || !versionId || !onDriverInputOverrideSave) return;
    const { account, monthIndex } = contextMenuState.cellData;
    const monthKey = MONTHS[monthIndex];
    const rows = account.monthlyDriverInputOverrides?.[monthKey] || [];
    handleContextMenuClose();
    if (rows.length === 0) return;
    await handleDriverOverrideSave(rows.map(r => ({
      chartOfAccountId: account.chartOfAccountId,
      year: year || new Date().getFullYear(),
      month: monthIndex + 1,
      layerKey: r.layerKey,
      field: r.field,
      value: null,
    })));
  }, [contextMenuState.cellData, versionId, year, onDriverInputOverrideSave, handleDriverOverrideSave, handleContextMenuClose]);

  // Handle clear daily values from context menu
  const handleClearDailyValues = useCallback(async () => {
    if (!contextMenuState.cellData || !versionId) return;

    const { account, monthIndex } = contextMenuState.cellData;

    if (!window.confirm('Clear all daily values for this month? This will revert to driver calculation.')) {
      return;
    }

    try {
      await clearDailyValues({
        versionId,
        chartOfAccountId: account.chartOfAccountId,
        year: year || new Date().getFullYear(),
        month: monthIndex + 1,
      });
      toast.success('Daily values cleared');
      handleContextMenuClose();
      onRefresh?.();
    } catch (error) {
      console.error('Failed to clear daily values:', error);
      toast.error('Failed to clear daily values');
    }
  }, [contextMenuState.cellData, versionId, year, handleContextMenuClose, onRefresh]);

  // Use expanded state from context, defaulting to true
  const isRevenueExpanded = expandedSections.revenue !== false;
  const isExpenseExpanded = expandedSections.expense !== false;

  // Calculate selection states for sections and departments
  const getSelectionState = (accountIds: number[]): 'none' | 'some' | 'all' => {
    if (accountIds.length === 0) return 'none';
    const selectedCount = accountIds.filter(id => selectedAccountIds.includes(id)).length;
    if (selectedCount === 0) return 'none';
    if (selectedCount === accountIds.length) return 'all';
    return 'some';
  };

  const revenueAccountIds = useMemo(() => revenueAccounts.map(a => a.chartOfAccountId), [revenueAccounts]);
  const expenseAccountIds = useMemo(() => expenseAccounts.map(a => a.chartOfAccountId), [expenseAccounts]);

  const revenueSelectionState = getSelectionState(revenueAccountIds);
  const expenseSelectionState = getSelectionState(expenseAccountIds);

  const handleSelectRevenueAccounts = () => {
    if (revenueSelectionState === 'all') {
      // Deselect all revenue accounts
      const remaining = selectedAccountIds.filter(id => !revenueAccountIds.includes(id));
      onSelectAccountsByType?.('Revenue');
    } else {
      // Select all revenue accounts
      onSelectAccountsByType?.('Revenue');
    }
  };

  const handleSelectExpenseAccounts = () => {
    if (expenseSelectionState === 'all') {
      // Deselect all expense accounts
      onSelectAccountsByType?.('Expense');
    } else {
      // Select all expense accounts
      onSelectAccountsByType?.('Expense');
    }
  };

  // Calculate totals from props
  const revenueTotals = useMemo(() => {
    const monthTotals: Record<string, number> = {};
    MONTHS.forEach(month => {
      monthTotals[month] = totals.revenue?.[month] || 0;
    });
    return monthTotals;
  }, [totals.revenue]);

  const expenseTotals = useMemo(() => {
    const monthTotals: Record<string, number> = {};
    MONTHS.forEach(month => {
      monthTotals[month] = totals.expense?.[month] || 0;
    });
    return monthTotals;
  }, [totals.expense]);

  const netIncome = useMemo(() => {
    const monthTotals: Record<string, number> = {};
    MONTHS.forEach(month => {
      monthTotals[month] = totals.netIncome?.[month] || 0;
    });
    return monthTotals;
  }, [totals.netIncome]);

  // Calculate net total
  const netTotal = useMemo(() => {
    return Object.values(netIncome).reduce((sum, val) => sum + (val || 0), 0);
  }, [netIncome]);

  // Calculate section totals
  const revenueSectionTotal = useMemo(() => {
    return Object.values(revenueTotals).reduce((sum, val) => sum + (val || 0), 0);
  }, [revenueTotals]);

  const expenseSectionTotal = useMemo(() => {
    return Object.values(expenseTotals).reduce((sum, val) => sum + (val || 0), 0);
  }, [expenseTotals]);

  // Calculate section expanded totals
  const revenueExpandedTotals = useMemo(() => {
    const result: Record<string, ExpandedTotals> = {};
    for (const month of MONTHS) {
      result[month] = { forecast: 0, budget: 0, actual: 0, lyActual: 0, lyBudget: 0, lyBudgetOccupiedRooms: 0 };
      for (const dept of revenueDepartments) {
        result[month].forecast += dept.monthlyExpandedTotals[month]?.forecast || 0;
        result[month].budget += dept.monthlyExpandedTotals[month]?.budget || 0;
        result[month].actual += dept.monthlyExpandedTotals[month]?.actual || 0;
        result[month].lyActual += dept.monthlyExpandedTotals[month]?.lyActual || 0;
        result[month].lyBudget! += dept.monthlyExpandedTotals[month]?.lyBudget || 0;
        result[month].lyBudgetOccupiedRooms = dept.monthlyExpandedTotals[month]?.lyBudgetOccupiedRooms || result[month].lyBudgetOccupiedRooms;
      }
    }
    return result;
  }, [revenueDepartments]);

  const expenseExpandedTotals = useMemo(() => {
    const result: Record<string, ExpandedTotals> = {};
    for (const month of MONTHS) {
      result[month] = { forecast: 0, budget: 0, actual: 0, lyActual: 0, lyBudget: 0, lyBudgetOccupiedRooms: 0 };
      for (const dept of expenseDepartments) {
        result[month].forecast += dept.monthlyExpandedTotals[month]?.forecast || 0;
        result[month].budget += dept.monthlyExpandedTotals[month]?.budget || 0;
        result[month].actual += dept.monthlyExpandedTotals[month]?.actual || 0;
        result[month].lyActual += dept.monthlyExpandedTotals[month]?.lyActual || 0;
        result[month].lyBudget! += dept.monthlyExpandedTotals[month]?.lyBudget || 0;
        result[month].lyBudgetOccupiedRooms = dept.monthlyExpandedTotals[month]?.lyBudgetOccupiedRooms || result[month].lyBudgetOccupiedRooms;
      }
    }
    return result;
  }, [expenseDepartments]);

  const handleToggleSection = (section: 'revenue' | 'expense') => {
    toggleSection(section);
  };

  // Statement lines for the GOP layout. Each profit line is the income side
  // less the expense side of every band above it, so the last line here is
  // exactly the Net Income of the two-section layout — the same rows,
  // re-dealt. Intermediate lines follow the printed statement: GOP always;
  // Income Before Fixed Charges only under a Management Fees band; EBITDA
  // only when something sits beneath Fixed Charges; Net Income only when any
  // band sits below GOP (otherwise GOP is the bottom line).
  const gopLines = useMemo(() => {
    const income: DepartmentGroup[] = [...gopLayout.revenue];
    const expense: DepartmentGroup[] = [...gopLayout.deptExpense];
    const bandOf = (key: string) => gopLayout.costBands.find((b) => b.key === key);
    const bands: Record<string, ReturnType<typeof sumStatementLine>> = {};
    const absorb = (key: string) => {
      const band = bandOf(key);
      if (!band) return false;
      const bandIncome = band.members.filter((m) => !m.isExpense).map((m) => m.dept);
      const bandExpense = band.members.filter((m) => m.isExpense).map((m) => m.dept);
      bands[key] = sumStatementLine(bandIncome, bandExpense, true);
      income.push(...bandIncome);
      expense.push(...bandExpense);
      return true;
    };
    const running = () => sumStatementLine(income, expense, false);

    const departmentalProfit = running();
    absorb('UND');
    const gop = running();
    const hasMgmt = absorb('MGMT');
    const ibfc = hasMgmt ? running() : null;
    const hasFixed = absorb('FIXED');
    const hasBelow = !!bandOf('BELOW');
    const ebitda = hasFixed && hasBelow ? running() : null;
    absorb('BELOW');
    const net = gopLayout.hasBelowGop ? running() : null;

    return {
      revenue: sumStatementLine(gopLayout.revenue, [], false),
      deptExpense: sumStatementLine([], gopLayout.deptExpense, true),
      departmentalProfit,
      bands,
      gop,
      ibfc,
      ebitda,
      net,
    };
  }, [gopLayout]);

  const gopRowCommon = {
    isBulkMode,
    isColumnExpanded,
    activeColumns,
    occupiedRoomsPerMonth,
    revenuePerMonth: revenueExpandedTotals,
    closedMonthSet: actualRollInEnabled ? closedMonthSet : undefined,
    currency,
  };

  // A cost band of the GOP layout: its heading row (band total, cost-positive)
  // with the member departments beneath. Bands start open like the two
  // sections do, so account search can still land on a row inside one.
  const renderCostBand = (key: string) => {
    const band = gopLayout.costBands.find((b) => b.key === key);
    const line = gopLines.bands[key];
    if (!band || !line) return null;
    const bandKey = `gop:${key}`;
    const isBandExpanded = expandedSections[bandKey] !== false;
    return (
      <React.Fragment key={bandKey}>
        <tbody><GopLineRow
          label={band.label}
          variant="band"
          isExpanded={isBandExpanded}
          onToggle={() => toggleSection(bandKey)}
          line={line}
          isExpense={true}
          {...gopRowCommon}
        /></tbody>
        {isBandExpanded && band.members.map((m) => renderDepartmentBlock(m.dept, m.isExpense))}
      </React.Fragment>
    );
  };

  // One department with its account rows — identical in both layouts; the
  // layout only decides which band the block sits under.
  const renderDepartmentBlock = (dept: DepartmentGroup, isExpense: boolean) => {
    const deptKey = `dept:${isExpense ? 'expense' : 'revenue'}:${dept.department}`;
    // Departments default to collapsed (must explicitly be set to true to expand)
    const isDeptExpanded = expandedSections[deptKey] === true;
    const deptAccountIds = dept.accounts.map(a => a.chartOfAccountId);
    const deptSelectionState = getSelectionState(deptAccountIds);
    const isRoomsRevenue = !isExpense && /room/i.test(dept.department);
    return (
      <tbody key={deptKey} className="fv3-dept">
        <DepartmentRow
          department={dept.department}
          departmentId={dept.departmentId}
          monthlyTotals={dept.monthlyTotals}
          monthlyExpandedTotals={dept.monthlyExpandedTotals}
          total={dept.total}
          isExpanded={isDeptExpanded}
          onToggle={() => toggleSection(deptKey)}
          isBulkMode={isBulkMode}
          selectionState={deptSelectionState}
          onToggleSelection={() => onSelectAccountsByDepartment?.(deptAccountIds)}
          isColumnExpanded={isColumnExpanded}
          isExpense={isExpense}
          driverType={departmentDrivers?.[dept.departmentId?.toString() ?? ''] ?? departmentDrivers?.[dept.department]}
          onEditDriver={() => onEditDepartment?.({ departmentId: dept.departmentId, departmentName: dept.department }, isExpense ? 'Expense' : 'Revenue')}
          isRecalculating={false}
          onMonthlySpread={!isReadOnly ? (monthIndex) => setDeptSpreadRequest({ dept, monthIndex, isRevenue: !isExpense }) : undefined}
          onClearOverrides={!isReadOnly ? (monthIndex) => setDeptClearRequest({ dept, monthIndex }) : undefined}
          childOverrideMonths={new Set(dept.accounts.flatMap(a => Object.entries(a.monthlyOverrides || {}).filter(([, v]) => v).map(([k]) => k)))}
          onCellSave={!isReadOnly ? handleDeptCellSave(dept, isExpense) : undefined}
          isMonthEditable={isMonthEditable}
          activeColumns={activeColumns}
          occupiedRoomsPerMonth={occupiedRoomsPerMonth}
          revenuePerMonth={revenueExpandedTotals}
          currency={currency}
          onRevenueForecastSetup={!isReadOnly && onRevenueForecastSetup && isRoomsRevenue ? () => onRevenueForecastSetup({ departmentName: dept.department, departmentId: dept.departmentId }) : undefined}
          hasRevenueForecast={isRoomsRevenue && dept.accounts.some(a => a.monthlyDailyValues && Object.values(a.monthlyDailyValues).some(v => v))}
        />
        {isDeptExpanded &&
          dept.accounts.map((account) => {
            const rowIndex = allAccounts.findIndex(a => a.id === account.id);
            return (
              <React.Fragment key={account.id}>
              <AccountRow
                account={account}
                isDriverExpandable={!!onDriverInputOverrideSave && isDriverExpandable(account)}
                isDriverExpanded={expandedDriverAccounts.has(account.chartOfAccountId)}
                onToggleDriverExpand={() => toggleDriverExpand(account.chartOfAccountId)}
                isSelected={selectedAccount?.id === account.id}
                onSelect={() => onSelectAccount(account)}
                onCellSave={handleCellSaveTracked}
                rowIndex={rowIndex}
                onCellNavigate={handleCellNavigate}
                cellRefs={cellRefs}
                isReadOnly={isReadOnly}
                isMonthEditable={isMonthEditable}
                activeMonth={activeMonth}
                isBulkMode={isBulkMode}
                isBulkSelected={selectedAccountIds.includes(account.chartOfAccountId)}
                onToggleBulkSelection={() => onToggleAccountSelection?.(account.chartOfAccountId)}
                isColumnExpanded={isColumnExpanded}
                isExpense={isExpense}
                recalculatingCell={recalculatingCell}
                onContextMenu={handleContextMenu}
                onGLDetailClick={onGLDetailClick}
                isCellSelected={gridSelection.isCellSelected}
                getSelectionStyle={gridSelection.getSelectionStyle}
                onCellMouseDown={gridSelection.handleCellMouseDown}
                onCellMouseEnter={gridSelection.handleCellMouseEnter}
                onGridKeyDown={gridSelection.handleKeyDown}
                selectedRange={gridSelection.selectedRange}
                getCopyIndicatorEdges={gridSelection.getCopyIndicatorEdges}
                activeColumns={activeColumns}
                occupiedRoomsPerMonth={occupiedRoomsPerMonth}
                revenuePerMonth={revenueExpandedTotals}
                actualRollInEnabled={actualRollInEnabled}
                currency={currency}
              />
              {onDriverInputOverrideSave && expandedDriverAccounts.has(account.chartOfAccountId) && (
                <DriverSubRows
                  account={account}
                  rows={buildDriverSubRows(account, currency)}
                  year={year || new Date().getFullYear()}
                  currency={currency}
                  isBulkMode={isBulkMode}
                  isReadOnly={isReadOnly}
                  isMonthEditable={isMonthEditable}
                  monthColSpan={(i) => (isColumnExpanded(i) ? Math.max(1, activeColumns.length) : 1)}
                  totalColSpan={totalColSpan}
                  activeMonth={activeMonth}
                  onSave={handleDriverOverrideSave}
                  onOpenPositionConfig={onOpenLaborConfig}
                />
              )}
              </React.Fragment>
            );
          })}
      </tbody>
    );
  };

  // Export to CSV
  const handleExportCSV = () => {
    const rows: string[][] = [];

    // Header row
    rows.push(['Account', 'Type', 'Driver', ...MONTH_LABELS, 'Total']);

    // Revenue section
    rows.push(['Revenue', '', '', ...MONTH_LABELS.map(m => formatCurrency(revenueTotals[m.toLowerCase()] || 0, true, currency).replace(/,/g, '')), formatCurrency(revenueSectionTotal, true, currency).replace(/,/g, '')]);
    revenueAccounts.forEach(account => {
      rows.push([
        account.accountName,
        'Revenue',
        account.driverType,
        ...MONTH_LABELS.map(m => {
          const value = account.monthlyValues[m.toLowerCase() as keyof typeof account.monthlyValues];
          return value?.toString() || '0';
        }),
        account.total?.toString() || '0'
      ]);
    });

    // Expense section
    rows.push(['Expense', '', '', ...MONTH_LABELS.map(m => formatCurrency(expenseTotals[m.toLowerCase()] || 0, true, currency).replace(/,/g, '')), formatCurrency(expenseSectionTotal, true, currency).replace(/,/g, '')]);
    expenseAccounts.forEach(account => {
      rows.push([
        account.accountName,
        'Expense',
        account.driverType,
        ...MONTH_LABELS.map(m => {
          const value = account.monthlyValues[m.toLowerCase() as keyof typeof account.monthlyValues];
          return value?.toString() || '0';
        }),
        account.total?.toString() || '0'
      ]);
    });

    // Net Income row
    rows.push([
      'Net Income',
      '',
      '',
      ...MONTH_LABELS.map(m => (netIncome[m.toLowerCase()] || 0).toString()),
      netTotal.toString()
    ]);

    // Convert to CSV string
    const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `forecast_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);

    toast.success('Exported to CSV');
  };

  // Everything the flat exports and the budget pack read, beyond what the
  // grid already holds: stat-account rows (the Statistics band) and, when
  // asked, the full chart (Export-tab zero-fill + account-level USALI
  // sections for the pack's GOP). Fetch failures degrade, never block.
  const loadExportInput = useCallback(async (opts: { chart: boolean; inactiveStats: boolean; quiet?: boolean }): Promise<BuildBudgetPackInput> => {
    let statAccounts: Awaited<ReturnType<typeof getStatAccountExport>>['accounts'] = [];
    // Account-level USALI sections — the report's GOP classification lives
    // here (auto-classify writes ChartOfAccount.usaliSection; the
    // department default is usually null).
    let accountUsaliSections: Array<{ id: number; usaliSection?: string | null }> = [];
    let chartOfAccounts: Array<{ id: number; accountNumber?: string | null; accountName?: string | null; accountType?: string | null; department?: string | null }> = [];
    if (orgId && locationId) {
      const [statResult, coaResult] = await Promise.allSettled([
        getStatAccountExport({ orgId, locationId, versionId, year: year ?? new Date().getFullYear(), projected: buildStatProjected(roomStats, revenueAccounts), includeInactive: opts.inactiveStats }),
        opts.chart ? getChartOfAccounts(orgId, locationId) : Promise.resolve([]),
      ]);
      if (statResult.status === 'fulfilled') {
        statAccounts = statResult.value.accounts || [];
      } else {
        console.error('[PnLGrid] stat-account export fetch failed', statResult.reason);
        if (!opts.quiet) toast.error('Stat accounts unavailable — exporting without the Statistics band');
      }
      if (coaResult.status === 'fulfilled') {
        const coa = (coaResult.value as Array<{ id: number; usaliSection?: string | null; accountNumber?: string | null; accountName?: string | null; accountType?: string | null; department?: string | null }> || []);
        accountUsaliSections = coa
          .filter(a => a.usaliSection)
          .map(a => ({ id: a.id, usaliSection: a.usaliSection }));
        chartOfAccounts = coa.map(a => ({ id: a.id, accountNumber: a.accountNumber, accountName: a.accountName, accountType: a.accountType, department: a.department }));
      } else {
        console.error('[PnLGrid] chart of accounts fetch failed', coaResult.reason);
      }
    }
    return {
      locationName: locationName || '',
      year: year ?? new Date().getFullYear(),
      actualMonthKeys: new Set<string>(actualRollInEnabled && !isBudget ? MONTHS.filter(m => closedMonthSet.has(m)) : []),
      planLabel: isBudget ? 'Budget' : 'Forecast',
      versionName,
      currency,
      revenueDepartments,
      expenseDepartments,
      revenueTotals,
      expenseTotals,
      netIncome,
      roomStats: roomStats ?? null,
      statAccounts,
      deptUsaliSections,
      accountUsaliSections,
      chartOfAccounts,
    };
  }, [orgId, locationId, versionId, year, roomStats, revenueAccounts, locationName, actualRollInEnabled, isBudget, closedMonthSet, versionName, currency, revenueDepartments, expenseDepartments, revenueTotals, expenseTotals, netIncome, deptUsaliSections]);
  const loadExportPreviewInput = useCallback(() => loadExportInput({ chart: true, inactiveStats: true, quiet: true }), [loadExportInput]);

  // Saved export layouts (org default / this location's override) for the
  // flat exports; none saved → the built-in layouts.
  const [exportFormats, setExportFormats] = useState<ExportFormatsResponse['formats'] | null>(null);
  const [showExportFormats, setShowExportFormats] = useState<ExportFormatTarget | null>(null);
  useEffect(() => {
    if (!orgId) { setExportFormats(null); return undefined; }
    let cancelled = false;
    getExportFormats(orgId, locationId)
      .then(res => { if (!cancelled) setExportFormats(res.formats); })
      .catch(err => { if (!cancelled) { console.warn('[PnLGrid] export formats unavailable', err); setExportFormats(null); } });
    return () => { cancelled = true; };
  }, [orgId, locationId]);
  const rawExportFormat = resolveExportFormat(exportFormats?.raw).format;
  const packExportFormat = resolveExportFormat(exportFormats?.packExport).format;

  const exportSlug = (s: string) => s.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase();

  // Raw data export — the financial report's budget-template layout, fed
  // from what the grid shows: value columns mirror the Actuals + Forecast
  // toggle, and the Statistics band is rebuilt at stat-GL-account grain.
  // A saved raw format replaces the layout (not the numbers).
  const handleExportRaw = async () => {
    const toastId = toast.loading('Preparing raw data export…');
    try {
      const format = rawExportFormat;
      const input = await loadExportInput({ chart: !!format?.chartZeroFill, inactiveStats: !!format?.chartZeroFill });
      const aoa = format ? buildFormattedExportRows(input, format) : buildForecastRawExport(input);
      const parts = ['forecast-raw', exportSlug(locationName || ''), String(year ?? ''), exportSlug(versionName || '')].filter(Boolean);
      const blob = new Blob([rawExportToCsv(aoa)], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${parts.join('-')}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success('Raw data exported', { id: toastId });
    } catch (err) {
      console.error('[PnLGrid] raw export failed', err);
      toast.error('Raw data export failed', { id: toastId });
    }
  };

  // Budget pack export — multi-tab Excel workbook for budget versions: the
  // full P&L (with the Statistics band) on the first tab, then one tab per
  // department with account-by-month detail and every driver's assumptions.
  // The dialog first asks whether to carry the expanded view's comparison
  // columns into the workbook as collapsed column groups.
  const packComparisonDefs = activeColumns.filter(c => c.key !== 'forecast');
  const handleExportBudgetPack = async (includeComparisons: boolean) => {
    setShowBudgetPackDialog(false);
    const toastId = toast.loading('Preparing budget pack…');
    try {
      const input = await loadExportInput({ chart: true, inactiveStats: true });
      const parts = ['budget-pack', exportSlug(locationName || ''), String(year ?? ''), exportSlug(versionName || '')].filter(Boolean);
      await downloadBudgetPackXlsx({
        ...input,
        comparison: includeComparisons && packComparisonDefs.length > 0 ? {
          isBudget,
          columnKeys: packComparisonDefs.map(c => c.key),
          occupiedRoomsPerMonth,
          revenueExpandedTotals,
        } : null,
      }, parts.join('-'), packExportFormat);
      toast.success('Budget pack exported', { id: toastId });
    } catch (err) {
      console.error('[PnLGrid] budget pack export failed', err);
      toast.error('Budget pack export failed', { id: toastId });
    }
  };

  // Export to Excel with expanded view columns
  const handleExportExcel = () => {
    // Helper to get expanded values for an account using column registry
    const getExpandedRow = (account: ForecastAccount, isExpense: boolean) => {
      const row: (string | number)[] = [
        account.accountNumber,
        account.accountName,
        isExpense ? 'Expense' : 'Revenue',
        account.department || '',
        account.driverType || '',
      ];

      let rowTotal = 0;
      MONTH_LABELS.forEach((month, index) => {
        const monthKey = month.toLowerCase();
        const expanded = account.monthlyExpanded?.[monthKey];
        const value = account.monthlyValues[monthKey] || 0;
        if (expanded) {
          const rooms = occupiedRoomsPerMonth[monthKey];
          const rev = revenueExpandedTotals[monthKey];
          const ctx: ColumnRenderContext = { expanded, value, isExpense, monthIndex: index, actualOccupiedRooms: rooms?.actual, forecastOccupiedRooms: rooms?.forecast, actualTotalRevenue: rev?.actual ? rev.actual * -1 : 0, budgetTotalRevenue: rev?.budget || 0, lyBudgetTotalRevenue: rev?.lyBudget || 0, forecastTotalRevenue: rev?.forecast || 0 };
          activeColumns.forEach(col => row.push(col.getValue(ctx)));
          // Find the forecast column value for the total
          const forecastCol = activeColumns.find(c => c.key === 'forecast');
          if (forecastCol) rowTotal += forecastCol.getValue(ctx);
        } else {
          activeColumns.forEach(() => row.push(0));
          rowTotal += value;
        }
      });
      row.push(rowTotal);
      return row;
    };

    // Build header rows
    const headerRow1: (string | number)[] = ['Account #', 'Account Name', 'Type', 'Department', 'Driver'];
    const headerRow2: (string | number)[] = ['', '', '', '', ''];

    MONTH_LABELS.forEach(month => {
      headerRow1.push(month, ...Array(activeColumnCount - 1).fill(''));
      activeColumns.forEach(col => headerRow2.push(col.label));
    });
    headerRow1.push('Total');
    headerRow2.push('');

    const data: (string | number)[][] = [headerRow1, headerRow2];

    // Helper: push column values for a section/department totals row
    const pushExpandedTotals = (row: (string | number)[], expanded: ExpandedTotals | undefined, isExpense: boolean, monthIndex: number, monthKey: string) => {
      const rooms = occupiedRoomsPerMonth[monthKey];
      const rev = revenueExpandedTotals[monthKey];
      const ctx: ColumnRenderContext = { expanded: expanded || null, value: 0, isExpense, monthIndex, actualOccupiedRooms: rooms?.actual, forecastOccupiedRooms: rooms?.forecast, actualTotalRevenue: rev?.actual ? rev.actual * -1 : 0, budgetTotalRevenue: rev?.budget || 0, lyBudgetTotalRevenue: rev?.lyBudget || 0, forecastTotalRevenue: rev?.forecast || 0 };
      activeColumns.forEach(col => row.push(col.getValue(ctx)));
    };

    // Revenue Section Header
    const revenueSectionRow: (string | number)[] = ['', 'REVENUE', '', '', ''];
    MONTH_LABELS.forEach((month, index) => {
      pushExpandedTotals(revenueSectionRow, revenueExpandedTotals[month.toLowerCase()], false, index, month.toLowerCase());
    });
    revenueSectionRow.push(revenueSectionTotal);
    data.push(revenueSectionRow);

    // Revenue accounts grouped by department
    revenueDepartments.forEach(dept => {
      const deptRow: (string | number)[] = ['', `  ${dept.department}`, '', '', ''];
      MONTH_LABELS.forEach((month, index) => {
        pushExpandedTotals(deptRow, dept.monthlyExpandedTotals[month.toLowerCase()], false, index, month.toLowerCase());
      });
      deptRow.push(dept.total);
      data.push(deptRow);
      dept.accounts.forEach(account => data.push(getExpandedRow(account, false)));
    });

    // Expense Section Header
    const expenseSectionRow: (string | number)[] = ['', 'EXPENSE', '', '', ''];
    MONTH_LABELS.forEach((month, index) => {
      pushExpandedTotals(expenseSectionRow, expenseExpandedTotals[month.toLowerCase()], true, index, month.toLowerCase());
    });
    expenseSectionRow.push(expenseSectionTotal);
    data.push(expenseSectionRow);

    // Expense accounts grouped by department
    expenseDepartments.forEach(dept => {
      const deptRow: (string | number)[] = ['', `  ${dept.department}`, '', '', ''];
      MONTH_LABELS.forEach((month, index) => {
        pushExpandedTotals(deptRow, dept.monthlyExpandedTotals[month.toLowerCase()], true, index, month.toLowerCase());
      });
      deptRow.push(dept.total);
      data.push(deptRow);
      dept.accounts.forEach(account => data.push(getExpandedRow(account, true)));
    });

    // Net Income Row
    const netIncomeRow: (string | number)[] = ['', 'NET INCOME', '', '', ''];
    MONTH_LABELS.forEach(month => {
      const monthKey = month.toLowerCase();
      const revExp = revenueExpandedTotals[monthKey];
      const expExp = expenseExpandedTotals[monthKey];
      activeColumns.forEach(col => netIncomeRow.push(getNetIncomeValue(col.key, revExp || null, expExp || null)));
    });
    netIncomeRow.push(netTotal);
    data.push(netIncomeRow);

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);

    // Set column widths
    const colWidths: XLSX.ColInfo[] = [
      { wch: 12 }, // Account #
      { wch: 35 }, // Account Name
      { wch: 10 }, // Type
      { wch: 15 }, // Department
      { wch: 20 }, // Driver
    ];
    // Add widths for each month's columns
    MONTH_LABELS.forEach(() => {
      activeColumns.forEach(col => {
        colWidths.push({ wch: col.category === 'computed' ? 10 : 12 });
      });
    });
    colWidths.push({ wch: 14 }); // Total
    ws['!cols'] = colWidths;

    // Merge cells for month headers (row 1)
    const merges: XLSX.Range[] = [];
    let colIndex = 5; // Start after fixed columns
    MONTH_LABELS.forEach(() => {
      merges.push({
        s: { r: 0, c: colIndex },
        e: { r: 0, c: colIndex + activeColumnCount - 1 }
      });
      colIndex += activeColumnCount;
    });
    ws['!merges'] = merges;

    XLSX.utils.book_append_sheet(wb, ws, 'Forecast P&L');

    // Generate filename with date
    const fileName = `forecast_pnl_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Save file
    XLSX.writeFile(wb, fileName);

    toast.success('Exported to Excel');
  };

  const toolbarBar = (
    <div ref={toolbarRef} className={`fv3-grid-toolbar flex-shrink-0 bg-white px-3 py-1.5 flex items-center justify-end gap-1 z-40 relative whitespace-nowrap ${hasRibbonSlot ? 'fv3-grid-toolbar--ribbon w-full' : 'border-b border-slate-100'}`}>
      {/* Sync / recalc status */}
      {isRecalculating ? (
        <div className="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium" style={{ color: 'var(--lodge-onyx-500)' }}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0" style={{ background: 'var(--lodge-orange-400)' }} />
          {toolbar.showSyncLabel && 'Recalculating'}
        </div>
      ) : isRefreshing ? (
        <div className="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium" style={{ color: 'var(--lodge-onyx-500)' }}>
          <RefreshCw size={11} className="animate-spin flex-shrink-0" style={{ color: 'var(--lodge-orange-400)' }} />
          {toolbar.showSyncLabel && 'Refreshing'}
        </div>
      ) : (
        <button
          onClick={onRefresh}
          disabled={isRefreshing || isRecalculating}
          className="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors disabled:opacity-40"
          style={{ color: 'var(--color-success-ink, #16a34a)' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-onyx-50)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          title="Click to refresh"
        >
          <Check size={12} strokeWidth={2.5} />
          {toolbar.showSyncLabel && (refreshSuccess ? 'Updated' : 'Synced')}
        </button>
      )}

      {isBudget ? (
        <span
          className="px-2.5 py-1 text-xs font-semibold rounded-md"
          style={{ background: 'var(--color-info-bg)', color: 'var(--color-info-ink)', border: '1px solid rgba(11,165,236,0.35)' }}
          title="Budget versions show budget values for every month"
        >
          Budget
        </span>
      ) : (
        <div className="fv3-seg inline-flex items-center rounded-lg p-0.5 flex-none" style={{ background: 'var(--lodge-onyx-50)' }} role="group" aria-label="Actuals blend">
          <button
            type="button"
            onClick={() => onActualRollInChange?.(true)}
            className={`fv3-seg-btn px-2.5 py-1 text-xs rounded-md transition-colors ${actualRollInEnabled ? 'fv3-seg-on font-semibold' : 'font-medium'}`}
            title="Blend closed-month actuals with remaining forecast"
          >
            {segments.blended}
          </button>
          <button
            type="button"
            onClick={() => onActualRollInChange?.(false)}
            className={`fv3-seg-btn px-2.5 py-1 text-xs rounded-md transition-colors ${!actualRollInEnabled ? 'fv3-seg-on font-semibold' : 'font-medium'}`}
            title="Show forecast values for every month"
          >
            {segments.forecastOnly}
          </button>
        </div>
      )}

      {/* Statement layout: two-section P&L or the USALI GOP statement */}
      <div className="fv3-seg inline-flex items-center rounded-lg p-0.5 flex-none" style={{ background: 'var(--lodge-onyx-50)' }} role="group" aria-label="Statement layout">
        <button
          type="button"
          onClick={() => changeGridLayout('pnl')}
          className={`fv3-seg-btn px-2.5 py-1 text-xs rounded-md transition-colors ${gridLayout === 'pnl' ? 'fv3-seg-on font-semibold' : 'font-medium'}`}
          title="Revenue, Expense, Net Income"
        >
          P&L
        </button>
        <button
          type="button"
          onClick={() => changeGridLayout('gop')}
          className={`fv3-seg-btn px-2.5 py-1 text-xs rounded-md transition-colors ${gridLayout === 'gop' ? 'fv3-seg-on font-semibold' : 'font-medium'}`}
          title="USALI operating statement: Departmental Profit, Undistributed, GOP, fixed charges, Net Income — as on the Financial report"
        >
          GOP
        </button>
      </div>

      {toolbar.showDividers && <div className="w-px h-4 bg-slate-200 mx-1 flex-none" />}

      {/* Account search — icon only */}
      <AccountSearchBar
        revenueAccounts={revenueAccounts}
        expenseAccounts={expenseAccounts}
        expandedSections={expandedSections}
        onEnsureExpanded={ensureSectionExpanded}
        onSelectAccount={onSelectAccount}
        scrollContainerRef={scrollContainerRef}
        iconOnly
      />

      {/* Filter — icon only */}
      <div ref={filterDropdownRef} className="relative">
        <button
          onClick={() => setShowFilterDropdown(!showFilterDropdown)}
          className="p-1.5 rounded transition-colors relative"
          style={{ color: hideZeroAccounts ? 'var(--lodge-orange-500)' : 'var(--lodge-onyx-400)' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-onyx-50)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          title="Filters"
        >
          <Filter size={15} />
          {hideZeroAccounts && (
            <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full" style={{ background: 'var(--lodge-orange-400)' }} />
          )}
        </button>
        {showFilterDropdown && (
          <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-[100] w-52">
            <div className="p-2">
              <label className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-2 rounded">
                <input
                  type="checkbox"
                  checked={hideZeroAccounts}
                  onChange={(e) => setHideZeroAccounts(e.target.checked)}
                  className="rounded border-slate-300"
                  style={{ accentColor: 'var(--lodge-orange-400)' }}
                />
                <span className="text-sm text-slate-700">Hide $0 accounts</span>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Edit toggle */}
      {onToggleBulkMode && !isReadOnly && (
        <>
          <button
            onClick={onToggleBulkMode}
            className={`flex items-center gap-1.5 h-[30px] text-[12.5px] font-medium rounded-lg transition-colors flex-none ${toolbar.showBulkEditLabel ? 'px-2.5' : 'px-1.5'}`}
            style={isBulkMode
              ? { background: 'var(--lodge-orange-50)', color: 'var(--lodge-orange-600)', border: '1px solid var(--lodge-orange-200)' }
              : { color: 'var(--lodge-onyx-700)', border: '1px solid var(--lodge-onyx-200)' }}
            onMouseEnter={e => { if (!isBulkMode) (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-onyx-50)'; }}
            onMouseLeave={e => { if (!isBulkMode) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            title={isBulkMode ? 'Exit bulk edit' : 'Bulk edit'}
          >
            <Layers size={14} />
            {toolbar.showBulkEditLabel && 'Bulk Edit'}
          </button>
          {isBulkMode && selectedAccountIds.length > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded font-medium tabular-nums" style={{ color: 'var(--lodge-orange-600)', background: 'var(--lodge-orange-50)' }}>
              {selectedAccountIds.length}
            </span>
          )}
        </>
      )}

          {/* AI workflow. One control: the primary half keeps the
              stage-appropriate action — Build with AI on a fresh forecast,
              Analyze once it is built — and the caret opens the rest, because
              "review my accuracy", "fix every driver" and "check this against
              the books" are three different questions and only one of them
              fits on a button. */}
          {aiWorkflowMode !== 'none' && (onAiBuild || onAiAnalyze) && (() => {
            const buildIsPrimary = aiWorkflowMode === 'build' && !!onAiBuild && !isReadOnly;
            const kind = isBudget ? 'budget' : 'forecast';
            const menuItems = [
              onAiAnalyze && {
                key: 'analyze',
                title: 'Analyze with AI',
                desc: `Accuracy review — where this ${kind} is off, and what to change`,
                run: onAiAnalyze,
              },
              onAiBuildDrivers && !isReadOnly && {
                key: 'build-drivers',
                title: 'Build drivers with AI',
                desc: 'Assign or correct the driver on every account',
                run: onAiBuildDrivers,
              },
              onAiReviewBudget && isBudget && {
                key: 'review-budget',
                title: 'Review budget',
                desc: 'Audit the completed budget vs history, last year, notes and your checklist',
                run: onAiReviewBudget,
              },
              onAiCompareOtb && {
                key: 'compare-otb',
                title: 'Compare to on-the-books & RMS',
                desc: 'Rooms revenue vs booked and the RMS projection',
                run: onAiCompareOtb,
              },
              onAiBuild && !isReadOnly && !buildIsPrimary && {
                key: 'build-review',
                title: 'Open the build review screen',
                desc: 'Driver proposals with computed monthly values',
                run: onAiBuild,
              },
            ].filter(Boolean) as { key: string; title: string; desc: string; run: () => void }[];

            return (
              <>
                <div ref={aiMenuRef} className="relative flex-none">
                  <div className="lodge-ai-split lodge-ai-split--cta">
                    <button
                      onClick={buildIsPrimary ? onAiBuild : onAiAnalyze}
                      disabled={!buildIsPrimary && !onAiAnalyze}
                      className={`py-1.5 text-sm ${toolbar.showAiLabel ? 'px-2.5' : 'px-1.5'} ${buildIsPrimary ? 'font-semibold' : 'font-medium'}`}
                      title={buildIsPrimary
                        ? `Let AI propose ${kind} drivers for every account`
                        : `Review this ${kind} for accuracy — the analyst workflow`}
                    >
                      <Sparkles size={14} />
                      {toolbar.showAiLabel && (buildIsPrimary ? 'Build with AI' : 'Analyze with AI')}
                    </button>
                    <button
                      className="lodge-ai-split__caret"
                      onClick={() => setShowAiMenu(v => !v)}
                      title="More AI actions"
                      aria-haspopup="menu"
                      aria-expanded={showAiMenu}
                    >
                      <ChevronDown size={13} />
                    </button>
                  </div>

                  {showAiMenu && (
                    <div className="lodge-ai-menu absolute left-0 top-full mt-1.5 z-[100] w-[18rem] overflow-hidden" role="menu">
                      {menuItems.map(item => (
                        <button
                          key={item.key}
                          role="menuitem"
                          className="lodge-ai-menu__item"
                          onClick={() => { setShowAiMenu(false); item.run(); }}
                        >
                          <div className="lodge-ai-menu__title">{item.title}</div>
                          <div className="lodge-ai-menu__desc">{item.desc}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            );
          })()}
      {/* ⋯ More — the controls that set the grid up rather than work in it:
          export, expanded columns, density, month zoom, fullscreen. */}
      <div ref={moreMenuRef} className="relative flex-none">
        <button
          onClick={() => setShowMoreMenu(v => !v)}
          className="flex items-center justify-center w-[30px] h-[30px] rounded-lg transition-colors"
          style={{ color: 'var(--lodge-onyx-500)', background: showMoreMenu ? 'var(--lodge-onyx-100)' : 'var(--lodge-onyx-50)' }}
          title="More — export, columns, density, zoom, fullscreen"
          aria-haspopup="menu"
          aria-expanded={showMoreMenu}
        >
          <MoreHorizontal size={15} />
        </button>
        {showMoreMenu && (
          <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-[100] w-64 py-1" role="menu">
            <div className="px-3 pt-2 pb-1 text-[10.5px] font-semibold text-slate-400" style={{ letterSpacing: '0.7px' }}>EXPORT</div>
                <button
                  onClick={() => { handleExportCSV(); setShowMoreMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <Download size={13} />
                  Export CSV
                </button>
                <button
                  onClick={() => { handleExportExcel(); setShowMoreMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <FileSpreadsheet size={13} />
                  Export Excel
                </button>
                <button
                  onClick={() => { void handleExportRaw(); setShowMoreMenu(false); }}
                  className="w-full flex flex-col items-start px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
                >
                  <span className="flex items-center gap-2 whitespace-nowrap"><Download size={13} />Raw data export · CSV</span>
                  <span className="text-[11px] text-slate-400 pl-[21px] leading-snug">{rawExportFormat ? `Layout: ${rawExportFormat.name || 'custom'}` : 'Flat account detail, budget template layout'}</span>
                </button>
                {isBudget && (
                  <button
                    onClick={() => {
                      setShowMoreMenu(false);
                      // Nothing to ask when no comparison columns are active.
                      if (packComparisonDefs.length === 0) void handleExportBudgetPack(false);
                      else setShowBudgetPackDialog(true);
                    }}
                    className="w-full flex flex-col items-start px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
                  >
                    <span className="flex items-center gap-2 whitespace-nowrap"><FileSpreadsheet size={13} />Budget pack · Excel</span>
                    <span className="text-[11px] text-slate-400 pl-[21px] leading-snug">P&L tab + a tab per department with driver detail{packExportFormat ? ` · Export tab: ${packExportFormat.name || 'custom'}` : ''}</span>
                  </button>
                )}
                {orgId ? (
                  <button
                    onClick={() => { setShowMoreMenu(false); setShowExportFormats(isBudget ? 'packExport' : 'raw'); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <Sliders size={13} />
                    Export formats…
                  </button>
                ) : null}
            <div className="my-1 border-t border-slate-100" />
            <div className="px-3 pt-2 pb-1 text-[10.5px] font-semibold text-slate-400" style={{ letterSpacing: '0.7px' }}>GRID</div>
            <button
              onClick={() => { setShowMoreMenu(false); setShowColumnSettings(true); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Sliders size={13} />
              Expanded columns…
            </button>
            {insetActive && (
              <button
                onClick={() => { setGridDensity(gridDensity === 'compact' ? 'comfortable' : 'compact'); setShowMoreMenu(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                {gridDensity === 'compact' ? <Rows3 size={13} /> : <Rows4 size={13} />}
                {gridDensity === 'compact' ? 'Comfortable rows' : 'Compact rows'}
              </button>
            )}
            <button
              onClick={() => { toggleGridLines(); setShowMoreMenu(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              aria-pressed={gridLines}
            >
              <Grid3x3 size={13} />
              {gridLines ? 'Hide grid lines' : 'Show grid lines'}
            </button>
            <button
              onClick={() => { expandAllColumns(); setShowMoreMenu(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Plus size={13} />
              Expand all months
            </button>
            <button
              onClick={() => { collapseAllColumns(); setShowMoreMenu(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Minus size={13} />
              Collapse all months
            </button>
            <button
              onClick={() => { setFullscreen(!isFullscreen); setShowMoreMenu(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
            >
              {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
              {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            </button>
          </div>
        )}
        {/* Column picker, anchored here; opened from the menu */}
        <ExpandedColumnSettings
          allColumns={pickerColumns}
          visibleKeys={pickerVisibleKeys}
          onToggle={toggleColumn}
          onMove={(key, direction) => {
            const list = [...pickerVisibleKeys];
            const idx = list.indexOf(key);
            const to = direction === 'up' ? idx - 1 : idx + 1;
            if (idx === -1 || to < 0 || to >= list.length) return;
            [list[idx], list[to]] = [list[to], list[idx]];
            updateKeys(list);
          }}
          onReorder={updateKeys}
          onReset={resetToDefaults}
          open={showColumnSettings}
          onOpenChange={setShowColumnSettings}
          hideTrigger
        />
      </div>

      {/* Driver-level override count + clear all */}
      {driverOverrideTotal > 0 && (
        <>
          <div
            className="px-2.5 py-1 rounded-full text-xs font-semibold tabular-nums whitespace-nowrap"
            style={{ background: 'var(--lodge-orange-50)', border: '1px solid var(--fv3-accent-line, #FFD9B3)', color: 'var(--lodge-orange-500)' }}
            title="Driver-level monthly overrides in this version (driver stays live)"
          >
            {driverOverrideTotal} driver override{driverOverrideTotal === 1 ? '' : 's'}
          </div>
          {onClearDriverInputOverrides && !isReadOnly && (
            <button
              type="button"
              onClick={() => { void handleClearAllDriverOverrides(); }}
              className="px-2.5 py-1 rounded-lg text-xs font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 whitespace-nowrap"
            >
              Clear all overrides
            </button>
          )}
        </>
      )}

      {/* Sweep line — only during cell-save recalc */}
      {isRecalculating && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 overflow-hidden" style={{ background: 'var(--lodge-orange-100)' }}>
          <div
            className="absolute inset-y-0 w-1/3"
            style={{ background: 'var(--lodge-orange-400)', animation: 'recalc-sweep 1.4s ease-in-out infinite' }}
          />
        </div>
      )}
    </div>
  );

  const gridDisplay = useMemo(() => ({ closedMonthSet, hideSymbol: insetActive }), [closedMonthSet, insetActive]);

  return (
    <GridDisplayContext.Provider value={gridDisplay}>
    <div className="fv3-grid-shell flex-1 min-h-0 flex flex-col overflow-hidden border border-slate-200 rounded-b-xl">
      {/* Toolbar — one working bar, right-aligned: status · view toggles ·
          search · filter · Bulk Edit · AI · ⋯ (export, columns, density,
          zoom, fullscreen) · NET. Rendered into the module ribbon when the
          ribbon offers a slot, inline above the grid otherwise (pop-outs). */}
      <RibbonPortal fallback={toolbarBar}>{toolbarBar}</RibbonPortal>

      {/* First-run AI invitation — a fresh forecast should invite the draft,
          not hide it behind a toolbar button. Dismiss persists per version.
          Budgets never enter 'build' mode — they only ever show Analyze with AI. */}
      {aiWorkflowMode === 'build' && onAiBuild && !isReadOnly && !aiBandDismissed && !isBudget && (
        <div className="lodge-ai-band flex items-center gap-3 px-4 py-2.5 flex-shrink-0">
          <div className="lodge-ai-tile w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0">
            <Sparkles size={15} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-tight" style={{ color: 'var(--lodge-onyx-800)' }}>
              This {isBudget ? 'budget' : 'forecast'} is empty — let AI draft it
            </p>
            <p className="text-xs leading-tight mt-0.5" style={{ color: 'var(--lodge-onyx-500)' }}>
              Drivers proposed for every account from 12 months of history. You review everything before it's applied — and one click undoes it all.
            </p>
          </div>
          <button
            onClick={onAiBuild}
            className="lodge-ai-cta flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold rounded-lg flex-shrink-0"
          >
            <Sparkles size={14} />
            Build with AI
          </button>
          <button
            onClick={() => setAiBandDismissed(true)}
            className="p-1.5 rounded-md flex-shrink-0 transition-colors"
            style={{ color: 'var(--lodge-onyx-400)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-onyx-50)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            title="Dismiss — the Build with AI button stays in the toolbar"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Grid - scrollable container */}
      <div ref={scrollContainerRef} className={`flex-1 min-h-0 overflow-auto ${gridSelection.isDragging ? 'grid-selecting' : ''}`}>
        <table
          className={`fv3-grid w-full text-sm border-collapse${gridLines ? '' : ' fv3-grid--nolines'}`}
          style={{ '--fv3-thead-h': `${theadHeight}px` } as React.CSSProperties}
        >
          {/* Column ground: the working month is tinted through every row,
              as on the printed statement, not just in its header. */}
          <colgroup>
            {isBulkMode && <col />}
            <col />
            {MONTH_LABELS.map((month, index) => {
              const span = isColumnExpanded(index) ? activeColumnCount : 1;
              const active = activeMonth === index + 1;
              return Array.from({ length: span }, (_, k) => (
                <col key={`${month}-${k}`} className={active ? 'fv3-col-active' : undefined} />
              ));
            })}
            {Array.from({ length: totalColSpan }, (_, k) => <col key={`total-${k}`} />)}
          </colgroup>
          <thead ref={theadRef}>
            <tr className="fv3-head-row border-b border-slate-100">
              {isBulkMode && (
                <th className="sticky top-0 left-0 bg-slate-50 py-3 px-2 w-10 z-30">
                  {/* Header checkbox - no action, just placeholder */}
                </th>
              )}
              <th className={`sticky top-0 ${isBulkMode ? '' : 'left-0'} bg-slate-50 text-left py-3 px-4 font-medium text-slate-500 w-64 z-30 fv3-col-account`}>
                Account{insetActive && <span className="fv3-col-unit"> · {currencySymbol(currency)}</span>}
              </th>
              {MONTH_LABELS.map((month, index) => {
                const status = getMonthStatus(index);
                const isActive = activeMonth === index + 1;
                const isExpanded = isColumnExpanded(index);
                const monthKey = month.toLowerCase();
                const isRolledActual = actualRollInEnabled && closedMonthSet.has(monthKey);
                return (
                  <React.Fragment key={month}>
                  <th
                    className={`fv3-col-month${isActive ? ' fv3-month-active' : ''}${isExpanded ? ' fv3-month-expanded' : ''} sticky top-0 z-20 text-center py-3 px-3 font-medium text-slate-500 min-w-[100px] ${onMonthClick ? 'cursor-pointer' : ''}`}
                    style={{ background: isActive ? 'var(--lodge-orange-50)' : 'var(--lodge-onyx-50)' }}
                    onClick={() => onMonthClick?.(index + 1)}
                    title={insetActive && isRolledActual ? (status === 'locked' ? 'Actual · locked' : 'Actual · recorded month') : getStatusLabel(status)}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleColumnExpansion(index); }}
                        className="fv3-month-expand p-0.5 hover:bg-slate-200 rounded text-slate-400 hover:text-slate-600 transition-colors"
                        title={isExpanded ? 'Collapse month' : 'Expand month'}
                      >
                        {isExpanded ? <Minus size={12} /> : <Plus size={12} />}
                      </button>
                      <div className="flex flex-col items-center gap-0.5 flex-1">
                        <div className="flex items-center gap-1">
                          <span className="fv3-month-label">{month}</span>
                          {isRolledActual ? <Check size={12} className="text-emerald-600" /> : <MonthStatusIndicator status={status} />}
                          {isRolledActual && status === 'locked' && insetActive && <Lock size={11} className="fv3-month-lock" />}
                        </div>
                        <span
                          className="fv3-month-status text-[10px] font-normal"
                          style={{
                            color: isRolledActual ? '#15803d'
                              : status === 'draft' ? 'var(--lodge-orange-400)'
                              : status === 'projected' ? 'var(--color-info)'
                              : 'var(--lodge-onyx-400)'
                          }}
                        >
                          {isRolledActual ? (status === 'locked' ? 'ACT - LOCKED' : 'ACTUAL') :
                           status === 'locked' ? 'LOCKED' :
                           status === 'draft' ? 'DRAFT' :
                           status === 'projected' ? 'PROJ' :
                           'NONE'}
                        </span>
                      </div>
                      <div className="fv3-month-spacer w-4" /> {/* Spacer for symmetry */}
                    </div>
                  </th>
                  {/* The month's comparison columns: one header each, on the
                      banded ground, in the same row — the month keeps its cell. */}
                  {isExpanded && activeColumns.filter(c => !c.isEditable).map(col => (
                    <th key={`${month}-${col.key}`} className="fv3-col-sub sticky top-0 z-20 text-right py-3 px-3 font-medium text-slate-500 min-w-[96px]" title={col.label}>
                      {col.label}
                    </th>
                  ))}
                  </React.Fragment>
                );
              })}
              <th
                className={`fv3-col-total${isTotalExpanded ? ' fv3-month-expanded' : ''} sticky top-0 bg-slate-50 py-3 px-3 font-medium text-slate-500 min-w-[110px] z-20`}
              >
                <div className="flex items-center justify-between gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleColumnExpansion(TOTAL_COLUMN_INDEX); }}
                    className="fv3-month-expand p-0.5 hover:bg-slate-200 rounded text-slate-400 hover:text-slate-600 transition-colors"
                    title={isTotalExpanded ? 'Collapse total' : 'Expand total into the selected columns'}
                  >
                    {isTotalExpanded ? <Minus size={12} /> : <Plus size={12} />}
                  </button>
                  <span className="flex-1 text-right">{insetActive ? 'Total' : 'TOTAL'}</span>
                </div>
              </th>
              {isTotalExpanded && activeColumns.filter(c => !c.isEditable).map(col => (
                <th key={`total-${col.key}`} className="fv3-col-sub sticky top-0 z-20 text-right py-3 px-3 font-medium text-slate-500 min-w-[96px]" title={col.label}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <>
            {/* Room Stats Section */}
            {roomStats && (
              <tbody><RoomStatsSection
                roomStats={roomStats}
                isBulkMode={isBulkMode}
                isColumnExpanded={isColumnExpanded}
                isRecalculating={false}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
                roomStatSource={roomStatSource}
                onRoomStatSourceChange={onRoomStatSourceChange}
                actualRollInEnabled={actualRollInEnabled}
                closedMonthSet={closedMonthSet}
                isReadOnly={isReadOnly}
                onRoomStatOverride={onRoomStatOverride}
                year={year}
                activeColumnCount={activeColumnCount}
                activeColumns={activeColumns}
                currency={currency}
                orgId={orgId}
                locationId={locationId}
                versionId={versionId}
                revenueAccounts={revenueAccounts}
                isBudget={isBudget}
                onStatInputsApplied={onStatInputsApplied}
                onOpenStatMapping={onOpenStatMapping}
              /></tbody>
            )}

            {/* Other Statistics — keyed values on any non-room-night stat account */}
            {versionId && (
              <tbody><OtherStatsSection
                orgId={orgId}
                locationId={locationId}
                versionId={versionId}
                year={year}
                isBulkMode={isBulkMode}
                isColumnExpanded={isColumnExpanded}
                activeColumns={activeColumns}
                activeColumnCount={activeColumnCount}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
                actualRollInEnabled={actualRollInEnabled}
                closedMonthSet={closedMonthSet}
                isBudget={isBudget}
                isReadOnly={isReadOnly}
                roomStats={roomStats ?? null}
                revenueAccounts={revenueAccounts}
              /></tbody>
            )}

            {gridLayout === 'gop' ? (
              <>
                {/* ── GOP statement layout ─────────────────────────────── */}
                <tbody><GopLineRow
                  label="Revenue"
                  variant="band"
                  isExpanded={isRevenueExpanded}
                  onToggle={() => handleToggleSection('revenue')}
                  line={gopLines.revenue}
                  isExpense={false}
                  {...gopRowCommon}
                /></tbody>
                {isRevenueExpanded && gopLayout.revenue.map((dept) => renderDepartmentBlock(dept, false))}

                <tbody><GopLineRow
                  label="Department Expense"
                  variant="band"
                  isExpanded={isExpenseExpanded}
                  onToggle={() => handleToggleSection('expense')}
                  line={gopLines.deptExpense}
                  isExpense={true}
                  {...gopRowCommon}
                /></tbody>
                {isExpenseExpanded && gopLayout.deptExpense.map((dept) => renderDepartmentBlock(dept, true))}

                <tbody><GopLineRow label="Departmental Profit" variant="line" line={gopLines.departmentalProfit} isExpense={false} {...gopRowCommon} /></tbody>

                {renderCostBand('UND')}
                <tbody><GopLineRow label="Gross Operating Profit (GOP)" variant={gopLayout.hasBelowGop ? 'line' : 'net'} line={gopLines.gop} isExpense={false} {...gopRowCommon} /></tbody>

                {renderCostBand('MGMT')}
                {gopLines.ibfc && <tbody><GopLineRow label="Income Before Fixed Charges" variant="line" line={gopLines.ibfc} isExpense={false} {...gopRowCommon} /></tbody>}

                {renderCostBand('FIXED')}
                {gopLines.ebitda && <tbody><GopLineRow label="EBITDA" variant="line" line={gopLines.ebitda} isExpense={false} {...gopRowCommon} /></tbody>}

                {renderCostBand('BELOW')}
                {gopLines.net && <tbody><GopLineRow label="Net Income" variant="net" line={gopLines.net} isExpense={false} {...gopRowCommon} /></tbody>}
              </>
            ) : (
              <>
                {/* Revenue Section */}
                <tbody><SimpleSectionRow
                  name="Revenue"
                  monthlyTotals={revenueTotals}
                  monthlyExpandedTotals={revenueExpandedTotals}
                  total={revenueSectionTotal}
                  isExpanded={isRevenueExpanded}
                  onToggle={() => handleToggleSection('revenue')}
                  isBulkMode={isBulkMode}
                  selectionState={revenueSelectionState}
                  onToggleSelection={handleSelectRevenueAccounts}
                  isColumnExpanded={isColumnExpanded}
                  isExpense={false}
                  isRecalculating={false}
                  activeColumns={activeColumns}
                  occupiedRoomsPerMonth={occupiedRoomsPerMonth}
                  revenuePerMonth={revenueExpandedTotals}
                  closedMonthSet={actualRollInEnabled ? closedMonthSet : undefined}
                  currency={currency}
                /></tbody>
                {isRevenueExpanded && revenueDepartments.map((dept) => renderDepartmentBlock(dept, false))}

                {/* Expense Section */}
                <tbody><SimpleSectionRow
                  name="Expense"
                  monthlyTotals={expenseTotals}
                  monthlyExpandedTotals={expenseExpandedTotals}
                  total={expenseSectionTotal}
                  isExpanded={isExpenseExpanded}
                  onToggle={() => handleToggleSection('expense')}
                  isBulkMode={isBulkMode}
                  selectionState={expenseSelectionState}
                  onToggleSelection={handleSelectExpenseAccounts}
                  isColumnExpanded={isColumnExpanded}
                  isExpense={true}
                  isRecalculating={false}
                  activeColumns={activeColumns}
                  occupiedRoomsPerMonth={occupiedRoomsPerMonth}
                  revenuePerMonth={revenueExpandedTotals}
                  closedMonthSet={actualRollInEnabled ? closedMonthSet : undefined}
                  currency={currency}
                /></tbody>
                {isExpenseExpanded && expenseDepartments.map((dept) => renderDepartmentBlock(dept, true))}

                {/* Net Income Row */}
                <tbody><NetIncomeRow netIncome={netIncome} total={netTotal} isBulkMode={isBulkMode} revenueExpandedTotals={revenueExpandedTotals} expenseExpandedTotals={expenseExpandedTotals} isColumnExpanded={isColumnExpanded} isRecalculating={false} activeColumns={activeColumns} closedMonthSet={actualRollInEnabled ? closedMonthSet : undefined} currency={currency} /></tbody>
              </>
            )}
          </>
        </table>
      </div>

      {/* Context Menu */}
      <CellContextMenu
        isOpen={contextMenuState.isOpen}
        position={contextMenuState.position}
        onClose={handleContextMenuClose}
        onDailyEntry={handleOpenDailyEntry}
        onClearOverride={handleClearOverride}
        onClearDailyValues={handleClearDailyValues}
        hasOverride={contextMenuState.cellData?.hasOverride || false}
        hasDailyValues={contextMenuState.cellData?.hasDailyValues || false}
        dailyMode={contextMenuState.cellData?.dailyMode ?? null}
        pinnedCount={contextMenuState.cellData?.pinnedCount ?? 0}
        isEditable={
          contextMenuState.cellData
            ? isMonthEditable(contextMenuState.cellData.monthIndex)
            : false
        }
        accountName={contextMenuState.cellData?.account.accountName}
        monthName={
          contextMenuState.cellData
            ? MONTH_LABELS[contextMenuState.cellData.monthIndex]
            : undefined
        }
        isRevenueAccount={contextMenuState.cellData?.isRevenueAccount || false}
        driverOverrideCount={contextMenuState.cellData?.driverOverrideCount ?? 0}
        onClearDriverOverrides={onDriverInputOverrideSave ? handleClearDriverOverridesForMonth : undefined}
        onRevenueForecastSetup={
          contextMenuState.cellData?.isRevenueAccount && onRevenueForecastSetup && /room/i.test(contextMenuState.cellData?.account?.department || '')
            ? () => {
                const account = contextMenuState.cellData!.account;
                onRevenueForecastSetup({ departmentName: account.department, departmentId: account.departmentId });
                handleContextMenuClose();
              }
            : undefined
        }
      />

      {/* Daily Entry Modal */}
      {dailyEntryState.isOpen && dailyEntryState.account && versionId && locationId && year && (
        <DailyEntryModal
          isOpen={dailyEntryState.isOpen}
          onClose={handleCloseDailyEntry}
          versionId={versionId}
          chartOfAccountId={dailyEntryState.account.chartOfAccountId}
          locationId={locationId}
          accountName={dailyEntryState.account.accountName}
          accountNumber={dailyEntryState.account.accountNumber || ''}
          driverType={dailyEntryState.account.driverType}
          driverValue={
            dailyEntryState.account.monthlyValues[
              MONTH_LABELS[dailyEntryState.month - 1].toLowerCase()
            ] || 0
          }
          year={year}
          month={dailyEntryState.month}
          currency={currency}
          onSave={handleDailyEntrySave}
          showStatMode={!!dailyEntryState.linkedStatAccount}
          linkedStatAccountName={dailyEntryState.linkedStatAccount?.accountName}
          initialMonthlyRN={dailyEntryState.initialMonthlyRN ?? undefined}
          initialMonthlyADR={dailyEntryState.initialMonthlyADR ?? undefined}
        />
      )}

      {showExportFormats && orgId ? (
        <ExportFormatsModal
          orgId={orgId}
          locationId={locationId}
          locationName={locationName}
          initialTarget={showExportFormats}
          loadPreviewInput={loadExportPreviewInput}
          onClose={() => setShowExportFormats(null)}
          onSaved={setExportFormats}
        />
      ) : null}

      {/* Budget Pack export — include comparison columns? */}
      {showBudgetPackDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-slate-100 rounded-lg flex-shrink-0">
                <FileSpreadsheet className="w-5 h-5 text-slate-600" />
              </div>
              <h2 className="text-base font-semibold text-slate-900">Export budget pack</h2>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Include your comparison columns? They export as collapsed column groups behind each
              month's {isBudget ? 'budget' : 'forecast'} figure — and the annual total — expandable
              in Excel with the +/&minus; outline controls.
            </p>
            <div className="flex flex-wrap gap-1.5 mb-5">
              {packComparisonDefs.map(col => (
                <span key={col.key} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium">
                  {col.label}
                </span>
              ))}
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowBudgetPackDialog(false)}
                className="px-4 py-2 text-sm font-medium text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { void handleExportBudgetPack(false); }}
                className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors"
              >
                Plan only
              </button>
              <button
                onClick={() => { void handleExportBudgetPack(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <FileSpreadsheet size={14} />Include comparisons
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Department Clear Overrides Confirmation */}
      {deptClearRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-base font-semibold text-slate-900">Clear Overrides</h2>
            </div>
            <p className="text-sm text-slate-600 mb-5">
              Clear all sub-account overrides for <strong>{deptClearRequest.dept.department}</strong> —{' '}
              <strong>{['January','February','March','April','May','June','July','August','September','October','November','December'][deptClearRequest.monthIndex]}</strong>?
              Accounts will revert to their driver-calculated values.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeptClearRequest(null)}
                disabled={isClearingOverrides}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleClearDeptOverrides}
                disabled={isClearingOverrides}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {isClearingOverrides ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Clearing...</>
                ) : (
                  <><Trash2 size={14} />Clear Overrides</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Department Monthly Spread Modal */}
      {deptSpreadRequest && versionId && orgId && locationId && year && (
        <DepartmentSpreadModal
          isOpen={true}
          onClose={() => setDeptSpreadRequest(null)}
          departmentName={deptSpreadRequest.dept.department}
          month={deptSpreadRequest.monthIndex + 1}
          monthKey={MONTHS[deptSpreadRequest.monthIndex]}
          childAccounts={deptSpreadRequest.dept.accounts}
          isRevenue={deptSpreadRequest.isRevenue}
          isBudget={isBudget}
          versionId={versionId}
          orgId={orgId}
          locationId={locationId}
          year={year}
          initialAmount={deptSpreadRequest.initialAmount}
          currency={currency}
          onBulkSave={onBulkPaste}
          onSuccess={() => {
            setDeptSpreadRequest(null);
            if (!onBulkPaste) onRefresh?.();
          }}
        />
      )}
    </div>
    </GridDisplayContext.Provider>
  );
}

interface SimpleSectionRowProps {
  name: string;
  revenuePerMonth?: Record<string, ExpandedTotals>;
  monthlyTotals: Record<string, number>;
  monthlyExpandedTotals?: Record<string, ExpandedTotals>;
  total: number;
  isExpanded: boolean;
  onToggle: () => void;
  isBulkMode?: boolean;
  selectionState?: 'none' | 'some' | 'all';
  onToggleSelection?: () => void;
  isColumnExpanded?: (index: number) => boolean;
  isExpense?: boolean;
  isRecalculating?: boolean;
  activeColumns?: ExpandedColumnDef[];
  occupiedRoomsPerMonth?: Record<string, { actual: number; forecast: number }>;
  closedMonthSet?: Set<string>;
  currency?: string | null;
}

function SimpleSectionRow({ name, monthlyTotals, monthlyExpandedTotals, total, isExpanded, onToggle, isBulkMode, selectionState = 'none', onToggleSelection, isColumnExpanded, isExpense = false, isRecalculating = false, activeColumns = [], occupiedRoomsPerMonth, revenuePerMonth, closedMonthSet, currency }: SimpleSectionRowProps) {
  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleSelection?.();
  };

  return (
    <tr
      className="fv3-r-section bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
      onClick={onToggle}
    >
      {isBulkMode && (
        <td className="sticky left-0 bg-slate-50 py-2.5 px-2 z-20" onClick={handleCheckboxClick}>
          {selectionState === 'all' ? (
            <CheckSquare size={18} className="cursor-pointer" style={{ color: 'var(--lodge-orange-400)' }} />
          ) : selectionState === 'some' ? (
            <MinusSquare size={18} className="cursor-pointer" style={{ color: 'var(--lodge-orange-300)' }} />
          ) : (
            <Square size={18} className="cursor-pointer" style={{ color: 'var(--lodge-onyx-400)' }} />
          )}
        </td>
      )}
      <td className={`fv3-label-cell ${isBulkMode ? '' : 'sticky left-0'} bg-slate-50 py-2.5 px-4 font-semibold text-slate-800 z-20`}>
        <div className="flex items-center gap-2">
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          {name}
        </div>
      </td>
      {MONTH_LABELS.map((month, index) => {
        const monthKey = month.toLowerCase();
        const isColExpanded = isColumnExpanded?.(index);

        if (isColExpanded && monthlyExpandedTotals) {
          const expanded = monthlyExpandedTotals[monthKey];
          const rooms = occupiedRoomsPerMonth?.[monthKey];
          const rev = revenuePerMonth?.[monthKey];
          const ctx: ColumnRenderContext = {
            expanded: expanded || null,
            value: 0,
            isExpense,
            monthIndex: index,
            actualOccupiedRooms: rooms?.actual,
            forecastOccupiedRooms: rooms?.forecast,
            actualTotalRevenue: rev?.actual ? rev.actual * -1 : 0,
            budgetTotalRevenue: rev?.budget || 0,
            lyBudgetTotalRevenue: rev?.lyBudget || 0, forecastTotalRevenue: rev?.forecast || 0,
            currency,
          };
          return (
            <React.Fragment key={month}>
              <ExpandedCellsRenderer
                columns={activeColumns}
                ctx={ctx}
                cellClassName="py-2.5 px-2 font-semibold text-slate-800"
              />
            </React.Fragment>
          );
        }

        const value = monthlyTotals[monthKey] || 0;
        const displayValue = value;
        const showVariance = closedMonthSet?.has(monthKey);
        const variance = showVariance ? getActualVsForecastVariance(monthlyExpandedTotals?.[monthKey], isExpense) : 0;
        return (
          <td key={month} className="text-right py-2.5 px-3 font-semibold text-slate-800 tabular-nums">
            <div className="flex flex-col items-end leading-tight">
              <RecalculatingValue value={formatCell(displayValue, true, currency)} isRecalculating={false} />
              {showVariance && (
                <span className={`text-[10px] font-medium ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatInlineVariance(variance, currency)}
                </span>
              )}
            </div>
          </td>
        );
      })}
      {isColumnExpanded?.(TOTAL_COLUMN_INDEX) && monthlyExpandedTotals ? (
        <ExpandedCellsRenderer
          columns={activeColumns}
          ctx={buildTotalContext({ expandedByMonth: monthlyExpandedTotals, value: total, isExpense, occupiedRoomsPerMonth, revenuePerMonth, currency })}
          cellClassName="py-2.5 px-2 font-semibold text-slate-800"
        />
      ) : (
        <td className="text-right py-2.5 px-4 font-semibold text-slate-800 tabular-nums">
          <RecalculatingValue value={formatCell(total, true, currency)} isRecalculating={false} />
        </td>
      )}
    </tr>
  );
}

interface DepartmentRowProps {
  department: string;
  departmentId?: number;  // FK to chartOfAccountDepartments
  monthlyTotals: Record<string, number>;
  monthlyExpandedTotals?: Record<string, ExpandedTotals>;
  total: number;
  isExpanded: boolean;
  onToggle: () => void;
  isBulkMode?: boolean;
  selectionState?: 'none' | 'some' | 'all';
  onToggleSelection?: () => void;
  isColumnExpanded?: (index: number) => boolean;
  isExpense?: boolean;
  driverType?: string | null;  // Department-level driver type (if set)
  onEditDriver?: () => void;   // Callback to open department panel
  isRecalculating?: boolean;
  onMonthlySpread?: (monthIndex: number) => void;  // Callback to spread a monthly amount
  onClearOverrides?: (monthIndex: number) => void; // Callback to clear child overrides for a month
  childOverrideMonths?: Set<string>;               // Month keys that have at least one child override
  onCellSave?: (monthIndex: number, value: number) => Promise<void>; // Inline dept entry
  isMonthEditable?: (monthIndex: number) => boolean;
  activeColumns?: ExpandedColumnDef[];
  occupiedRoomsPerMonth?: Record<string, { actual: number; forecast: number }>;
  revenuePerMonth?: Record<string, ExpandedTotals>;
  onRevenueForecastSetup?: () => void;  // Revenue departments only
  hasRevenueForecast?: boolean;  // True if rooms forecast has been applied (any child has daily values)
  currency?: string | null;
}

function DepartmentRow({ department, departmentId, monthlyTotals, monthlyExpandedTotals, total, isExpanded, onToggle, isBulkMode, selectionState = 'none', onToggleSelection, isColumnExpanded, isExpense = false, driverType, onEditDriver, isRecalculating = false, onMonthlySpread, onClearOverrides, childOverrideMonths, onCellSave, isMonthEditable, activeColumns = [], occupiedRoomsPerMonth, revenuePerMonth, onRevenueForecastSetup, hasRevenueForecast, currency }: DepartmentRowProps) {
  const { insetActive } = useTheme() as { insetActive: boolean };
  const [isHovered, setIsHovered] = useState(false);
  const [editingMonth, setEditingMonth] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [optimisticValues, setOptimisticValues] = useState<Record<number, number>>({});
  const cellTdRefs = useRef<(HTMLTableCellElement | null)[]>([]);
  const editInputRef = useRef<HTMLInputElement>(null);
  const editStartModeRef = useRef<'key' | 'enter'>('enter');
  const hasCommittedRef = useRef(false);

  // When fresh data arrives (monthlyTotals reference changes after a refetch), clear any
  // stale optimistic values so the recalculated department total is shown correctly.
  // This fixes the bug where a child account override change wasn't reflected in the
  // department row because the old optimistic value was still shadowing the fresh total.
  useEffect(() => {
    setOptimisticValues({});
  }, [monthlyTotals]);

  useEffect(() => {
    if (editingMonth !== null && editInputRef.current) {
      hasCommittedRef.current = false;
      editInputRef.current.focus();
      if (editStartModeRef.current === 'key') {
        const len = editInputRef.current.value.length;
        editInputRef.current.setSelectionRange(len, len);
      } else {
        editInputRef.current.select();
      }
    }
  }, [editingMonth]);

  const parseDeptValue = (str: string): number | null => {
    const cleaned = str.replace(/[$,\s]/g, '');
    if (cleaned === '' || cleaned === '-') return null;
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  };

  const commitDeptEdit = (monthIndex: number, direction?: 'left' | 'right' | 'up' | 'down') => {
    if (hasCommittedRef.current) return;
    hasCommittedRef.current = true;
    const newValue = parseDeptValue(editValue);
    setEditingMonth(null);
    // Navigate to adjacent month cell after closing input
    const delta = direction === 'left' || direction === 'up' ? -1
                : direction === 'right' || direction === 'down' ? 1
                : 0;
    if (delta !== 0) {
      const nextIndex = monthIndex + delta;
      setTimeout(() => {
        const td = cellTdRefs.current[nextIndex];
        if (td) {
          td.focus();
          // If typing starts immediately on the focused td we'll let its onKeyDown handle it
        }
      }, 0);
    } else {
      // No direction — return focus to current td
      setTimeout(() => cellTdRefs.current[monthIndex]?.focus(), 0);
    }
    if (newValue === null || !onCellSave) return;
    setOptimisticValues(prev => ({ ...prev, [monthIndex]: newValue }));
    onCellSave(monthIndex, newValue).catch(() => {
      toast.error('Failed to apply department overrides');
      setOptimisticValues(prev => { const n = { ...prev }; delete n[monthIndex]; return n; });
    });
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleSelection?.();
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEditDriver?.();
  };

  const handleSpreadClick = (e: React.MouseEvent, monthIndex: number) => {
    e.stopPropagation();
    onMonthlySpread?.(monthIndex);
  };

  const handleClearClick = (e: React.MouseEvent, monthIndex: number) => {
    e.stopPropagation();
    onClearOverrides?.(monthIndex);
  };

  return (
    <tr
      className={`fv3-r-dept bg-slate-25 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100 group${isExpanded ? ' fv3-r-dept--open' : ''}`}
      onClick={(e) => {
        // An editable value cell is a cell, not a disclosure control: clicking
        // it focuses it for typing (double-click opens the editor) and must not
        // fold the department. Guarded here as well as by stopPropagation on
        // the cell, so no event-order quirk can turn an edit into a toggle.
        const td = (e.target as HTMLElement).closest?.('td');
        if (td && td.dataset.deptValue === 'editable') return;
        onToggle();
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onContextMenu={onRevenueForecastSetup ? (e) => { e.preventDefault(); e.stopPropagation(); onRevenueForecastSetup(); } : undefined}
      title={onRevenueForecastSetup ? 'Right-click for Revenue Forecast' : undefined}
    >
      {isBulkMode && (
        <td className="sticky left-0 bg-white py-2 px-2 z-20" onClick={handleCheckboxClick}>
          {selectionState === 'all' ? (
            <CheckSquare size={16} className="cursor-pointer" style={{ color: 'var(--lodge-orange-400)' }} />
          ) : selectionState === 'some' ? (
            <MinusSquare size={16} className="cursor-pointer" style={{ color: 'var(--lodge-orange-300)' }} />
          ) : (
            <Square size={16} className="cursor-pointer" style={{ color: 'var(--lodge-onyx-400)' }} />
          )}
        </td>
      )}
      <td className={`fv3-label-cell ${isBulkMode ? '' : 'sticky left-0'} bg-white py-2 px-4 pl-8 font-medium text-slate-700 z-20`}>
        <div className="flex items-center gap-2">
          {isExpanded ? <ChevronDown size={14} className="flex-none" /> : <ChevronRight size={14} className="flex-none" />}
          {department}
          {hasRevenueForecast && (
            <span className="flex items-center gap-1 text-[10px] font-semibold text-green-600 bg-green-50 border border-green-200 rounded px-1.5 py-0.5">
              <TrendingUp size={10} />
              Forecast
            </span>
          )}
          {driverType ? (
            <>
              <DriverBadge driver={driverType as any} size="sm" />
              <button
                onClick={handleEditClick}
                className={`p-1 hover:bg-slate-100 rounded transition-all ${
                  isHovered && onEditDriver ? 'opacity-100' : 'opacity-0'
                }`}
                title="Edit department driver"
              >
                <Edit3 size={12} style={{ color: 'var(--lodge-onyx-400)' }} />
              </button>
            </>
          ) : onEditDriver ? (
            <button
              onClick={handleEditClick}
              className={`flex items-center gap-1 text-xs transition-all ${
                isHovered ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ color: 'var(--lodge-orange-400)' }}
            >
              <Edit3 size={12} />
              <span>Set driver...</span>
            </button>
          ) : null}
        </div>
      </td>
      {MONTH_LABELS.map((month, index) => {
        const monthKey = month.toLowerCase();
        const isColExpanded = isColumnExpanded?.(index);

        if (isColExpanded && monthlyExpandedTotals) {
          const expanded = monthlyExpandedTotals[monthKey];
          const rooms = occupiedRoomsPerMonth?.[monthKey];
          const rev = revenuePerMonth?.[monthKey];
          const ctx: ColumnRenderContext = {
            expanded: expanded || null,
            value: 0,
            isExpense,
            monthIndex: index,
            actualOccupiedRooms: rooms?.actual,
            forecastOccupiedRooms: rooms?.forecast,
            actualTotalRevenue: rev?.actual ? rev.actual * -1 : 0,
            budgetTotalRevenue: rev?.budget || 0,
            lyBudgetTotalRevenue: rev?.lyBudget || 0, forecastTotalRevenue: rev?.forecast || 0,
            currency,
          };
          return (
            <React.Fragment key={month}>
              <ExpandedCellsRenderer
                columns={activeColumns}
                ctx={ctx}
                cellClassName="py-2 px-2 font-medium text-slate-600"
                renderOverride={(col, value) => {
                  // Forecast column gets spread/clear buttons
                  if (col.key === 'forecast') {
                    return (
                      <td key={col.key} className="text-right py-2 px-2 font-medium text-slate-600 tabular-nums">
                        <div className="flex items-center justify-end gap-1">
                          {(onMonthlySpread || onClearOverrides) && (
                            <div className={`flex items-center gap-0.5 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                              {onMonthlySpread && (
                                <button
                                  onClick={(e) => handleSpreadClick(e, index)}
                                  className="p-0.5 rounded transition-colors"
                                  style={{ color: 'var(--lodge-onyx-300)' }}
                                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-100)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-orange-400)'; }}
                                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = ''; (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-300)'; }}
                                  title="Spread department amount to sub-accounts"
                                >
                                  <Sliders size={11} />
                                </button>
                              )}
                              {onClearOverrides && childOverrideMonths?.has(monthKey) && (
                                <button
                                  onClick={(e) => handleClearClick(e, index)}
                                  className="p-0.5 rounded hover:bg-red-100 text-slate-300 hover:text-red-500 transition-colors"
                                  title="Clear sub-account overrides for this month"
                                >
                                  <Trash2 size={11} />
                                </button>
                              )}
                            </div>
                          )}
                          <RecalculatingValue value={formatCell(value, true, currency)} isRecalculating={false} />
                        </div>
                      </td>
                    );
                  }
                  return undefined;
                }}
              />
            </React.Fragment>
          );
        }

        const value = monthlyTotals[monthKey] || 0;
        const optimistic = optimisticValues[index] ?? null;
        const rawDisplay = optimistic !== null ? optimistic : value;
        const displayValue = rawDisplay;
        const hasOverrides = childOverrideMonths?.has(monthKey);
        const canEdit = !!onCellSave && !!(isMonthEditable?.(index));

        if (editingMonth === index) {
          return (
            <td key={month} className="text-right py-1 px-1" onClick={(e) => e.stopPropagation()}>
              <input
                ref={editInputRef}
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  switch (e.key) {
                    case 'Enter':     e.preventDefault(); commitDeptEdit(index, 'right'); break;
                    case 'ArrowDown': e.preventDefault(); commitDeptEdit(index, 'right'); break;
                    case 'ArrowUp':   e.preventDefault(); commitDeptEdit(index, 'left');  break;
                    case 'Tab':       e.preventDefault(); commitDeptEdit(index, e.shiftKey ? 'left' : 'right'); break;
                    case 'Escape':
                      hasCommittedRef.current = true;
                      setEditingMonth(null);
                      setTimeout(() => cellTdRefs.current[index]?.focus(), 0);
                      break;
                  }
                }}
                onBlur={() => commitDeptEdit(index)}
                className="w-full px-2 py-1 text-right text-sm font-semibold rounded focus:outline-none tabular-nums"
                style={{ border: '1px solid var(--lodge-orange-400)' }}
              />
            </td>
          );
        }

        return (
          <td
            key={month}
            ref={(el) => { cellTdRefs.current[index] = el; }}
            tabIndex={canEdit ? 0 : undefined}
            data-dept-value={canEdit ? 'editable' : 'readonly'}
            onClick={(e) => { if (canEdit) e.stopPropagation(); }}
            onDoubleClick={canEdit ? (e) => {
              e.stopPropagation();
              editStartModeRef.current = 'enter';
              setEditValue(rawDisplay.toString());
              setEditingMonth(index);
            } : undefined}
            onKeyDown={canEdit ? (e) => {
              if (e.key === 'Enter' || e.key === 'F2') {
                e.preventDefault();
                editStartModeRef.current = 'enter';
                setEditValue(rawDisplay.toString());
                setEditingMonth(index);
              } else if (/^[\d.\-]$/.test(e.key) && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                editStartModeRef.current = 'key';
                setEditValue(e.key);
                setEditingMonth(index);
              } else if (e.key === 'ArrowRight' || (e.key === 'Tab' && !e.shiftKey)) {
                e.preventDefault();
                cellTdRefs.current[index + 1]?.focus();
              } else if (e.key === 'ArrowLeft' || (e.key === 'Tab' && e.shiftKey)) {
                e.preventDefault();
                cellTdRefs.current[index - 1]?.focus();
              }
            } : undefined}
            className={`text-right py-2 px-3 font-medium tabular-nums relative ${
              canEdit ? 'cursor-default focus:outline-none' : 'text-slate-600'
            } ${hasOverrides ? 'bg-amber-50' : ''}`}
            title={insetActive && hasOverrides ? 'Sub-accounts overridden this month' : undefined}
            onMouseEnter={canEdit ? e => { if (!hasOverrides) (e.currentTarget as HTMLTableCellElement).style.background = 'var(--lodge-orange-50)'; } : undefined}
            onMouseLeave={canEdit ? e => { (e.currentTarget as HTMLTableCellElement).style.background = ''; } : undefined}
            onFocus={canEdit ? e => { (e.currentTarget as HTMLTableCellElement).style.boxShadow = 'inset 0 0 0 2px var(--lodge-orange-400)'; } : undefined}
            onBlur={canEdit ? e => { (e.currentTarget as HTMLTableCellElement).style.boxShadow = ''; } : undefined}
          >
            {hasOverrides && (
              <div className="fv3-mark fv3-mark-override absolute top-0 right-0 w-0 h-0 border-t-[8px] border-t-amber-400 border-l-[8px] border-l-transparent" />
            )}
            <div className="flex items-center justify-end gap-1">
              {(onMonthlySpread || onClearOverrides) && (
                <div className={`flex items-center gap-0.5 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                  {onMonthlySpread && (
                    <button
                      onClick={(e) => handleSpreadClick(e, index)}
                      className="p-0.5 rounded transition-colors"
                      style={{ color: 'var(--lodge-onyx-300)' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-100)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-orange-400)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = ''; (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-300)'; }}
                      title="Spread department amount to sub-accounts"
                    >
                      <Sliders size={11} />
                    </button>
                  )}
                  {onClearOverrides && (
                    <button
                      onClick={(e) => handleClearClick(e, index)}
                      className="p-0.5 rounded hover:bg-red-100 text-slate-300 hover:text-red-500 transition-colors"
                      title="Clear sub-account overrides for this month"
                    >
                      <Trash2 size={11} />
                    </button>
                  )}
                </div>
              )}
              <div className="flex flex-col items-end leading-tight">
                <RecalculatingValue value={formatCell(displayValue, true, currency)} isRecalculating={false} />
              </div>
            </div>
          </td>
        );
      })}
      {isColumnExpanded?.(TOTAL_COLUMN_INDEX) && monthlyExpandedTotals ? (
        <ExpandedCellsRenderer
          columns={activeColumns}
          ctx={buildTotalContext({ expandedByMonth: monthlyExpandedTotals, value: total, isExpense, occupiedRoomsPerMonth, revenuePerMonth, currency })}
          cellClassName="py-2 px-2 font-medium text-slate-700"
        />
      ) : (
        <td className="text-right py-2 px-4 font-medium text-slate-700 tabular-nums">
          <RecalculatingValue value={formatCell(total, true, currency)} isRecalculating={false} />
        </td>
      )}
    </tr>
  );
}

interface AccountRowProps {
  account: ForecastAccount;
  isSelected: boolean;
  onSelect: () => void;
  // Driver-level monthly overrides: expand the account to its driver sub-rows
  isDriverExpandable?: boolean;
  isDriverExpanded?: boolean;
  onToggleDriverExpand?: () => void;
  onCellSave?: (chartOfAccountId: number, month: number, value: number) => Promise<void>;
  rowIndex: number;
  onCellNavigate?: (rowIndex: number, colIndex: number, direction: 'up' | 'down' | 'left' | 'right') => void;
  cellRefs?: React.MutableRefObject<Map<string, HTMLTableCellElement>>;
  isReadOnly?: boolean;
  isMonthEditable?: (monthIndex: number) => boolean;
  activeMonth?: number | null;
  isBulkMode?: boolean;
  isBulkSelected?: boolean;
  onToggleBulkSelection?: () => void;
  isColumnExpanded?: (index: number) => boolean;
  isExpense?: boolean;
  recalculatingCell?: { chartOfAccountId: number; monthIndex: number } | null;
  onContextMenu?: (e: React.MouseEvent, account: ForecastAccount, monthIndex: number) => void;
  onGLDetailClick?: (chartOfAccountId: number, accountName: string, accountNumber: string, month: number, columnType: 'Actual' | 'LY') => void;
  // Grid selection props
  isCellSelected?: (rowIndex: number, colIndex: number) => boolean;
  getSelectionStyle?: (rowIndex: number, colIndex: number) => React.CSSProperties;
  onCellMouseDown?: (rowIndex: number, colIndex: number, e: React.MouseEvent) => void;
  onCellMouseEnter?: (rowIndex: number, colIndex: number) => void;
  onGridKeyDown?: (e: React.KeyboardEvent, rowIndex: number, colIndex: number) => void;
  selectedRange?: CellRange | null;
  getCopyIndicatorEdges?: (rowIndex: number, colIndex: number) => { top: boolean; bottom: boolean; left: boolean; right: boolean } | null;
  activeColumns?: ExpandedColumnDef[];
  occupiedRoomsPerMonth?: Record<string, { actual: number; forecast: number }>;
  revenuePerMonth?: Record<string, ExpandedTotals>;
  actualRollInEnabled?: boolean;
  currency?: string | null;
}

function AccountRow({
  account,
  isSelected,
  onSelect,
  isDriverExpandable = false,
  isDriverExpanded = false,
  onToggleDriverExpand,
  onCellSave,
  rowIndex,
  onCellNavigate,
  cellRefs,
  isReadOnly = false,
  isMonthEditable,
  activeMonth,
  isBulkMode = false,
  isBulkSelected = false,
  onContextMenu,
  onToggleBulkSelection,
  isColumnExpanded,
  isExpense = false,
  recalculatingCell = null,
  onGLDetailClick,
  isCellSelected,
  getSelectionStyle,
  onCellMouseDown,
  onCellMouseEnter,
  onGridKeyDown,
  getCopyIndicatorEdges,
  activeColumns = [],
  occupiedRoomsPerMonth,
  revenuePerMonth,
  actualRollInEnabled = true,
  currency,
}: AccountRowProps) {
  const gridDisplay = useGridDisplay();
  const { insetActive } = useTheme() as { insetActive: boolean };
  // Create callback for cell navigation
  const handleCellNavigate = useCallback((colIndex: number) => (direction: 'up' | 'down' | 'left' | 'right') => {
    onCellNavigate?.(rowIndex, colIndex, direction);
  }, [rowIndex, onCellNavigate]);

  // Register cell ref
  const registerCellRef = useCallback((colIndex: number) => (el: HTMLTableCellElement | null) => {
    if (el) {
      el.dataset.gridRow = String(rowIndex);
      el.dataset.gridCol = String(colIndex);
      cellRefs?.current.set(`${rowIndex}-${colIndex}`, el);
    }
  }, [rowIndex, cellRefs]);

  // Keyboard handling for read-only (closed-month / locked) cells: they can't
  // be edited but must still take focus, join ranges, copy, and arrow-navigate —
  // otherwise arrows fall through to the scroll container and scroll the grid.
  const readOnlyCellKeyDown = useCallback((colIndex: number) => (e: React.KeyboardEvent) => {
    onGridKeyDown?.(e, rowIndex, colIndex);
    if (e.defaultPrevented) return;
    const dir = ({ ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' } as const)[e.key];
    if (dir) { e.preventDefault(); onCellNavigate?.(rowIndex, colIndex, dir); return; }
    if (e.key === 'Tab') { e.preventDefault(); onCellNavigate?.(rowIndex, colIndex, e.shiftKey ? 'left' : 'right'); }
  }, [rowIndex, onGridKeyDown, onCellNavigate]);
  const readOnlyFocusRing = {
    onFocus: (e: React.FocusEvent<HTMLTableCellElement>) => { if (!e.currentTarget.dataset.selected) e.currentTarget.style.boxShadow = 'inset 0 0 0 2px var(--lodge-orange-400)'; },
    onBlur: (e: React.FocusEvent<HTMLTableCellElement>) => { e.currentTarget.style.boxShadow = ''; },
  };

  const handleRowClick = () => {
    if (isBulkMode) {
      onToggleBulkSelection?.();
    } else {
      onSelect();
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleBulkSelection?.();
  };

  return (
    <tr
      className={`fv3-r-account${isSelected && !isBulkMode ? ' fv3-r-selected' : ''} border-b border-slate-50 cursor-pointer transition-colors`}
      data-account-id={account.chartOfAccountId}
      style={{
        background: (isSelected && !isBulkMode) ? 'var(--lodge-orange-50)'
          : isBulkSelected ? 'color-mix(in srgb, var(--lodge-orange-50) 50%, transparent)'
          : undefined
      }}
      onClick={handleRowClick}
    >
      {isBulkMode && (
        <td
          className="sticky left-0 py-2 px-2 z-20"
          style={{ background: isBulkSelected ? 'color-mix(in srgb, var(--lodge-orange-50) 50%, var(--lodge-white))' : 'var(--lodge-white)' }}
          onClick={handleCheckboxClick}
        >
          {isBulkSelected ? (
            <CheckSquare size={16} className="cursor-pointer" style={{ color: 'var(--lodge-orange-400)' }} />
          ) : (
            <Square size={16} className="cursor-pointer" style={{ color: 'var(--lodge-onyx-400)' }} />
          )}
        </td>
      )}
      <td
        className={`fv3-acct-cell ${isBulkMode ? '' : 'sticky left-0'} py-2 px-4 pl-14 z-20`}
        style={{
          // Composite the (translucent) selection tint over the opaque card
          // ONCE — the tr already paints the tint, so a plain translucent bg
          // here would stack to a double-strength muddy band, and the sticky
          // cell needs an opaque base anyway so columns can't bleed through.
          background: (isSelected && !isBulkMode)
            ? 'linear-gradient(var(--lodge-orange-50), var(--lodge-orange-50)), var(--lodge-white)'
            : isBulkSelected ? 'color-mix(in srgb, var(--lodge-orange-50) 50%, var(--lodge-white))' : 'var(--lodge-white)',
          // Brand spine marks the selected account
          boxShadow: (isSelected && !isBulkMode) ? 'inset 3px 0 0 var(--accent, var(--fv3-accent, #FF7A00))' : undefined,
        }}
      >
        <div className="fv3-acct-label flex flex-col">
          <div className="fv3-acct-main flex items-center gap-1.5">
            {isDriverExpandable && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleDriverExpand?.(); }}
                className="w-5 h-5 -ml-5 flex items-center justify-center rounded transition-colors hover:bg-slate-100"
                style={{ color: isDriverExpanded ? 'var(--lodge-orange-400)' : 'var(--lodge-onyx-400)' }}
                title={isDriverExpanded ? 'Hide drivers' : 'Show drivers — override a rate for one month while the driver stays live'}
                aria-expanded={isDriverExpanded}
              >
                {isDriverExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
            )}
            <span className="fv3-acct-name text-slate-700" title={insetActive ? [account.accountNumber, account.accountName].filter(Boolean).join(' · ') : undefined}>{account.accountName}</span>
            {/* Adjustment badge - shows when account has active adjustments */}
            {account.hasAdjustments && (
              <span
                className="fv3-pill inline-flex items-center px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-medium rounded cursor-pointer hover:bg-amber-200 transition-colors"
                title={`${account.adjustmentsCount || 1} active adjustment${(account.adjustmentsCount || 1) > 1 ? 's' : ''}`}
              >
                +adj
              </span>
            )}
          </div>
          <div className="fv3-acct-meta flex items-center gap-1.5 mt-0.5">
            {account.accountNumber && (
              <span className="fv3-acct-number text-xs text-slate-400">{account.accountNumber}</span>
            )}
            {(() => {
              const inherits = account.inheritsFromDepartment;
              const deptDriver = account.originalDepartmentDriverType || account.departmentDriverType;
              const effectiveDriver = account.driverType;
              if (inherits && deptDriver && effectiveDriver && deptDriver !== effectiveDriver) {
                return (
                  <div className="flex items-center gap-1">
                    <DriverBadge driver={deptDriver as any} size="sm" />
                    <DriverBadge driver={effectiveDriver} size="sm" />
                  </div>
                );
              }
              if (inherits && deptDriver) return <DriverBadge driver={deptDriver as any} size="sm" />;
              if (effectiveDriver) return <DriverBadge driver={effectiveDriver} size="sm" />;
              return null;
            })()}
            {account.driverError && (
              <span
                className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5"
                title={account.driverError}
              >
                driver not allowed
              </span>
            )}
          </div>
        </div>
      </td>
      {MONTH_LABELS.map((month, colIndex) => {
        const monthKey = month.toLowerCase();
        const value = account.monthlyValues[monthKey as keyof typeof account.monthlyValues];
        const isCellEditable = isMonthEditable ? isMonthEditable(colIndex) : !isReadOnly;
        const isActiveMonthCol = activeMonth === colIndex + 1;
        const hasOverride = shouldShowOverrideHighlight(account, monthKey, actualRollInEnabled);
        const hasDailyValues = account.monthlyDailyValues?.[monthKey] === true;
        const dailyMode = hasDailyValues ? (account.monthlyDailyMode?.[monthKey] ?? 'legacy') : null;
        const pinnedCount = account.monthlyPinnedCounts?.[monthKey] ?? 0;
        const isBlendedMonth = dailyMode === 'blended';
        const isColExpanded = isColumnExpanded?.(colIndex);

        // Context menu handler for this cell
        const handleCellContextMenu = (e: React.MouseEvent) => {
          onContextMenu?.(e, account, colIndex);
        };

        // Grid selection props for this cell
        const cellSelectionStyle = getSelectionStyle?.(rowIndex, colIndex) ?? {};
        const cellGridMouseDown = onCellMouseDown ? (e: React.MouseEvent) => {
          if (e.button !== 0) return; // Right/middle-click must not select the row or open the detail pane (context menu handles it)
          if (!isBulkMode) onSelect(); // Switch detail panel to this account when clicking a cell
          onCellMouseDown(rowIndex, colIndex, e);
        } : undefined;
        const cellGridMouseEnter = onCellMouseEnter ? () => onCellMouseEnter(rowIndex, colIndex) : undefined;
        const cellGridKeyDown = onGridKeyDown ? (e: React.KeyboardEvent) => onGridKeyDown(e, rowIndex, colIndex) : undefined;
        const cellCopyEdges = getCopyIndicatorEdges?.(rowIndex, colIndex) ?? null;

        // Handle expanded column view
        if (isColExpanded && account.monthlyExpanded) {
          const expanded = account.monthlyExpanded[monthKey];
          const forecast = expanded?.forecast ?? value;
          const rooms = occupiedRoomsPerMonth?.[monthKey];
          const rev = revenuePerMonth?.[monthKey];
          const ctx: ColumnRenderContext = {
            expanded: expanded || null,
            value,
            isExpense,
            monthIndex: colIndex,
            actualOccupiedRooms: rooms?.actual,
            forecastOccupiedRooms: rooms?.forecast,
            actualTotalRevenue: rev?.actual ? rev.actual * -1 : 0,
            budgetTotalRevenue: rev?.budget || 0,
            lyBudgetTotalRevenue: rev?.lyBudget || 0, forecastTotalRevenue: rev?.forecast || 0,
            currency,
          };
          return (
            <React.Fragment key={month}>
              <ExpandedCellsRenderer
                columns={activeColumns}
                ctx={ctx}
                cellClassName="py-2.5 px-2 text-slate-700"
                renderOverride={(col, displayValue) => {
                  // Clickable columns (Actual, LY) for GL detail
                  if (col.isClickable && onGLDetailClick) {
                    return (
                      <td key={col.key} className="fv3-xcell text-right py-2.5 px-2 tabular-nums text-slate-700">
                        <button
                          className="cursor-pointer hover:underline hover:text-blue-600 transition-colors"
                          onClick={(e) => { e.stopPropagation(); onGLDetailClick(account.chartOfAccountId, account.accountName, account.accountNumber, colIndex + 1, col.clickType as 'Actual' | 'LY'); }}
                        >
                          {expandedCellText(col, displayValue, ctx, gridDisplay)}
                        </button>
                      </td>
                    );
                  }
                  // Editable forecast column
                  if (col.isEditable) {
                    if (onCellSave && isCellEditable) {
                      return (
                        <EditableCellWithRef
                          key={col.key}
                          value={forecast}
                          chartOfAccountId={account.chartOfAccountId}
                          monthIndex={colIndex}
                          onSave={onCellSave}
                          onNavigate={handleCellNavigate(colIndex)}
                          registerRef={registerCellRef(colIndex)}
                          hasOverride={hasOverride}
                          hasDailyValues={hasDailyValues}
                          dailyMode={dailyMode}
                          pinnedCount={pinnedCount}
                          isRecalculating={recalculatingCell?.chartOfAccountId === account.chartOfAccountId && recalculatingCell?.monthIndex === colIndex}
                          onContextMenu={handleCellContextMenu}
                          selectionStyle={cellSelectionStyle}
                          onGridMouseDown={cellGridMouseDown}
                          onGridMouseEnter={cellGridMouseEnter}
                          onGridKeyDown={cellGridKeyDown}
                          copyIndicatorEdges={cellCopyEdges}
                          currency={currency}
                        />
                      );
                    }
                    const hasSelStyleX = cellSelectionStyle && Object.keys(cellSelectionStyle).length > 0;
                    return (
                      <td
                        key={col.key}
                        ref={registerCellRef(colIndex)}
                        tabIndex={0}
                        data-selected={hasSelStyleX ? '1' : undefined}
                        className={`fv3-xcell text-right py-2.5 px-2 tabular-nums relative text-slate-700 cursor-default focus:outline-none ${hasOverride && !hasSelStyleX ? 'bg-amber-50' : ''} ${hasDailyValues && !isBlendedMonth && !hasSelStyleX ? (/room/i.test(account.department || '') ? 'bg-green-50' : 'bg-blue-50') : ''}`}
                        style={hasSelStyleX ? cellSelectionStyle : undefined}
                        onContextMenu={handleCellContextMenu}
                        onMouseDown={cellGridMouseDown}
                        onMouseEnter={cellGridMouseEnter}
                        onKeyDown={readOnlyCellKeyDown(colIndex)}
                        {...readOnlyFocusRing}
                        title={isBlendedMonth ? `${pinnedCount} pinned day${pinnedCount === 1 ? '' : 's'} + live driver` : insetActive && hasOverride ? 'Override value (not calculated by driver)' : insetActive && hasDailyValues ? 'Daily values · right-click for options' : undefined}
                      >
                        {cellCopyEdges && <CopyIndicator edges={cellCopyEdges} />}
                        {hasOverride && (
                          <div className="fv3-mark fv3-mark-override absolute top-0 right-0 w-0 h-0 border-t-[8px] border-t-amber-400 border-l-[8px] border-l-transparent" />
                        )}
                        {isBlendedMonth && !hasOverride && (
                          <Pin size={9} className="fv3-mark-pin absolute top-0.5 right-0.5 text-amber-500 fill-amber-400" />
                        )}
                        {hasDailyValues && !isBlendedMonth && !hasOverride && (
                          <div className={`fv3-mark fv3-mark-daily absolute top-0 right-0 w-0 h-0 border-t-[8px] border-l-[8px] border-l-transparent ${/room/i.test(account.department || '') ? 'border-t-green-400' : 'border-t-blue-400'}`} />
                        )}
                        <RecalculatingValue
                          value={formatCell(displayValue, true, currency)}
                          isRecalculating={recalculatingCell?.chartOfAccountId === account.chartOfAccountId && recalculatingCell?.monthIndex === colIndex}
                          className="inline-block px-1 rounded"
                        />
                      </td>
                    );
                  }
                  return undefined;
                }}
              />
            </React.Fragment>
          );
        }

        // monthlyValues already use display signs: expense credits must stay negative.
        const displayValue = value;

        // Only render editable cells if onCellSave is provided AND cell is editable
        if (onCellSave && isCellEditable) {
          return (
            <EditableCellWithRef
              key={month}
              value={value}
              chartOfAccountId={account.chartOfAccountId}
              monthIndex={colIndex}
              onSave={onCellSave}
              onNavigate={handleCellNavigate(colIndex)}
              registerRef={registerCellRef(colIndex)}
              hasOverride={hasOverride}
              hasDailyValues={hasDailyValues}
              dailyMode={dailyMode}
              pinnedCount={pinnedCount}
              isRecalculating={recalculatingCell?.chartOfAccountId === account.chartOfAccountId && recalculatingCell?.monthIndex === colIndex}
              onContextMenu={handleCellContextMenu}
              selectionStyle={cellSelectionStyle}
              onGridMouseDown={cellGridMouseDown}
              onGridMouseEnter={cellGridMouseEnter}
              onGridKeyDown={cellGridKeyDown}
              copyIndicatorEdges={cellCopyEdges}
              currency={currency}
            />
          );
        }

        // Read-only cell with status-based styling
        const hasSelStyle = cellSelectionStyle && Object.keys(cellSelectionStyle).length > 0;
        return (
          <td
            key={month}
            ref={registerCellRef(colIndex)}
            tabIndex={0}
            data-selected={hasSelStyle ? '1' : undefined}
            onKeyDown={readOnlyCellKeyDown(colIndex)}
            {...readOnlyFocusRing}
            className={`text-right py-2.5 px-3 tabular-nums relative cursor-default focus:outline-none ${
              value < 0 && !isExpense ? 'text-red-600' : 'text-slate-700'
            } ${hasOverride && !hasSelStyle ? 'bg-amber-50' : ''} ${hasDailyValues && !isBlendedMonth && !hasOverride && !hasSelStyle ? 'bg-blue-50' : ''}`}
            style={hasSelStyle ? cellSelectionStyle : isActiveMonthCol && !hasOverride && !(hasDailyValues && !isBlendedMonth) ? { background: 'color-mix(in srgb, var(--lodge-orange-50) 50%, transparent)' } : undefined}
            title={hasOverride
              ? 'Override value (not calculated by driver)'
              : isBlendedMonth
                ? `${pinnedCount} pinned day${pinnedCount === 1 ? '' : 's'} + live driver - right-click for options`
                : hasDailyValues
                  ? 'Has daily values - right-click for options'
                  : undefined}
            onContextMenu={handleCellContextMenu}
            onMouseDown={cellGridMouseDown}
            onMouseEnter={cellGridMouseEnter}
          >
            {cellCopyEdges && <CopyIndicator edges={cellCopyEdges} />}
            {hasOverride && (
              <div className="fv3-mark fv3-mark-override absolute top-0 right-0 w-0 h-0 border-t-[8px] border-t-amber-400 border-l-[8px] border-l-transparent" />
            )}
            {isBlendedMonth && !hasOverride && (
              <Pin size={9} className="fv3-mark-pin absolute top-0.5 right-0.5 text-amber-500 fill-amber-400" />
            )}
            {hasDailyValues && !isBlendedMonth && !hasOverride && (
              <div className="fv3-mark fv3-mark-daily absolute top-0 right-0 w-0 h-0 border-t-[8px] border-t-blue-400 border-l-[8px] border-l-transparent" />
            )}
            <RecalculatingValue
              value={formatCell(displayValue, true, currency)}
              isRecalculating={recalculatingCell?.chartOfAccountId === account.chartOfAccountId && recalculatingCell?.monthIndex === colIndex}
              className="inline-block px-1 rounded"
            />
          </td>
        );
      })}
      {isColumnExpanded?.(TOTAL_COLUMN_INDEX) && account.monthlyExpanded ? (
        <ExpandedCellsRenderer
          columns={activeColumns}
          ctx={buildTotalContext({ expandedByMonth: account.monthlyExpanded, value: account.total, isExpense, occupiedRoomsPerMonth, revenuePerMonth, currency })}
          cellClassName="py-2.5 px-2 font-medium text-slate-800"
        />
      ) : (
        <td className="text-right py-2.5 px-4 font-medium text-slate-800 tabular-nums">
          {formatCell(account.total, true, currency)}
        </td>
      )}
    </tr>
  );
}

// Wrapper component to handle ref registration
interface EditableCellWithRefProps extends Omit<EditableCellProps, 'cellRef'> {
  registerRef: (el: HTMLTableCellElement | null) => void;
  hasOverride?: boolean;
  isRecalculating?: boolean;
}

function EditableCellWithRef({ registerRef, hasOverride, isRecalculating, ...props }: EditableCellWithRefProps) {
  const cellRef = useRef<HTMLTableCellElement>(null);

  useEffect(() => {
    registerRef(cellRef.current);
  }, [registerRef]);

  return <EditableCell {...props} cellRef={cellRef} hasOverride={hasOverride} isRecalculating={isRecalculating} />;
}

/**
 * One line of the GOP statement layout: a band heading (collapsible, carries
 * the band total), an intermediate profit line, or the bottom line. The
 * `line` is a field-wise sum of department totals (see sumStatementLine), so
 * the standard column renderers price it exactly as they would a department
 * — `isExpense` picks the cost-positive or profit-positive presentation.
 */
interface GopLineRowProps {
  label: string;
  variant: 'band' | 'line' | 'net';
  isExpanded?: boolean;
  onToggle?: () => void;
  line: { monthly: Record<string, number>; total: number; expanded: Record<string, ExpandedTotals> };
  isExpense: boolean;
  isBulkMode?: boolean;
  isColumnExpanded?: (index: number) => boolean;
  activeColumns?: ExpandedColumnDef[];
  occupiedRoomsPerMonth?: Record<string, { actual: number; forecast: number }>;
  revenuePerMonth?: Record<string, ExpandedTotals>;
  closedMonthSet?: Set<string>;
  currency?: string | null;
}

function GopLineRow({ label, variant, isExpanded = false, onToggle, line, isExpense, isBulkMode, isColumnExpanded, activeColumns = [], occupiedRoomsPerMonth, revenuePerMonth, closedMonthSet, currency }: GopLineRowProps) {
  const isBand = variant === 'band';
  const rowClass = variant === 'net'
    ? 'fv3-r-net bg-slate-100 border-t-2 border-slate-300'
    : variant === 'line'
      ? 'fv3-r-section bg-slate-50 border-t border-slate-200'
      : 'fv3-r-section bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors';
  const bg = variant === 'net' ? 'bg-slate-100' : 'bg-slate-50';
  const weight = variant === 'band' ? 'font-semibold' : 'font-bold';
  const pad = variant === 'net' ? 'py-3' : 'py-2.5';
  // Profit lines colour by sign like Net Income; band totals are amounts.
  const signClass = (v: number) => (isBand ? 'text-slate-800' : v < 0 ? 'text-red-600' : 'text-emerald-600');

  return (
    <tr className={rowClass} onClick={isBand ? onToggle : undefined}>
      {isBulkMode && <td className={`sticky left-0 ${bg} ${pad} px-2 z-20`} />}
      <td className={`fv3-label-cell ${isBulkMode ? '' : 'sticky left-0'} ${bg} ${pad} px-4 ${weight} text-slate-900 z-20`}>
        <div className="flex items-center gap-2">
          {isBand && (isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
          {label}
        </div>
      </td>
      {MONTH_LABELS.map((month, index) => {
        const monthKey = month.toLowerCase();
        const expanded = line.expanded[monthKey];
        if (isColumnExpanded?.(index)) {
          const rooms = occupiedRoomsPerMonth?.[monthKey];
          const rev = revenuePerMonth?.[monthKey];
          const ctx: ColumnRenderContext = {
            expanded: expanded || null,
            value: line.monthly[monthKey] || 0,
            isExpense,
            monthIndex: index,
            actualOccupiedRooms: rooms?.actual,
            forecastOccupiedRooms: rooms?.forecast,
            actualTotalRevenue: rev?.actual ? rev.actual * -1 : 0,
            budgetTotalRevenue: rev?.budget || 0,
            lyBudgetTotalRevenue: rev?.lyBudget || 0,
            forecastTotalRevenue: rev?.forecast || 0,
            currency,
          };
          return (
            <React.Fragment key={month}>
              <ExpandedCellsRenderer
                columns={activeColumns}
                ctx={ctx}
                cellClassName={`${pad} px-2 ${weight} text-slate-800`}
                colorModeOverride={isBand ? undefined : getNetIncomeColorMode}
              />
            </React.Fragment>
          );
        }
        const value = line.monthly[monthKey] || 0;
        const showVariance = closedMonthSet?.has(monthKey);
        const variance = showVariance ? getActualVsForecastVariance(expanded, isExpense) : 0;
        return (
          <td key={month} className={`text-right ${pad} px-3 ${weight} tabular-nums ${signClass(value)}`}>
            <div className="flex flex-col items-end leading-tight">
              <RecalculatingValue value={formatCell(value, true, currency)} isRecalculating={false} />
              {showVariance && (
                <span className={`text-[10px] font-medium ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatInlineVariance(variance, currency)}
                </span>
              )}
            </div>
          </td>
        );
      })}
      {isColumnExpanded?.(TOTAL_COLUMN_INDEX) ? (
        <ExpandedCellsRenderer
          columns={activeColumns}
          ctx={buildTotalContext({ expandedByMonth: line.expanded, value: line.total, isExpense, occupiedRoomsPerMonth, revenuePerMonth, currency })}
          cellClassName={`${pad} px-2 ${weight} text-slate-800`}
          colorModeOverride={isBand ? undefined : getNetIncomeColorMode}
        />
      ) : (
        <td className={`text-right ${pad} px-4 ${weight} tabular-nums ${signClass(line.total)}`}>
          <RecalculatingValue value={formatCell(line.total, true, currency)} isRecalculating={false} />
        </td>
      )}
    </tr>
  );
}

interface NetIncomeRowProps {
  netIncome: Record<string, number>;
  total: number;
  isBulkMode?: boolean;
  revenueExpandedTotals?: Record<string, ExpandedTotals>;
  expenseExpandedTotals?: Record<string, ExpandedTotals>;
  isColumnExpanded?: (index: number) => boolean;
  isRecalculating?: boolean;
  activeColumns?: ExpandedColumnDef[];
  closedMonthSet?: Set<string>;
  currency?: string | null;
}

function NetIncomeRow({ netIncome, total, isBulkMode, revenueExpandedTotals, expenseExpandedTotals, isColumnExpanded, isRecalculating = false, activeColumns = [], closedMonthSet, currency }: NetIncomeRowProps) {
  const gridDisplay = useGridDisplay();
  return (
    <tr className="fv3-r-net bg-slate-100 border-t-2 border-slate-300">
      {isBulkMode && (
        <td className="sticky left-0 bg-slate-100 py-3 px-2 z-20"></td>
      )}
      <td className={`${isBulkMode ? '' : 'sticky left-0'} bg-slate-100 py-3 px-4 font-bold text-slate-900 z-20`}>
        Net Income
      </td>
      {MONTH_LABELS.map((month, index) => {
        const monthKey = month.toLowerCase();
        const value = netIncome[monthKey as keyof typeof netIncome];
        const isColExpanded = isColumnExpanded?.(index);
        const showVariance = closedMonthSet?.has(monthKey);
        const forecastNet = (revenueExpandedTotals?.[monthKey]?.forecast || 0) - (expenseExpandedTotals?.[monthKey]?.forecast || 0);
        const variance = showVariance ? (value || 0) - forecastNet : 0;

        if (isColExpanded && revenueExpandedTotals && expenseExpandedTotals) {
          const revExp = revenueExpandedTotals[monthKey];
          const expExp = expenseExpandedTotals[monthKey];
          return (
            <React.Fragment key={month}>
              {activeColumns.map(col => {
                const value = getNetIncomeValue(col.key, revExp || null, expExp || null);
                const text = expandedCellText(col, value, { expanded: null, value, isExpense: false, monthIndex: index, currency }, gridDisplay);
                const colorClass = text === '\u2014' ? 'text-slate-400' : (value >= 0 ? 'text-emerald-600' : 'text-red-600');
                return (
                  <td key={col.key} className={`${col.isEditable ? '' : 'fv3-xcell '}text-right py-3 px-2 font-bold tabular-nums ${colorClass}`}>
                    <RecalculatingValue value={text} isRecalculating={false} />
                  </td>
                );
              })}
            </React.Fragment>
          );
        }

        return (
          <td
            key={month}
            className={`text-right py-3 px-3 font-bold tabular-nums ${
              value < 0 ? 'text-red-600' : 'text-emerald-600'
            }`}
          >
            <div className="flex flex-col items-end leading-tight">
              <RecalculatingValue value={formatCell(value, true, currency)} isRecalculating={false} />
              {showVariance && (
                <span className={`text-[10px] font-medium ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatInlineVariance(variance, currency)}
                </span>
              )}
            </div>
          </td>
        );
      })}
      {isColumnExpanded?.(TOTAL_COLUMN_INDEX) && revenueExpandedTotals && expenseExpandedTotals ? (
        (() => {
          const revYear = sumExpandedYear(revenueExpandedTotals);
          const expYear = sumExpandedYear(expenseExpandedTotals);
          return activeColumns.map(col => {
            const value = getNetIncomeValue(col.key, revYear, expYear);
            const text = expandedCellText(col, value, { expanded: null, value, isExpense: false, monthIndex: TOTAL_COLUMN_INDEX, currency }, gridDisplay);
            return (
              <td key={`total-${col.key}`} className={`${col.isEditable ? '' : 'fv3-xcell '}text-right py-3 px-2 font-bold tabular-nums ${text === '\u2014' ? 'text-slate-400' : value >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                <RecalculatingValue value={text} isRecalculating={false} />
              </td>
            );
          });
        })()
      ) : (
        <td
          className={`text-right py-3 px-4 font-bold tabular-nums ${
            total < 0 ? 'text-red-600' : 'text-emerald-600'
          }`}
        >
          <RecalculatingValue value={formatCell(total, true, currency)} isRecalculating={false} />
        </td>
      )}
    </tr>
  );
}

// Room Stat Source type
type RoomStatSource = 'pms' | 'budget' | 'actuals';

// Room Stats Section Component
interface RoomStatValues {
  availableRooms: number;
  occupiedRooms: number;
  occupancyRate: number;
}

interface RoomStatOverrideInfo {
  availableRooms: number | null;
  occupiedRooms: number | null;
  hasAvailableOverride: boolean;
  hasOccupiedOverride: boolean;
}

interface RoomStatsSectionProps {
  roomStats: {
    availableRooms: Record<string, number>;
    occupiedRooms: Record<string, number>;
    occupancyRate: Record<string, number>;
    expanded?: Record<string, {
      forecast: RoomStatValues;
      // Recorded rates ride along with the actuals so closed months can show
      // ADR/RevPAR from actual rooms revenue instead of the forecast's
      actual: RoomStatValues & { roomsRevenue?: number; adr?: number | null; revpar?: number | null };
      budget: RoomStatValues;
      lyBudget?: RoomStatValues;
      projected?: {
        availableRooms: number;
        occupiedRooms: number;
        occupiedRoomsSource?: string;
        // Folded in from the rooms revenue FORECAST (driver + pins + overrides)
        roomsRevenue?: number;
        adr?: number | null;
        revpar?: number | null;
      };
      overrides?: RoomStatOverrideInfo;
    }>;
  };
  isBulkMode?: boolean;
  isColumnExpanded?: (index: number) => boolean;
  isRecalculating?: boolean;
  expandedSections: Record<string, boolean>;
  toggleSection: (key: string) => void;
  roomStatSource?: RoomStatSource;
  onRoomStatSourceChange?: (source: RoomStatSource) => void;
  actualRollInEnabled?: boolean;
  closedMonthSet?: Set<string>;
  isReadOnly?: boolean;
  onRoomStatOverride?: (month: number, roomStatType: 'availableRooms' | 'occupiedRooms', value: number | null) => Promise<void>;
  year?: number;
  activeColumnCount?: number;
  activeColumns?: ExpandedColumnDef[];
  currency?: string | null;
  // Occupied (ORN) drill-down — stat accounts resolved like the raw export
  orgId?: number | null;
  locationId?: number | null;
  versionId?: number | string | null;
  revenueAccounts?: ForecastAccount[];
  isBudget?: boolean;
  onStatInputsApplied?: () => void | Promise<void>;
  onOpenStatMapping?: (dept?: { department: string; departmentId?: number }) => void;
}

function RoomStatsSection({
  roomStats,
  isBulkMode = false,
  isColumnExpanded,
  isRecalculating = false,
  expandedSections,
  toggleSection,
  roomStatSource = 'pms',
  onRoomStatSourceChange,
  actualRollInEnabled = false,
  closedMonthSet,
  isReadOnly = false,
  onRoomStatOverride,
  year,
  activeColumnCount = 7,
  activeColumns = [],
  currency,
  orgId,
  locationId,
  versionId,
  revenueAccounts = [],
  isBudget = false,
  onStatInputsApplied,
  onOpenStatMapping
}: RoomStatsSectionProps) {
  // Total column may be expanded into sub-columns; these rows span it as one cell.
  const totalColSpan = isColumnExpanded?.(TOTAL_COLUMN_INDEX) ? Math.max(1, activeColumnCount) : 1;
  const isRoomStatsExpanded = expandedSections['roomStats'] === true;
  const [showOrnBreakdown, setShowOrnBreakdown] = useState(false);
  const [showAdrBreakdown, setShowAdrBreakdown] = useState(false);
  const ornActualMonthKeys = useMemo(
    () => new Set<string>(actualRollInEnabled && !isBudget && closedMonthSet ? MONTHS.filter(m => closedMonthSet.has(m)) : []),
    [actualRollInEnabled, isBudget, closedMonthSet]
  );
  // One plan feeds both drill-downs (ORN room nights, ADR) and their edits.
  const statPlan = useStatAccountPlan({
    enabled: isRoomStatsExpanded && (showOrnBreakdown || showAdrBreakdown),
    orgId,
    locationId,
    versionId,
    year,
    roomStats,
    revenueAccounts,
    actualMonthKeys: ornActualMonthKeys,
    refreshKey: roomStats,
    isReadOnly,
    onApplied: onStatInputsApplied,
  });

  // Editing state for projected values
  const [editingCell, setEditingCell] = useState<{ month: number; type: 'availableRooms' | 'occupiedRooms' } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Focus input when editing starts
  React.useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  // Start editing a cell
  const handleStartEdit = (monthIndex: number, type: 'availableRooms' | 'occupiedRooms', currentValue: number) => {
    if (isReadOnly || !onRoomStatOverride) return;
    setEditingCell({ month: monthIndex + 1, type });
    setEditValue(currentValue.toString());
  };

  // Save the edited value
  const handleSave = async () => {
    if (!editingCell || !onRoomStatOverride) return;

    const numValue = parseInt(editValue, 10);
    if (isNaN(numValue) || numValue < 0) {
      // Invalid input - cancel edit
      setEditingCell(null);
      setEditValue('');
      return;
    }

    setIsSaving(true);
    try {
      await onRoomStatOverride(editingCell.month, editingCell.type, numValue);
    } finally {
      setIsSaving(false);
      setEditingCell(null);
      setEditValue('');
    }
  };

  // Cancel editing
  const handleCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Clear override
  const handleClearOverride = async (e: React.MouseEvent, monthIndex: number, type: 'availableRooms' | 'occupiedRooms') => {
    e.preventDefault();
    e.stopPropagation();
    if (isReadOnly || !onRoomStatOverride) return;

    setIsSaving(true);
    try {
      await onRoomStatOverride(monthIndex + 1, type, null);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle key press in input
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  // Render override indicator inline (not as a component to avoid remount issues)
  const renderOverrideIndicator = (originalValue: number) => (
    <span className="fv3-star ml-0.5" style={{ color: 'var(--lodge-orange-400)' }} title={`Original: ${formatNumber(originalValue)}`}>*</span>
  );

  // Render edit input inline (not as a component to avoid remount on state change)
  const renderEditInput = (monthIndex: number, type: 'availableRooms' | 'occupiedRooms', hasOverride: boolean) => (
    <div className="flex items-center gap-1">
      <input
        ref={inputRef}
        type="number"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        disabled={isSaving}
        className="w-full text-right bg-white rounded px-1 py-0.5 text-xs focus:outline-none"
        style={{ border: '1px solid var(--lodge-orange-300)' }}
        min="0"
      />
      {hasOverride && (
        <button
          onMouseDown={(e) => { e.preventDefault(); handleClearOverride(e, monthIndex, type); }}
          className="flex-shrink-0 px-1 py-0.5 text-[9px] font-medium rounded hover:bg-red-50 transition-colors whitespace-nowrap"
          style={{ color: 'var(--lodge-orange-500)', border: '1px solid var(--lodge-orange-200)' }}
          title="Clear override and restore original value"
        >
          Reset
        </button>
      )}
    </div>
  );

  // Check if we have any room stats data
  const hasData = Object.values(roomStats.availableRooms).some(v => v > 0) ||
                  Object.values(roomStats.occupiedRooms).some(v => v > 0);

  // Closed months display RECORDED actuals in the stat rows, the same way the
  // money rows above show actuals — a closed month's projection is only as
  // complete as the daily PMS stat feed, which can start mid-year, while the
  // GL stat accounts hold the whole month. Returns null when the month isn't
  // closed or has no GL stats, so the projection keeps the cell.
  // Variance lines ("vs fcst") deliberately keep using the forecast values.
  const rolledActual = (monthKey: string) => {
    if (!actualRollInEnabled || !closedMonthSet?.has(monthKey)) return null;
    const actual = roomStats.expanded?.[monthKey]?.actual;
    if (!actual || !(actual.availableRooms > 0) || !(actual.occupiedRooms > 0)) return null;
    return actual;
  };

  // Calculate totals
  const totalAvailable = MONTHS.reduce((sum, month) => {
    const rolled = rolledActual(month);
    return sum + ((rolled ? rolled.availableRooms : roomStats.availableRooms[month]) || 0);
  }, 0);
  const totalOccupied = MONTHS.reduce((sum, month) => {
    const rolled = rolledActual(month);
    if (rolled) return sum + (rolled.occupiedRooms || 0);
    const expanded = roomStats.expanded?.[month];
    const occupiedSource = expanded?.projected?.occupiedRoomsSource;
    const hasDriverProjection = occupiedSource === 'otb' || occupiedSource === 'rms';
    const hasOverride = expanded?.overrides?.hasOccupiedOverride || false;
    const originalValue = expanded?.forecast.occupiedRooms ?? roomStats.occupiedRooms[month] ?? 0;
    const effectiveValue = (hasOverride || hasDriverProjection)
      ? (expanded?.projected?.occupiedRooms ?? originalValue)
      : (roomStats.occupiedRooms[month] || 0);
    return sum + (effectiveValue || 0);
  }, 0);
  const avgOccupancy = totalAvailable > 0 ? (totalOccupied / totalAvailable) * 100 : 0;

  // Avg actual and budget occupancy from expanded data (for header row totals column)
  const expandedMonths = roomStats.expanded ? Object.values(roomStats.expanded) : [];

  // Does any month derive its stats from the rooms revenue forecast (OTB/RMS
  // driver, pins)? When it does, the source dropdown is only the FALLBACK for
  // undriven months — the occupancy section folds in from the forecast itself.
  const hasAnyDriverProjection = expandedMonths.some(e => {
    const src = e.projected?.occupiedRoomsSource;
    return src === 'otb' || src === 'rms';
  });

  // Full-year ADR / RevPAR: recorded rooms revenue for closed months, the
  // forecasted rooms revenue for the rest — matching the columns above.
  const totalRoomsForecastRevenue = MONTHS.reduce((sum, month) => {
    const rolled = rolledActual(month);
    if (rolled) return sum + (rolled.roomsRevenue || 0);
    return sum + (roomStats.expanded?.[month]?.projected?.roomsRevenue || 0);
  }, 0);
  const avgAdr = totalOccupied > 0 ? totalRoomsForecastRevenue / totalOccupied : null;
  const avgRevpar = totalAvailable > 0 ? totalRoomsForecastRevenue / totalAvailable : null;
  const totalActualAvailable = expandedMonths.reduce((sum, e) => sum + (e.actual.availableRooms || 0), 0);
  const totalActualOccupied = expandedMonths.reduce((sum, e) => sum + (e.actual.occupiedRooms || 0), 0);
  const avgActualOccupancy = totalActualAvailable > 0 ? (totalActualOccupied / totalActualAvailable) * 100 : 0;
  const totalBudgetAvailable = expandedMonths.reduce((sum, e) => sum + (e.budget.availableRooms || 0), 0);
  const totalBudgetOccupied = expandedMonths.reduce((sum, e) => sum + (e.budget.occupiedRooms || 0), 0);
  const avgBudgetOccupancy = totalBudgetAvailable > 0 ? (totalBudgetOccupied / totalBudgetAvailable) * 100 : 0;

  // Format number with commas
  const formatNumber = (num: number) => {
    return Math.round(num).toLocaleString();
  };

  // Format percentage
  const formatPercent = (num: number) => {
    return `${num.toFixed(1)}%`;
  };

  return (
    <>
      {/* Room Stats Header Row - Compact design showing just occupancy % */}
      <tr className="fv3-r-stat-head bg-slate-50 border-t border-slate-200 hover:bg-slate-100 transition-colors">
        {isBulkMode && <td className="sticky left-0 bg-slate-50 py-1.5 px-2 z-20" />}
        <td className={`${isBulkMode ? '' : 'sticky left-0'} bg-slate-50 py-1.5 px-4 z-20`}>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); toggleSection('roomStats'); }}
              className="flex items-center gap-1.5 text-sm font-medium transition-colors"
              style={{ color: 'var(--lodge-onyx-600)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-orange-500)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-600)'; }}
            >
              {isRoomStatsExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span>Occupancy</span>
            </button>
            {/* Stats fold in from the rooms revenue forecast — no source picker.
                Available rooms: budget first, else latest recorded capacity. */}
            {hasAnyDriverProjection && (
              <span
                className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-100 text-sky-700"
                title="Occupancy, ADR and RevPAR fold in from the rooms revenue forecast (driver, pins and overrides). Available rooms: budget first, else the latest recorded room count projected forward."
              >
                ROOMS FORECAST
              </span>
            )}
          </div>
        </td>
        {MONTH_LABELS.map((month, index) => {
          const monthKey = month.toLowerCase();
          const expanded = roomStats.expanded?.[monthKey];
          const hasArnOv = expanded?.overrides?.hasAvailableOverride || false;
          const hasOrnOv = expanded?.overrides?.hasOccupiedOverride || false;
          const occupiedSource = expanded?.projected?.occupiedRoomsSource;
          const hasDriverOrnProjection = occupiedSource === 'otb' || occupiedSource === 'rms';
          const hasAnyOverride = hasArnOv || hasOrnOv || hasDriverOrnProjection;
          const actualOccPct = expanded?.actual.occupancyRate ?? 0;
          const budgetOccPct = expanded?.budget.occupancyRate ?? 0;
          const showVariance = actualRollInEnabled && closedMonthSet?.has(monthKey);

          const forecastOccPct = (() => {
            if (!hasAnyOverride) {
              return expanded?.forecast.occupancyRate ?? roomStats.occupancyRate[monthKey] ?? 0;
            }
            const arn = hasArnOv
              ? (expanded?.projected?.availableRooms ?? expanded?.forecast.availableRooms ?? roomStats.availableRooms[monthKey] ?? 0)
              : (roomStats.availableRooms[monthKey] || 0);
            const orn = (hasOrnOv || hasDriverOrnProjection)
              ? (expanded?.projected?.occupiedRooms ?? expanded?.forecast.occupiedRooms ?? roomStats.occupiedRooms[monthKey] ?? 0)
              : (roomStats.occupiedRooms[monthKey] || 0);
            return arn > 0 ? (orn / arn) * 100 : 0;
          })();

          // Display actual occupancy for closed actual-roll-in months; otherwise forecast occupancy.
          const occPct = actualRollInEnabled && showVariance ? actualOccPct : forecastOccPct;
          const variancePct = actualOccPct - forecastOccPct;

          const isColExpanded = isColumnExpanded?.(index);

          if (isColExpanded) {
            // Render per-column cells so forecast value lands in the Forecast column
            return (
              <React.Fragment key={month}>
                {activeColumns.map(col => {
                  let val: number | null = null;
                  const monthClosed = closedMonthSet?.has(month.toLowerCase());
                  if (col.key === 'forecast') val = occPct;
                  else if (col.key === 'actual') val = monthClosed ? actualOccPct : null;
                  else if (col.key === 'budget') val = budgetOccPct;
                  const dash = val === null;
                  return (
                    <td key={col.key} className={`${col.isEditable ? '' : 'fv3-xcell '}text-right py-1.5 px-2 text-sm tabular-nums ${dash ? 'text-slate-400' : 'text-slate-500'} bg-slate-50`}>
                      {dash ? '\u2014' : formatPercent(val as number)}
                    </td>
                  );
                })}
              </React.Fragment>
            );
          }

          // Collapsed: stack Forecast / Actual / Budget
          return (
            <td key={month} className="text-right py-1 px-3 bg-slate-50">
              <div className="flex flex-col items-end">
                <span className="text-sm tabular-nums text-slate-500 leading-tight">{formatPercent(occPct)}</span>
                {showVariance ? (
                  <span className={`text-[10px] tabular-nums font-medium leading-tight ${variancePct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {variancePct >= 0 ? '+' : ''}{variancePct.toFixed(1)}% vs fcst
                  </span>
                ) : (
                  <span className="text-[10px] tabular-nums text-slate-400 leading-tight">Act {formatPercent(actualOccPct)}</span>
                )}
                <span className="text-[10px] tabular-nums text-slate-400 leading-tight">Bud {formatPercent(budgetOccPct)}</span>
              </div>
            </td>
          );
        })}
        <td colSpan={totalColSpan} className="text-right py-1 px-4 bg-slate-50">
          <div className="flex flex-col items-end">
            <span className="text-sm font-medium tabular-nums text-slate-600 leading-tight">{formatPercent(avgOccupancy)}</span>
            <span className="text-[10px] tabular-nums text-slate-400 leading-tight">Act {formatPercent(avgActualOccupancy)}</span>
            <span className="text-[10px] tabular-nums text-slate-400 leading-tight">Bud {formatPercent(avgBudgetOccupancy)}</span>
          </div>
        </td>
      </tr>

      {/* Available Rooms Row - with override support */}
      {isRoomStatsExpanded && (
        <tr className="fv3-r-stat bg-white hover:bg-slate-50 transition-colors group">
          {isBulkMode && <td className="sticky left-0 bg-white py-1 px-2 z-20 group-hover:bg-slate-50" />}
          <td className={`${isBulkMode ? '' : 'sticky left-0'} bg-white py-1 px-4 pl-8 text-xs text-slate-600 z-20 group-hover:bg-slate-50`}>
            Available Rooms
          </td>
          {MONTH_LABELS.map((month, index) => {
            const monthKey = month.toLowerCase();
            const expanded = roomStats.expanded?.[monthKey];
            const hasOverride = expanded?.overrides?.hasAvailableOverride || false;
            const originalValue = expanded?.forecast.availableRooms ?? roomStats.availableRooms[monthKey] ?? 0;
            const effectiveValue = hasOverride
              ? (expanded?.projected?.availableRooms ?? originalValue)
              : (roomStats.availableRooms[monthKey] || 0);
            // Closed months show the recorded ARN; the projection stays the
            // edit seed and keeps feeding the expanded Forecast column.
            const rolled = rolledActual(monthKey);
            const displayValue = rolled ? rolled.availableRooms : effectiveValue;
            const isColExpanded = isColumnExpanded?.(index);
            const isEditing = editingCell?.month === index + 1 && editingCell?.type === 'availableRooms';
            const canEdit = !isReadOnly && onRoomStatOverride;

            if (isColExpanded && expanded) {
              const actual = expanded.actual.availableRooms;
              const forecast = hasOverride ? effectiveValue : expanded.forecast.availableRooms;
              const budget = expanded.budget.availableRooms;
              const lyBudget = expanded.lyBudget?.availableRooms || 0;
              const variance = actual - forecast;


              // Map column keys to room stat values
              const colValues: Record<string, number> = {
                actual, forecast, budget, lyBudget, budgetVsLyBudget: forecast - lyBudget,
                var: variance, vsBud: actual - budget, budVsFcst: forecast - budget,
                lyActual: expanded.actual.availableRooms,
              };

              return (
                <React.Fragment key={month}>
                  {activeColumns.map(col => {
                    const val = colValues[col.key] ?? 0;
                    const isForecastCol = col.key === 'forecast';
                    const isVarianceCol = col.colorMode === 'variance';
                    const isEditingThis = isForecastCol && isEditing;
                    const canEditThis = isForecastCol && canEdit;

                    return (
                      <td
                        key={col.key}
                        className={`text-right py-1 px-2 text-xs tabular-nums ${
                          isForecastCol && hasOverride ? 'font-medium fv3-cell-override' : ''
                        } ${isVarianceCol ? (val >= 0 ? 'text-emerald-600' : 'text-red-600') : 'text-slate-600'} ${
                          canEditThis ? 'cursor-pointer hover:bg-slate-100' : ''
                        }`}
                        style={isForecastCol && hasOverride ? { color: 'var(--lodge-orange-500)' } : undefined}
                        onClick={() => canEditThis && handleStartEdit(index, 'availableRooms', forecast)}

                        title={isForecastCol ? (hasOverride ? `Original: ${formatNumber(originalValue)}` : canEdit ? 'Click to override' : undefined) : undefined}
                      >
                        {isEditingThis ? (
                          renderEditInput(index, 'availableRooms', hasOverride)
                        ) : (
                          <>
                            <RecalculatingValue value={formatNumber(val)} isRecalculating={false} />
                            {isForecastCol && hasOverride && renderOverrideIndicator(originalValue)}
                          </>
                        )}
                      </td>
                    );
                  })}
                </React.Fragment>
              );
            }

            return (
              <td
                key={month}
                className={`text-right py-1 px-3 text-xs tabular-nums ${hasOverride && !rolled ? 'font-medium fv3-cell-override' : ''} ${canEdit ? 'cursor-pointer hover:bg-slate-100' : ''}`}
                style={{ color: hasOverride && !rolled ? 'var(--lodge-orange-500)' : undefined }}
                onClick={() => canEdit && handleStartEdit(index, 'availableRooms', effectiveValue)}

                title={rolled ? `Recorded actual. Forecast: ${formatNumber(effectiveValue)}` : hasOverride ? `Original: ${formatNumber(originalValue)}` : canEdit ? 'Click to override' : undefined}
              >
                {isEditing ? (
                  renderEditInput(index, 'availableRooms', hasOverride)
                ) : (
                  <>
                    <RecalculatingValue value={formatNumber(displayValue)} isRecalculating={false} />
                    {hasOverride && !rolled && renderOverrideIndicator(originalValue)}
                  </>
                )}
              </td>
            );
          })}
          <td colSpan={totalColSpan} className="text-right py-1 px-4 text-xs font-medium tabular-nums text-slate-700">
            <RecalculatingValue value={formatNumber(totalAvailable)} isRecalculating={false} />
          </td>
        </tr>
      )}

      {/* Occupied Rooms Row - with override support */}
      {isRoomStatsExpanded && (
        <tr className="fv3-r-stat bg-white hover:bg-slate-50 transition-colors group">
          {isBulkMode && <td className="sticky left-0 bg-white py-1 px-2 z-20 group-hover:bg-slate-50" />}
          <td className={`${isBulkMode ? '' : 'sticky left-0'} bg-white py-1 px-4 pl-8 text-xs text-slate-600 z-20 group-hover:bg-slate-50`}>
            <button
              type="button"
              onClick={() => setShowOrnBreakdown(v => !v)}
              className="inline-flex items-center gap-1 -ml-4 text-slate-600 hover:text-slate-900"
              title={showOrnBreakdown ? 'Hide stat-account breakdown' : 'Show which stat accounts flow into Occupied Rooms, and at what share'}
            >
              {showOrnBreakdown ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              Occupied Rooms
            </button>
          </td>
          {MONTH_LABELS.map((month, index) => {
            const monthKey = month.toLowerCase();
            const expanded = roomStats.expanded?.[monthKey];
            const hasOverride = expanded?.overrides?.hasOccupiedOverride || false;
            const occupiedSource = expanded?.projected?.occupiedRoomsSource;
            const hasDriverProjection = occupiedSource === 'otb' || occupiedSource === 'rms';
            const usesProjectedOccupied = hasOverride || hasDriverProjection;
            const manualOverrideControlsValue = hasOverride && !hasDriverProjection;
            const originalValue = expanded?.forecast.occupiedRooms ?? roomStats.occupiedRooms[monthKey] ?? 0;
            const effectiveValue = usesProjectedOccupied
              ? (expanded?.projected?.occupiedRooms ?? originalValue)
              : (roomStats.occupiedRooms[monthKey] || 0);
            // Closed months show the recorded ORN. Without this a driven month
            // renders its projection while the Occ % row above renders the
            // rolled-in actual — the two rows disagreed on the same month.
            const rolled = rolledActual(monthKey);
            const displayValue = rolled ? rolled.occupiedRooms : effectiveValue;
            const projectionTitle = rolled
              ? `Recorded actual. Forecast: ${formatNumber(effectiveValue)}`
              : hasDriverProjection
              ? `${occupiedSource?.toUpperCase()} driver projection. Original: ${formatNumber(originalValue)}`
              : undefined;
            const isColExpanded = isColumnExpanded?.(index);
            const isEditing = editingCell?.month === index + 1 && editingCell?.type === 'occupiedRooms';
            const canEdit = !isReadOnly && onRoomStatOverride;

            if (isColExpanded && expanded) {
              const actual = expanded.actual.occupiedRooms;
              const forecast = usesProjectedOccupied ? effectiveValue : expanded.forecast.occupiedRooms;
              const budget = expanded.budget.occupiedRooms;
              const lyBudget = expanded.lyBudget?.occupiedRooms || 0;
              const variance = actual - forecast;


              const colValues: Record<string, number> = {
                actual, forecast, budget, lyBudget, budgetVsLyBudget: forecast - lyBudget,
                var: variance, vsBud: actual - budget, budVsFcst: forecast - budget,
                lyActual: expanded.actual.occupiedRooms,
              };

              return (
                <React.Fragment key={month}>
                  {activeColumns.map(col => {
                    const val = colValues[col.key] ?? 0;
                    const isForecastCol = col.key === 'forecast';
                    const isVarianceCol = col.colorMode === 'variance';
                    const isEditingThis = isForecastCol && isEditing;
                    const canEditThis = isForecastCol && canEdit;

                    return (
                      <td
                        key={col.key}
                        className={`text-right py-1 px-2 text-xs tabular-nums ${
                          isForecastCol && manualOverrideControlsValue ? 'font-medium fv3-cell-override' : ''
                        } ${isForecastCol && hasDriverProjection ? 'fv3-cell-projected' : ''} ${isVarianceCol ? (val >= 0 ? 'text-emerald-600' : 'text-red-600') : 'text-slate-600'} ${
                          canEditThis ? 'cursor-pointer hover:bg-slate-100' : ''
                        }`}
                        style={isForecastCol && manualOverrideControlsValue ? { color: 'var(--lodge-orange-500)' } : undefined}
                        onClick={() => canEditThis && handleStartEdit(index, 'occupiedRooms', forecast)}

                        title={isForecastCol ? (projectionTitle || (hasOverride ? `Original: ${formatNumber(originalValue)}` : canEdit ? 'Click to override' : undefined)) : undefined}
                      >
                        {isEditingThis ? (
                          renderEditInput(index, 'occupiedRooms', hasOverride)
                        ) : (
                          <>
                            <RecalculatingValue value={formatNumber(val)} isRecalculating={false} />
                            {isForecastCol && manualOverrideControlsValue && renderOverrideIndicator(originalValue)}
                          </>
                        )}
                      </td>
                    );
                  })}
                </React.Fragment>
              );
            }

            return (
              <td
                key={month}
                className={`text-right py-1 px-3 text-xs tabular-nums ${manualOverrideControlsValue && !rolled ? 'font-medium fv3-cell-override' : ''} ${hasDriverProjection && !rolled ? 'fv3-cell-projected' : ''} ${canEdit ? 'cursor-pointer hover:bg-slate-100' : ''}`}
                style={{ color: manualOverrideControlsValue && !rolled ? 'var(--lodge-orange-500)' : undefined }}
                onClick={() => canEdit && handleStartEdit(index, 'occupiedRooms', effectiveValue)}

                title={projectionTitle || (hasOverride ? `Original: ${formatNumber(originalValue)}` : canEdit ? 'Click to override' : undefined)}
              >
                {isEditing ? (
                  renderEditInput(index, 'occupiedRooms', hasOverride)
                ) : (
                  <>
                    <RecalculatingValue value={formatNumber(displayValue)} isRecalculating={false} />
                    {manualOverrideControlsValue && !rolled && renderOverrideIndicator(originalValue)}
                  </>
                )}
              </td>
            );
          })}
          <td colSpan={totalColSpan} className="text-right py-1 px-4 text-xs font-medium tabular-nums text-slate-700">
            <RecalculatingValue value={formatNumber(totalOccupied)} isRecalculating={false} />
          </td>
        </tr>
      )}

      {/* Occupied (ORN) drill-down — stat accounts flowing into each month */}
      {isRoomStatsExpanded && showOrnBreakdown && (
        <StatAccountBreakdownRows
          plan={statPlan}
          metric="rn"
          onOpenStatMapping={onOpenStatMapping}
          actualMonthKeys={ornActualMonthKeys}
          isBulkMode={isBulkMode}
          isColumnExpanded={isColumnExpanded}
          activeColumns={activeColumns}
          currency={currency}
        />
      )}

      {/* Occupancy % Row */}
      {isRoomStatsExpanded && (
        <tr className="fv3-r-stat bg-white hover:bg-slate-50 transition-colors group">
          {isBulkMode && <td className="sticky left-0 bg-white py-1 px-2 z-20 group-hover:bg-slate-50" />}
          <td className={`${isBulkMode ? '' : 'sticky left-0'} bg-white py-1 px-4 pl-8 text-xs text-slate-600 z-20 group-hover:bg-slate-50`}>
            Occ %
          </td>
          {MONTH_LABELS.map((month, index) => {
            const monthKey = month.toLowerCase();
            const value = roomStats.occupancyRate[monthKey] || 0;
            const expanded = roomStats.expanded?.[monthKey];
            const isColExpanded = isColumnExpanded?.(index);

            // Compute effective forecast occ % from override-aware ARN/ORN
            const hasArnOverride = expanded?.overrides?.hasAvailableOverride || false;
            const hasOrnOverride = expanded?.overrides?.hasOccupiedOverride || false;
            const effectiveArn = hasArnOverride
              ? (expanded?.projected?.availableRooms ?? expanded?.forecast.availableRooms ?? roomStats.availableRooms[monthKey] ?? 0)
              : (roomStats.availableRooms[monthKey] || 0);
            const effectiveOrn = hasOrnOverride
              ? (expanded?.projected?.occupiedRooms ?? expanded?.forecast.occupiedRooms ?? roomStats.occupiedRooms[monthKey] ?? 0)
              : (roomStats.occupiedRooms[monthKey] || 0);
            const effectiveOccPct = effectiveArn > 0 ? (effectiveOrn / effectiveArn) * 100 : 0;

            if (isColExpanded && expanded) {
              const actual = expanded.actual.occupancyRate;
              const forecast = (hasArnOverride || hasOrnOverride) ? effectiveOccPct : expanded.forecast.occupancyRate;
              const budget = expanded.budget.occupancyRate;
              const lyBudget = expanded.lyBudget?.occupancyRate || 0;
              const variance = actual - forecast;


              const colValues: Record<string, number> = {
                actual, forecast, budget, lyBudget, budgetVsLyBudget: forecast - lyBudget,
                var: variance, vsBud: actual - budget, budVsFcst: forecast - budget,
                lyActual: actual,
              };

              const formatOccCol = (key: string, val: number) => {
                if (key === 'var' || key === 'vsBud' || key === 'budVsFcst') return `${val >= 0 ? '+' : ''}${val.toFixed(1)}pp`;
                return formatPercent(val);
              };

              return (
                <React.Fragment key={month}>
                  {activeColumns.map(col => {
                    const val = colValues[col.key] ?? 0;
                    const isVarianceCol = col.colorMode === 'variance';
                    return (
                      <td
                        key={col.key}
                        className={`text-right py-1 px-2 text-xs tabular-nums ${isVarianceCol ? (val >= 0 ? 'text-emerald-600' : 'text-red-600') : 'text-slate-500'}`}
                      >
                        <RecalculatingValue value={formatOccCol(col.key, val)} isRecalculating={false} />
                      </td>
                    );
                  })}
                </React.Fragment>
              );
            }

            // A closed month's recorded occupancy wins over an override, so this
            // row can't disagree with the ARN/ORN rows above it.
            const rolled = rolledActual(monthKey);
            const collapsedOccPct = rolled
              ? rolled.occupancyRate
              : (hasArnOverride || hasOrnOverride) ? effectiveOccPct : value;

            return (
              <td key={month} className="text-right py-1 px-3 text-xs tabular-nums text-slate-500">
                <RecalculatingValue value={formatPercent(collapsedOccPct)} isRecalculating={false} />
              </td>
            );
          })}
          <td colSpan={totalColSpan} className="text-right py-1 px-4 text-xs font-medium tabular-nums text-slate-600">
            <RecalculatingValue value={formatPercent(avgOccupancy)} isRecalculating={false} />
          </td>
        </tr>
      )}

      {/* ADR + RevPAR Rows — closed months use the RECORDED rooms revenue ÷ actual
          ORN/ARN; open months derive from the rooms revenue FORECAST (driver +
          pins + overrides), so either way they tie to the money rows above */}
      {isRoomStatsExpanded && (['adr', 'revpar'] as const).map((metric) => (
        <React.Fragment key={metric}>
        <tr className="fv3-r-stat bg-white hover:bg-slate-50 transition-colors group">
          {isBulkMode && <td className="sticky left-0 bg-white py-1 px-2 z-20 group-hover:bg-slate-50" />}
          <td
            className={`${isBulkMode ? '' : 'sticky left-0'} bg-white py-1 px-4 pl-8 text-xs text-slate-600 z-20 group-hover:bg-slate-50`}
            title="Recorded actuals for closed months, otherwise derived from the rooms revenue forecast"
          >
            {metric === 'adr' ? (
              <button
                type="button"
                onClick={() => setShowAdrBreakdown(v => !v)}
                className="inline-flex items-center gap-1 -ml-4 text-slate-600 hover:text-slate-900"
                title={showAdrBreakdown ? 'Hide stat-account ADR' : 'Show ADR by stat account (RN × ADR = linked revenue)'}
              >
                {showAdrBreakdown ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                ADR
              </button>
            ) : 'RevPAR'}
          </td>
          {MONTH_LABELS.map((month, index) => {
            const monthKey = month.toLowerCase();
            const expanded = roomStats.expanded?.[monthKey];
            const rolled = rolledActual(monthKey);
            const forecastValue = metric === 'adr' ? expanded?.projected?.adr : expanded?.projected?.revpar;
            const actualValue = metric === 'adr' ? rolled?.adr : rolled?.revpar;
            const value = rolled && actualValue != null ? actualValue : forecastValue;
            const isColExpanded = isColumnExpanded?.(index);
            const display = value != null ? formatCurrency(value, false, currency) : '—';
            const forecastDisplay = forecastValue != null ? formatCurrency(forecastValue, false, currency) : '—';

            if (isColExpanded) {
              return (
                <React.Fragment key={month}>
                  {activeColumns.map(col => (
                    <td key={col.key} className="text-right py-1 px-2 text-xs tabular-nums text-slate-500">
                      {col.key === 'actual' && rolled && actualValue != null
                        ? <RecalculatingValue value={display} isRecalculating={false} />
                        : col.key === 'forecast'
                        ? <RecalculatingValue value={forecastDisplay} isRecalculating={false} />
                        : ''}
                    </td>
                  ))}
                </React.Fragment>
              );
            }

            return (
              <td
                key={month}
                className="text-right py-1 px-3 text-xs tabular-nums text-slate-500"
                title={rolled && actualValue != null ? `Recorded actual. Forecast: ${forecastDisplay}` : undefined}
              >
                <RecalculatingValue value={display} isRecalculating={false} />
              </td>
            );
          })}
          <td colSpan={totalColSpan} className="text-right py-1 px-4 text-xs font-medium tabular-nums text-slate-600">
            <RecalculatingValue
              value={(metric === 'adr' ? avgAdr : avgRevpar) != null
                ? formatCurrency((metric === 'adr' ? avgAdr : avgRevpar) as number, false, currency)
                : '—'}
              isRecalculating={false}
            />
          </td>
        </tr>
        {metric === 'adr' && showAdrBreakdown && (
          <StatAccountBreakdownRows
            plan={statPlan}
            metric="adr"
            onOpenStatMapping={onOpenStatMapping}
            actualMonthKeys={ornActualMonthKeys}
            isBulkMode={isBulkMode}
            isColumnExpanded={isColumnExpanded}
            activeColumns={activeColumns}
            currency={currency}
          />
        )}
        </React.Fragment>
      ))}

    </>
  );
}

export default PnLGrid;
